import TextbookProjectOverview from "../components/TextbookProjectOverview";
import TextbookBookMap from "../components/TextbookBookMap";
import TextbookChapterWorkspace from "../components/TextbookChapterWorkspace";
import TextbookWorkspaceNavigator from "../components/TextbookWorkspaceNavigator";
import TextbookBlueprintOverview from "../components/TextbookBlueprintOverview";
import TextbookWriteChapters from "../components/TextbookWriteChapters";
import TextbookBookReview from "../components/TextbookBookReview";
import TextbookPublicationProof from "../components/TextbookPublicationProof";
import TextbookPublicationPreparation from "../components/TextbookPublicationPreparation";
import TextbookFiguresMedia from "../components/TextbookFiguresMedia";
import {
  TextbookCollaborativeDiscovery,
  TextbookCollaborativeRequests,
} from "../components/TextbookOpenGroupParticipation";
import ScreenplayProjectOverview from "../components/ScreenplayProjectOverview";
import ScreenplayStoryMap from "../components/ScreenplayStoryMap";
import ScreenplayWorkspaceNavigator from "../components/ScreenplayWorkspaceNavigator";
import ScreenplayCharactersWorld from "../components/ScreenplayCharactersWorld";
import ScreenplayScriptWorkspace from "../components/ScreenplayScriptWorkspace";
import ScreenplayProposalRoundWorkspace from "../components/ScreenplayProposalRoundWorkspace";
import ScreenplayReviewDecideWorkspace from "../components/ScreenplayReviewDecideWorkspace";
import NovelStoryBookViewDraft from "../components/NovelStoryBookViewDraft";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  childrensBookPilotEnabled,
} from "../utils/childrensBookPilot";
import {
  childrensBookPersistencePilotEnabled,
} from "../utils/childrensBookPersistencePilot";
import {
  childrensBookReadOnlyPilotEnabled,
} from "../utils/childrensBookReadOnlyPilot";
import {
  normalizeChildrensBookAssets,
} from "../utils/childrensBookAssetState";
import {
  currentProjectPathname,
  projectIdFromSearch,
  replaceProjectPath,
  replaceProjectWorkspacePath,
} from "../utils/projectWorkspaceRouting";
import { api } from "../api";
import { selectNovelContinueWritingTargetV1 } from "../utils/novelContinueWritingTargetV1";
import { useUser } from "../UserContext";
import ProjectBreadcrumbTrail from "../components/ProjectBreadcrumbTrail";
import WritingCompanionPilotV1 from "../components/WritingCompanionPilotV1";
import ChildrensBookCreationFlow from "../components/ChildrensBookCreationFlow";

function clean(value) {
  return value == null ? "" : String(value).trim();
}

function safeObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  )
    ? value
    : {};
}

export function childrensBookBlueprintFromDraft(
  draft = {}
) {
  const source =
    safeObject(draft);

  return {
    projectPlan: {
      identity:
        safeObject(source.identity),

      audience:
        safeObject(source.audience),

      bookForm:
        safeObject(source.bookForm),

      visualFormat:
        safeObject(source.visualFormat),

      collaboration:
        safeObject(source.collaboration),
    },

    bookMap:
      safeObject(source.bookMap),

    manuscriptPlan:
      safeObject(source.manuscriptPlan),

    illustrationPlan:
      safeObject(source.illustrationPlan),

    coverPlan:
      safeObject(source.coverPlan),

    publicationReadiness:
      safeObject(
        source.publicationReadiness
      ),

    blueprintProgress:
      safeObject(
        source.blueprintProgress
      ),
  };
}

const CHILDRENS_BOOK_BASE_PATH =
  "/my-projects/childrens-book";

const TEXTBOOK_BASE_PATH =
  "/my-projects/textbook";

const TEXTBOOK_CHAPTER_QUERY_KEY =
  "chapterItemId";

function currentTextbookChapterItemId() {
  if (
    typeof window ===
      "undefined"
  ) {
    return "";
  }

  const params =
    new URLSearchParams(
      String(
        window.location.search ||
        ""
      )
    );

  return clean(
    params.get(
      TEXTBOOK_CHAPTER_QUERY_KEY
    )
  );
}

function textbookSearchWithChapterItem(
  chapterItemId = ""
) {
  if (
    typeof window ===
      "undefined"
  ) {
    return "";
  }

  const params =
    new URLSearchParams(
      String(
        window.location.search ||
        ""
      )
    );

  const chapterId =
    clean(
      chapterItemId
    );

  if (chapterId) {
    params.set(
      TEXTBOOK_CHAPTER_QUERY_KEY,
      chapterId
    );
  } else {
    params.delete(
      TEXTBOOK_CHAPTER_QUERY_KEY
    );
  }

  return params.toString();
}

function currentTextbookWorkspace() {
  const pathname = currentPathname();

  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/chapter-workspace`)) return "chapter-workspace";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/publication-preparation`)) return "publication-preparation";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/publication-proof`)) return "publication-proof";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/review-your-book`)) return "review-your-book";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/figures-media`)) return "figures-media";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/write-chapters`)) return "write-chapters";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/book-map`)) return "book-map";
  if (pathname.startsWith(`${TEXTBOOK_BASE_PATH}/project-plan`)) return "project-plan";
  return "overview";
}

const SCREENPLAY_BASE_PATH =
  "/my-projects/screenplay";

function currentScreenplayWorkspace() {
  const pathname =
    currentPathname();

  if (
    pathname.startsWith(
      `${SCREENPLAY_BASE_PATH}/review-and-decide`
    )
  ) {
    return "review-and-decide";
  }

  if (
    pathname.startsWith(
      `${SCREENPLAY_BASE_PATH}/proposal-round`
    )
  ) {
    return "proposal-round";
  }

  if (
    pathname.startsWith(
      `${SCREENPLAY_BASE_PATH}/script-workspace`
    )
  ) {
    return "script-workspace";
  }

  if (
    pathname.startsWith(
      `${SCREENPLAY_BASE_PATH}/characters-world`
    )
  ) {
    return "characters-world";
  }

  if (
    pathname.startsWith(
      `${SCREENPLAY_BASE_PATH}/story-map`
    )
  ) {
    return "story-map";
  }

  return "project-plan";
}

function currentPathname() {
  return currentProjectPathname();
}

function childrensBookRouteActive() {
  return currentPathname().startsWith(
    CHILDRENS_BOOK_BASE_PATH
  );
}

function currentChildrensBookRouteProjectId() {
  return projectIdFromSearch();
}

function replaceProjectHubPath(
  pathname,
  options = {}
) {
  return replaceProjectPath(
    pathname,
    options
  );
}

function projectId(project) {
  return clean(
    project?.projectId ||
    project?.legacyStoryId ||
    project?.id ||
    project?._id
  );
}

function novelStoryStructurePreviewEnabled() {
  if (typeof window === "undefined") return false;

  const host =
    clean(window.location?.hostname)
      .toLowerCase();

  return (
    host === "staging.nextscenes.org" ||
    host.includes("nextscenes-staging-frontend") ||
    host.includes("nextscenes-dev-frontend")
  );
}

function novelStoryStructurePreviewProject(isFR) {
  const title = isFR
    ? "Aperçu de structure — roman exemple"
    : "Structure Preview — Sample Novel";

  const scenes = [
    {
      _id: "preview-scene-1",
      sceneNumber: 1,
      title: isFR ? "Le quai vide" : "The Empty Platform",
      text:
        "Mara reaches the station before sunrise and finds the platform almost deserted. A delayed train and an unopened letter make her wonder whether leaving is still the right choice.",
      type: "canon",
      status: "approved",
    },
    {
      _id: "preview-scene-2",
      sceneNumber: 2,
      title: isFR ? "Une voix du passé" : "A Voice from the Past",
      text:
        "A familiar voice calls her name from behind the ticket hall. The encounter forces Mara to face a promise she thought time had erased.",
      type: "canon",
      status: "approved",
    },
    {
      _id: "preview-scene-3",
      sceneNumber: 3,
      title: isFR ? "La lettre" : "The Letter",
      text:
        "On the train, Mara finally opens the letter. Its contents change what she believes about her family and about the journey ahead.",
      type: "canon",
      status: "approved",
    },
    {
      _id: "preview-scene-4",
      sceneNumber: 4,
      title: isFR ? "La maison sur la colline" : "The House on the Hill",
      text:
        "At her destination, Mara walks to an old house above the town. Someone has been expecting her, but not for the reason she imagined.",
      type: "canon",
      status: "approved",
    },
    {
      _id: "preview-scene-5",
      sceneNumber: 5,
      title: isFR ? "La vérité incomplète" : "The Incomplete Truth",
      text:
        "A family account fills one gap and opens another. Mara must decide which version of the past deserves to guide her next step.",
      type: "canon",
      status: "approved",
    },
    {
      _id: "preview-scene-6",
      sceneNumber: 6,
      title: isFR ? "Le retour" : "The Return",
      text:
        "Mara returns to the station with a different understanding of why she left. The final choice is hers, and this time she makes it knowingly.",
      type: "canon",
      status: "approved",
    },
  ];

  return {
    id: "novel-story-structure-preview",
    projectId: "novel-story-structure-preview",
    title,
    isStructurePreview: true,
    description: isFR
      ? "Aperçu local uniquement pour tester Chapitres et Scènes."
      : "Local-only preview for testing Chapters and Scenes.",
    structurePreviewStory: {
      _id: "novel-story-structure-preview",
      title,
      scenes,
    },
    structurePreviewChapters: [
      {
        id: "preview-chapter-1",
        title: isFR ? "Chapitre 1 · Le départ" : "Chapter 1 · Departure",
        sceneIds: [
          "preview-scene-1",
          "preview-scene-2",
        ],
      },
      {
        id: "preview-chapter-2",
        title: isFR ? "Chapitre 2 · La découverte" : "Chapter 2 · Discovery",
        sceneIds: [
          "preview-scene-3",
          "preview-scene-4",
          "preview-scene-5",
        ],
      },
      {
        id: "preview-chapter-3",
        title: isFR ? "Chapitre 3 · Le choix" : "Chapter 3 · Choice",
        sceneIds: [
          "preview-scene-6",
        ],
      },
    ],
  };
}

function formatDate(value, lang) {
  const raw = clean(value);

  if (!raw) {
    return lang === "fr"
      ? "Date inconnue"
      : "Date unknown";
  }

  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  try {
    return new Intl.DateTimeFormat(
      lang === "fr" ? "fr-FR" : "en-GB",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      }
    ).format(date);
  } catch {
    return raw;
  }
}

function storyTypeLabel(value, lang) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "short") {
    return lang === "fr"
      ? "Nouvelle"
      : "Short Story";
  }

  return lang === "fr"
    ? "Histoire longue"
    : "Long-form Story";
}

function projectLanguageLabel(
  value,
  lang
) {
  const normalized =
    clean(value)
      .toLowerCase();

  if (
    normalized ===
      "bilingual"
  ) {
    return lang === "fr"
      ? "Bilingue"
      : "Bilingual";
  }

  if (normalized === "fr") {
    return lang === "fr"
      ? "Français"
      : "French";
  }

  return lang === "fr"
    ? "Anglais"
    : "English";
}

function projectOwnerDisplayName(
  project
) {
  return (
    clean(
      project?.owner?.name
    ) ||
    clean(
      project
        ?.owner
        ?.username
    ) ||
    clean(
      project?.ownerUsername
    ) ||
    "—"
  );
}

function projectOwnerInitials(
  project
) {
  const label =
    projectOwnerDisplayName(
      project
    );

  if (!label || label === "—") {
    return "NS";
  }

  const parts =
    label
      .split(/\s+/)
      .filter(Boolean);

  if (parts.length >= 2) {
    return (
      parts[0][0] +
      parts[parts.length - 1][0]
    ).toUpperCase();
  }

  return label
    .slice(0, 2)
    .toUpperCase();
}

function writingModeLabel(value, lang) {
  const normalized = clean(value).toLowerCase();

  if (normalized.includes("group")) {
    if (normalized.includes("closed")) {
      return lang === "fr"
        ? "Groupe (fermé)"
        : "Group (Closed)";
    }

    return lang === "fr"
      ? "Groupe (ouvert)"
      : "Group (Open)";
  }

  if (normalized.includes("collab")) {
    return lang === "fr"
      ? "Collaboratif"
      : "Collaborative";
  }

  return "Solo";
}

function textbookWritingModeLabel(
  value,
  lang
) {
  const normalized =
    clean(value)
      .toLowerCase();

  if (
    normalized === "group_open" ||
    normalized === "group_closed"
  ) {
    return lang === "fr"
      ? "Écriture en groupe"
      : "Group Writing";
  }

  if (
    normalized.includes(
      "collab"
    )
  ) {
    return lang === "fr"
      ? "Collaboratif"
      : "Collaborative";
  }

  return "Solo";
}


function screenplayGroupWritingMode(
  value
) {
  const mode =
    clean(value)
      .toLowerCase();

  return (
    mode === "group_open" ||
    mode === "group_closed"
  );
}

function textbookInvitationWritingMode(
  value
) {
  const mode =
    clean(value)
      .toLowerCase();

  return (
    mode === "group_open" ||
    mode === "group_closed"
  );
}

function screenplayInvitationStatusLabel(
  value,
  isFR
) {
  const status =
    clean(value)
      .toLowerCase();

  if (status === "accepted") {
    return isFR
      ? "Acceptée"
      : "Accepted";
  }

  if (status === "declined") {
    return isFR
      ? "Refusée"
      : "Declined";
  }

  if (status === "expired") {
    return isFR
      ? "Expirée"
      : "Expired";
  }

  if (
    status ===
    "delivery_failed"
  ) {
    return isFR
      ? "Échec d’envoi"
      : "Delivery failed";
  }

  if (status === "revoked") {
    return isFR
      ? "Révoquée"
      : "Revoked";
  }

  return isFR
    ? "En attente"
    : "Pending";
}


function parseScreenplayInviteEmails(
  value
) {
  const raw =
    clean(value);

  if (!raw) {
    return {
      emails: [],
      invalid: [],
    };
  }

  const seen =
    new Set();

  const emails = [];
  const invalid = [];

  raw
    .split(/[,;\n]+/)
    .map((email) =>
      clean(email)
        .toLowerCase()
    )
    .filter(Boolean)
    .forEach((email) => {
      if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/
          .test(email)
      ) {
        invalid.push(email);
        return;
      }

      if (
        seen.has(email)
      ) {
        return;
      }

      seen.add(email);
      emails.push(email);
    });

  return {
    emails,
    invalid,
  };
}

function parseTextbookInviteEmails(
  value
) {
  return parseScreenplayInviteEmails(
    value
  );
}


function publicationLabel(value, lang) {
  const normalized = clean(value).toLowerCase();

  if (
    normalized === "published" ||
    normalized === "publishedamazon" ||
    normalized === "published_amazon" ||
    normalized === "publishedexternal" ||
    normalized === "published_external" ||
    normalized === "externallypublished"
  ) {
    return lang === "fr"
      ? "Publié"
      : "Published";
  }

  if (
    normalized === "completed" ||
    normalized === "complete"
  ) {
    return lang === "fr"
      ? "Terminé"
      : "Completed";
  }

  return lang === "fr"
    ? "En développement"
    : "In Development";
}

const CATEGORY_STYLE = {
  story: {
    accent: "#2563eb",
    accentSoft: "rgba(37,99,235,0.14)",
    border: "rgba(37,99,235,0.50)",
    background:
      "linear-gradient(145deg, rgba(231,240,255,0.99), rgba(248,251,255,0.99))",
  },

  children: {
    accent: "#d97706",
    accentSoft: "rgba(217,119,6,0.13)",
    border: "rgba(217,119,6,0.28)",
    background:
      "linear-gradient(145deg, rgba(255,248,214,0.99), rgba(255,253,241,0.99))",
  },

  essay: {
    accent: "#475569",
    accentSoft: "rgba(71,85,105,0.13)",
    border: "rgba(71,85,105,0.24)",
    background:
      "linear-gradient(145deg, rgba(241,245,249,0.99), rgba(252,253,255,0.99))",
  },

  textbook: {
    accent: "#059669",
    accentSoft: "rgba(5,150,105,0.13)",
    border: "rgba(5,150,105,0.26)",
    background:
      "linear-gradient(145deg, rgba(224,250,239,0.99), rgba(247,255,251,0.99))",
  },

  research: {
    accent: "#4f46e5",
    accentSoft: "rgba(79,70,229,0.13)",
    border: "rgba(79,70,229,0.26)",
    background:
      "linear-gradient(145deg, rgba(232,235,255,0.99), rgba(249,250,255,0.99))",
  },

  technical: {
    accent: "#334155",
    accentSoft: "rgba(51,65,85,0.13)",
    border: "rgba(51,65,85,0.25)",
    background:
      "linear-gradient(145deg, rgba(232,238,244,0.99), rgba(250,252,254,0.99))",
  },

  screenplay: {
    accent: "#dc2626",
    accentSoft: "rgba(220,38,38,0.12)",
    border: "rgba(220,38,38,0.24)",
    background:
      "linear-gradient(145deg, rgba(255,232,232,0.99), rgba(255,249,249,0.99))",
  },

  stage: {
    accent: "#9333ea",
    accentSoft: "rgba(147,51,234,0.12)",
    border: "rgba(147,51,234,0.24)",
    background:
      "linear-gradient(145deg, rgba(248,232,255,0.99), rgba(254,249,255,0.99))",
  },

  music: {
    accent: "#b45309",
    accentSoft: "rgba(180,83,9,0.13)",
    border: "rgba(180,83,9,0.25)",
    background:
      "linear-gradient(145deg, rgba(255,243,210,0.99), rgba(255,251,241,0.99))",
  },
};

function ProjectTypeIcon({ type }) {
  const common = {
    width: 30,
    height: 30,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (type === "story") {
    return (
      <svg {...common}>
        <path d="M4 5.5c2.7-.8 5.3-.4 8 1.3v12c-2.7-1.7-5.3-2.1-8-1.3z" />
        <path d="M20 5.5c-2.7-.8-5.3-.4-8 1.3v12c2.7-1.7 5.3-2.1 8-1.3z" />
        <path d="M12 6.8V19" />
      </svg>
    );
  }

  if (type === "children") {
    return (
      <svg {...common}>
        <circle cx="12" cy="9" r="3.2" />
        <path d="M5 20c.8-4 3.1-6 7-6s6.2 2 7 6" />
        <path d="M5 6l-2-1M19 6l2-1M12 3V1" />
      </svg>
    );
  }

  if (type === "essay") {
    return (
      <svg {...common}>
        <path d="M6 3h9l3 3v15H6z" />
        <path d="M15 3v4h4" />
        <path d="M9 11h6M9 15h6M9 19h4" />
      </svg>
    );
  }

  if (type === "textbook") {
    return (
      <svg {...common}>
        <path d="M4 4.5c3-.8 5.6-.3 8 1.5v14c-2.4-1.8-5-2.3-8-1.5z" />
        <path d="M20 4.5c-3-.8-5.6-.3-8 1.5v14c2.4-1.8 5-2.3 8-1.5z" />
        <path d="M7 9h2M15 9h2M7 13h2M15 13h2" />
      </svg>
    );
  }

  if (type === "research") {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="5.5" />
        <path d="M14.3 14.3L21 21" />
        <path d="M7.5 10h5M10 7.5v5" />
      </svg>
    );
  }

  if (type === "technical") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="3.2" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
        <path d="M4.9 4.9L7 7M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />
      </svg>
    );
  }

  if (type === "screenplay") {
    return (
      <svg {...common}>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M8 5v14M16 5v14M3 9h5M16 9h5M3 15h5M16 15h5" />
        <path d="M11 9l4 3-4 3z" />
      </svg>
    );
  }

  if (type === "stage") {
    return (
      <svg {...common}>
        <path d="M4 4c3 1 5.7 1.2 8 0 2.3 1.2 5 1 8 0v16c-3-1-5.7-1.2-8 0-2.3-1.2-5-1-8 0z" />
        <path d="M8 9c.8-.7 1.7-.7 2.5 0M13.5 9c.8-.7 1.7-.7 2.5 0" />
        <path d="M9 15c2-1.4 4-1.4 6 0" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M9 18V5l10-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="16" cy="16" r="3" />
    </svg>
  );
}

export default function ProjectHub({
  onNavigatePage,
  onContextChange,
  initialCategory = "",
  initialProjectId = "",
  initialChapterId = "",
}) {
  const {
    isAuthenticated,
    isWriter,
    isAdmin,
    user,
  } = useUser();

  const canCreateProjects =
    Boolean(
      isWriter ||
      isAdmin ||
      user?.writerAccess === true
    );

  const textbookActorKey =
    clean(
      user?.id ||
      user?._id ||
      user?.email
    );

  const isReaderDiscovery =
    Boolean(
      isAuthenticated &&
      !canCreateProjects
    );

  const childrensBookPilot =
    childrensBookPilotEnabled();

  const childrensBookPersistencePilot =
    childrensBookPersistencePilotEnabled();

  const childrensBookReadOnlyPilot =
    childrensBookReadOnlyPilotEnabled();

  const childrensBookReadAccess =
    childrensBookPersistencePilot ||
    childrensBookReadOnlyPilot;

  const lang =
    (typeof window !== "undefined" &&
      window.localStorage.getItem("ns.lang")) ||
    "en";

  const isFR =
    String(lang).toLowerCase() === "fr";

  const novelStoryStructurePreview =
    novelStoryStructurePreviewEnabled();

  const [selectedCategory, setSelectedCategory] =
    useState(
      clean(initialCategory) ||
      (
        clean(initialProjectId)
          ? "story"
          : childrensBookRouteActive()
          ? "children"
          : currentPathname().startsWith(
              TEXTBOOK_BASE_PATH
            )
          ? "textbook"
          : currentPathname().startsWith(
              SCREENPLAY_BASE_PATH
            )
          ? "screenplay"
          : ""
      )
    );

  const [projects, setProjects] =
    useState([]);

  const [companionOtherLanguageProjects, setCompanionOtherLanguageProjects] = useState([]);
  const [companionOtherLanguageLoaded, setCompanionOtherLanguageLoaded] = useState(false);
  // New V1F1 pilot appears only on the two known staging hosts.
  const nativeChildrensNovelPilotV1F1 = process.env.REACT_APP_CHILDRENS_BOOK_NATIVE_V1F1_ENABLED === "true";
  const [nativeChildrensOpenV1F1, setNativeChildrensOpenV1F1] = useState(null);
  const [nativeChildrensTitleV1F1, setNativeChildrensTitleV1F1] = useState("");
  const [nativeChildrensAgeV1F1, setNativeChildrensAgeV1F1] = useState("");
  const [nativeChildrensArrangementV1F1, setNativeChildrensArrangementV1F1] = useState("");
  const [nativeChildrensLevelV1F1, setNativeChildrensLevelV1F1] = useState("");
  const [nativeChildrensBusyV1F1, setNativeChildrensBusyV1F1] = useState(false);
  const [nativeChildrensErrorV1F1, setNativeChildrensErrorV1F1] = useState("");

  // Project Hub does not invent a second Novel / Story visibility model.
  // This is the same access-filtered Storylines set returned by
  // /api/stories?mode=list for the current viewer.
  const [
    novelStoryVisibleStories,
    setNovelStoryVisibleStories,
  ] = useState([]);

  const [
    novelCollaborationCandidates,
    setNovelCollaborationCandidates,
  ] = useState([]);

  const [
    novelCollaborationLoading,
    setNovelCollaborationLoading,
  ] = useState(false);

  const [
    novelCollaborationError,
    setNovelCollaborationError,
  ] = useState("");

  const [
    activeNovelStoryDraftProject,
    setActiveNovelStoryDraftProject,
  ] = useState(null);

  const [
    showNovelStoryCreate,
    setShowNovelStoryCreate,
  ] = useState(false);

  const [
    novelStoryTitle,
    setNovelStoryTitle,
  ] = useState("");

  const [
    novelStoryForum,
    setNovelStoryForum,
  ] = useState("");

  const [
    novelStoryWritingMode,
    setNovelStoryWritingMode,
  ] = useState("Solo");

  const [
    novelStoryType,
    setNovelStoryType,
  ] = useState("longform");

  const [
    novelStoryVisibility,
    setNovelStoryVisibility,
  ] = useState("public_preview");

  const [
    novelStoryLanguage,
    setNovelStoryLanguage,
  ] = useState(isFR ? "fr" : "en");

  const [
    novelStoryDescription,
    setNovelStoryDescription,
  ] = useState("");

  const [
    novelStorySynopsis,
    setNovelStorySynopsis,
  ] = useState("");

  const [
    novelStoryAboutAuthor,
    setNovelStoryAboutAuthor,
  ] = useState("");

  const [
    novelStoryAuthorPhotoUrl,
    setNovelStoryAuthorPhotoUrl,
  ] = useState("");

  const [
    novelStoryCoverImageUrl,
    setNovelStoryCoverImageUrl,
  ] = useState("");

  const [
    novelStoryAuthorPhotoFile,
    setNovelStoryAuthorPhotoFile,
  ] = useState(null);

  const [
    novelStoryCoverFile,
    setNovelStoryCoverFile,
  ] = useState(null);

  const [
    novelStoryInviteEmailsText,
    setNovelStoryInviteEmailsText,
  ] = useState("");

  const [
    novelStoryCreateError,
    setNovelStoryCreateError,
  ] = useState("");

  const [
    novelStoryCreating,
    setNovelStoryCreating,
  ] = useState(false);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    textbookProjects,
    setTextbookProjects,
  ] = useState([]);

  const [
    textbookProjectsLoading,
    setTextbookProjectsLoading,
  ] = useState(false);

  const [
    textbookProjectsError,
    setTextbookProjectsError,
  ] = useState("");

  const [
    archivedTextbookProjects,
    setArchivedTextbookProjects,
  ] = useState([]);

  const [
    textbookProjectActionId,
    setTextbookProjectActionId,
  ] = useState("");

  const [
    showArchivedTextbookProjects,
    setShowArchivedTextbookProjects,
  ] = useState(false);

  const [
    pendingTextbookArchiveProject,
    setPendingTextbookArchiveProject,
  ] = useState(null);

  const [
    activeTextbookProject,
    setActiveTextbookProject,
  ] = useState(null);

  const [
    textbookTitle,
    setTextbookTitle,
  ] = useState("");

  const [
    textbookPrimaryLanguage,
    setTextbookPrimaryLanguage,
  ] = useState(
    isFR ? "fr" : "en"
  );

  const [
    textbookWritingMode,
    setTextbookWritingMode,
  ] = useState("solo");

  const [
    textbookInviteEmailsText,
    setTextbookInviteEmailsText,
  ] = useState("");

  const [
    textbookInviteEmailsError,
    setTextbookInviteEmailsError,
  ] = useState("");

  const [
    textbookCreating,
    setTextbookCreating,
  ] = useState(false);

  const [
    textbookProjectAccess,
    setTextbookProjectAccess,
  ] = useState(null);

  const [
    textbookProjectAccessActorKey,
    setTextbookProjectAccessActorKey,
  ] = useState("");

  const currentTextbookProjectAccess =
    textbookProjectAccessActorKey &&
    textbookProjectAccessActorKey ===
      textbookActorKey
      ? textbookProjectAccess
      : null;

  const [
    textbookProjectPlan,
    setTextbookProjectPlan,
  ] = useState(null);

  const [
    textbookProjectPlanLoading,
    setTextbookProjectPlanLoading,
  ] = useState(false);

  const [
    textbookProjectPlanError,
    setTextbookProjectPlanError,
  ] = useState("");

  const textbookProjectPlanLoadRequest =
    useRef(0);

  const textbookDirectOpenRequest =
    useRef("");

  const [
    textbookDirectOpenLoading,
    setTextbookDirectOpenLoading,
  ] = useState(false);

  const textbookChapterLeaveGuard =
    useRef(null);

  const [
    textbookWorkspace,
    setTextbookWorkspace,
  ] = useState(
    () =>
      currentTextbookWorkspace()
  );

  const [
    textbookChapterItemId,
    setTextbookChapterItemId,
  ] = useState(
    () =>
      currentTextbookChapterItemId()
  );

  const [
    textbookChapterWorkspaceData,
    setTextbookChapterWorkspaceData,
  ] = useState(null);

  const [
    textbookChapterWorkspaceActorKey,
    setTextbookChapterWorkspaceActorKey,
  ] = useState("");

  const currentTextbookChapterWorkspaceData =
    textbookChapterWorkspaceActorKey &&
    textbookChapterWorkspaceActorKey ===
      textbookActorKey
      ? textbookChapterWorkspaceData
      : null;

  const [
    textbookChapterWorkspaceLoading,
    setTextbookChapterWorkspaceLoading,
  ] = useState(false);

  const [
    textbookChapterWorkspaceError,
    setTextbookChapterWorkspaceError,
  ] = useState("");

  const textbookChapterWorkspaceLoadRequest =
    useRef(0);

  const [
    screenplayProjects,
    setScreenplayProjects,
  ] = useState([]);

  const [
    screenplayProjectsLoading,
    setScreenplayProjectsLoading,
  ] = useState(false);

  const [
    screenplayProjectsError,
    setScreenplayProjectsError,
  ] = useState("");

  const [
    archivedScreenplayProjects,
    setArchivedScreenplayProjects,
  ] = useState([]);

  const [
    screenplayProjectActionId,
    setScreenplayProjectActionId,
  ] = useState("");

  const [
    screenplayInvitations,
    setScreenplayInvitations,
  ] = useState([]);

  const [
    screenplayInvitationsLoading,
    setScreenplayInvitationsLoading,
  ] = useState(false);

  const [
    screenplayInvitationsError,
    setScreenplayInvitationsError,
  ] = useState("");

  const [
    screenplayInvitationActionId,
    setScreenplayInvitationActionId,
  ] = useState("");

  const [
    screenplayInvitationsNotice,
    setScreenplayInvitationsNotice,
  ] = useState("");

  const [
    showScreenplayInvitations,
    setShowScreenplayInvitations,
  ] = useState(false);

  const [
    showArchivedScreenplayProjects,
    setShowArchivedScreenplayProjects,
  ] = useState(false);

  const [
    pendingScreenplayArchiveProject,
    setPendingScreenplayArchiveProject,
  ] = useState(null);

  const [
    editingScreenplayProject,
    setEditingScreenplayProject,
  ] = useState(null);

  const [
    screenplayEditTitle,
    setScreenplayEditTitle,
  ] = useState("");

  const [
    screenplayEditPrimaryLanguage,
    setScreenplayEditPrimaryLanguage,
  ] = useState("en");

  const [
    screenplayEditError,
    setScreenplayEditError,
  ] = useState("");

  const [
    activeScreenplayProject,
    setActiveScreenplayProject,
  ] = useState(null);

  const [
    screenplayTitle,
    setScreenplayTitle,
  ] = useState("");

  const [
    screenplayWritingMode,
    setScreenplayWritingMode,
  ] = useState("solo");

  const [
    screenplayInviteEmailsText,
    setScreenplayInviteEmailsText,
  ] = useState("");

  const [
    screenplayInviteEmailsError,
    setScreenplayInviteEmailsError,
  ] = useState("");

  const [
    screenplayInvitationNotice,
    setScreenplayInvitationNotice,
  ] = useState(null);

  const [
    screenplayCreating,
    setScreenplayCreating,
  ] = useState(false);

  const [
    screenplayProjectAccess,
    setScreenplayProjectAccess,
  ] = useState(null);

  const [
    screenplayProjectPlan,
    setScreenplayProjectPlan,
  ] = useState(null);

  const [
    screenplayProjectPlanLoading,
    setScreenplayProjectPlanLoading,
  ] = useState(false);

  const [
    screenplayProjectPlanError,
    setScreenplayProjectPlanError,
  ] = useState("");

  const [
    screenplayWorkspace,
    setScreenplayWorkspace,
  ] = useState(
    () =>
      currentScreenplayWorkspace()
  );

  const screenplayProjectPlanLoadRequest =
    useRef(0);

  const [
    childrensBookProjects,
    setChildrensBookProjects,
  ] = useState([]);

  const [
    childrensBookProjectsLoading,
    setChildrensBookProjectsLoading,
  ] = useState(false);

  const [
    childrensBookProjectsError,
    setChildrensBookProjectsError,
  ] = useState("");

  const [
    activeChildrensBookProject,
    setActiveChildrensBookProject,
  ] = useState(null);

  const [
    childrensBookAssets,
    setChildrensBookAssets,
  ] = useState([]);

  const [
    childrensBookAssetsLoading,
    setChildrensBookAssetsLoading,
  ] = useState(false);

  const [
    childrensBookAssetsError,
    setChildrensBookAssetsError,
  ] = useState("");

  const [
    childrensBookAssetActionId,
    setChildrensBookAssetActionId,
  ] = useState("");

  const [
    openingChildrensBookProjectId,
    setOpeningChildrensBookProjectId,
  ] = useState("");

  const [
    archivedChildrensBookProjects,
    setArchivedChildrensBookProjects,
  ] = useState([]);

  const [
    childrensBookProjectActionId,
    setChildrensBookProjectActionId,
  ] = useState("");

  const [
    showArchivedChildrensBookProjects,
    setShowArchivedChildrensBookProjects,
  ] = useState(false);

  const [
    pendingChildrensBookArchiveProject,
    setPendingChildrensBookArchiveProject,
  ] = useState(null);

  const [
    showChildrensBookCreationFlow,
    setShowChildrensBookCreationFlow,
  ] = useState(() => {
    const path = currentPathname();

    return (
      path.startsWith(
        `${CHILDRENS_BOOK_BASE_PATH}/`
      )
    );
  });

  useEffect(() => {
    const routeCategory =
      childrensBookRouteActive()
        ? "children"
        : currentPathname().startsWith(
            TEXTBOOK_BASE_PATH
          )
        ? "textbook"
        : currentPathname().startsWith(
            SCREENPLAY_BASE_PATH
          )
        ? "screenplay"
        : "";

    setSelectedCategory(
      clean(initialCategory) ||
      routeCategory
    );

    if (
      routeCategory === "textbook"
    ) {
      const routeWorkspace =
        currentTextbookWorkspace();

      setTextbookWorkspace(
        routeWorkspace
      );

      setTextbookChapterItemId(
        routeWorkspace ===
          "chapter-workspace"
          ? currentTextbookChapterItemId()
          : ""
      );
    }

    if (
      routeCategory === "screenplay"
    ) {
      setScreenplayWorkspace(
        currentScreenplayWorkspace()
      );
    }

    setShowChildrensBookCreationFlow(
      currentPathname().startsWith(
        `${CHILDRENS_BOOK_BASE_PATH}/`
      )
    );
  }, [initialCategory]);

  const ui = useMemo(
    () => ({
      eyebrow: isFR
        ? "CENTRE DES PROJETS CRÉATIFS"
        : "CREATIVE PROJECT CENTRE",

      title: isFR
        ? "Hub de projets"
        : "Project Hub",

      projectsTitle: isReaderDiscovery
        ? (
            isFR
              ? "Découvrez Roman / Histoire"
              : "Explore Novel / Story"
          )
        : (
            isFR
              ? "Mes projets"
              : "My Projects"
          ),

      projectsSubtitle: selectedCategory === "children"
        ? (
            isFR
              ? "Planifiez et préparez votre projet de livre pour enfants."
              : "Plan and prepare your Children’s Book project."
          )
        : selectedCategory === "textbook"
        ? (
            isFR
              ? "Créez ou reprenez votre projet de manuel."
              : "Create or continue your Textbook project."
          )
        : selectedCategory === "screenplay"
        ? (
            isFR
              ? "Créez ou reprenez votre projet de scénario."
              : "Create or continue your Screenplay project."
          )
        : isReaderDiscovery
        ? (
            isFR
              ? "Découvrez ce que les auteurs peuvent créer dans Story Studio."
              : "Discover what writers can create in Story Studio."
          )
        : (
            isFR
              ? "Choisissez un projet Roman / Histoire à poursuivre."
              : "Choose a Novel / Story project to continue."
          ),

      subtitle: isFR
        ? "Choisissez le type de projet que vous souhaitez créer ou poursuivre."
        : "Choose the kind of project you want to create or continue.",

      pilot: isFR
        ? "Fondation pilote en lecture seule"
        : "Read-only foundation pilot",

      principle: isFR
        ? "Une fondation commune, mais un Studio, un vocabulaire et un flux de travail propres à chaque type de projet."
        : "One shared foundation, but a distinct Studio, vocabulary, and workflow for every project type.",

      chooseType: isFR
        ? "Choisissez un type de projet"
        : "Choose a project type",

      chooseTypeHelp: isFR
        ? "Choisissez une catégorie disponible pour commencer ou retrouver votre projet."
        : "Choose an available category to start or return to your project.",

      active: isFR
        ? "Disponible"
        : "Available",

      planned: isFR
        ? "Prévu"
        : "Planned",

      comingLater: isFR
        ? "À venir"
        : "Coming later",

      enterCategory: isFR
        ? "Ouvrir cette catégorie"
        : "Open this category",

      backToTypes: isFR
        ? "Retour aux types de projet"
        : "Back to project types",

      projectsHeading: isReaderDiscovery
        ? (
            isFR
              ? "Découvrez Story Studio"
              : "Explore Story Studio"
          )
        : (
            isFR
              ? "Mes projets Roman / Histoire"
              : "My Novel / Story Projects"
          ),

      projectsIntro: isReaderDiscovery
        ? (
            isFR
              ? "Les lecteurs peuvent découvrir les possibilités de création, lire les œuvres disponibles et devenir auteurs lorsqu’ils sont prêts."
              : "Readers can explore the creative possibilities, read available works, and become writers when they are ready."
          )
        : (
            isFR
              ? "Vos storylines existantes apparaissent ici comme projets Roman / Histoire."
              : "Your existing Storylines appear here as Novel / Story projects."
          ),

      novelStory: isFR
        ? "Roman / Histoire"
        : "Novel / Story",

      novelStoryDescription: isFR
        ? "Créez des romans, nouvelles et récits collaboratifs dans Story Studio."
        : "Create novels, short stories, and collaborative narratives in Story Studio.",

      children: isFR
        ? "Livre pour enfants"
        : "Children’s Book",

      childrenDescription: isFR
        ? "Concevez des livres adaptés à l’âge, aux illustrations et à la mise en page."
        : "Create age-appropriate books with illustration and layout needs.",

      essay: isFR
        ? "Essai / Recueil d’essais"
        : "Essay / Essay Collection",

      essayDescription: isFR
        ? "Développez des essais personnels, critiques ou thématiques."
        : "Develop personal, critical, or thematic essays and collections.",

      textbook: isFR
        ? "Manuel scolaire"
        : "Textbook",

      textbookDescription: isFR
        ? "Structurez chapitres, leçons, exercices, références et diagrammes."
        : "Structure chapters, lessons, exercises, references, and diagrams.",

      research: isFR
        ? "Recherche"
        : "Research",

      researchDescription: isFR
        ? "Organisez les questions, sources, preuves, analyses et conclusions."
        : "Organize questions, sources, evidence, analysis, and conclusions.",

      technical: isFR
        ? "Manuel technique"
        : "Technical Manual",

      technicalDescription: isFR
        ? "Préparez des procédures, systèmes, instructions et documentation."
        : "Prepare procedures, systems, instructions, and documentation.",

      screenplay: isFR
        ? "Scénario"
        : "Screenplay",

      screenplayDescription: isFR
        ? "Écrivez scènes, dialogues, actions et structure cinématographique."
        : "Write scenes, dialogue, action, and screen-ready structure.",

      stage: isFR
        ? "Pièce de théâtre"
        : "Stage Play",

      stageDescription: isFR
        ? "Développez actes, scènes, personnages et indications scéniques."
        : "Develop acts, scenes, characters, and stage directions.",

      music: isFR
        ? "Musique et paroles"
        : "Music & Lyrics",

      musicDescription: isFR
        ? "Créez chansons, paroles, structures musicales et collaborations."
        : "Create songs, lyrics, musical structures, and collaborations.",

      childrenStudio: isFR
        ? "Studio Livre pour enfants"
        : "Children’s Book Studio",

      childrenFoundationTitle: isFR
        ? "Planifiez votre livre pour enfants"
        : "Plan Your Children’s Book",

      childrenFoundationIntro: isFR
        ? "Définissez le public, la structure, les illustrations et le format avant de commencer la rédaction."
        : "Define the audience, structure, illustrations, and format before drafting begins.",

      childrenReadOnlyNotice: isFR
        ? "Fondation pilote en lecture seule. Aucun projet ni aucune donnée ne sont créés depuis cet espace."
        : "Read-only foundation pilot. No project or data is created from this workspace.",

      childrenExistingProjectsReadOnlyNotice: isFR
        ? "Preuve authentifiée en lecture seule. Vous pouvez ouvrir vos projets existants, mais la création, l’enregistrement, les téléversements, l’archivage, la restauration, le remplacement et la suppression sont désactivés."
        : "Authenticated read-only proof. You can open existing projects, but creating, saving, uploading, archiving, restoring, replacing, and removing are disabled.",

      childrenAgeTitle: isFR
        ? "Public et tranche d’âge"
        : "Audience and Age Range",

      childrenAgeBody: isFR
        ? "Précisez l’âge cible, le niveau de lecture, le ton et la longueur appropriée."
        : "Plan the target age, reading level, tone, and appropriate length.",

      childrenStructureTitle: isFR
        ? "Pages et doubles pages"
        : "Pages and Spreads",

      childrenStructureBody: isFR
        ? "Organisez le livre par pages, doubles pages, scènes ou courtes sections."
        : "Organize the book by pages, spreads, scenes, or short sections.",

      childrenIllustrationTitle: isFR
        ? "Plan d’illustration"
        : "Illustration Plan",

      childrenIllustrationBody: isFR
        ? "Associez chaque texte à une illustration, une consigne visuelle et son état d’avancement."
        : "Pair each text section with an illustration, visual brief, and progress status.",

      childrenFormatTitle: isFR
        ? "Format et orientation"
        : "Format and Orientation",

      childrenFormatBody: isFR
        ? "Choisissez les dimensions, l’orientation portrait, paysage ou carrée, et les besoins de mise en page."
        : "Choose dimensions, portrait, landscape, or square orientation, and layout needs.",

      childrenLanguageTitle: isFR
        ? "Langue et édition bilingue"
        : "Language and Bilingual Edition",

      childrenLanguageBody: isFR
        ? "Planifiez une édition anglaise, française ou bilingue sans mélanger les textes par erreur."
        : "Plan an English, French, or bilingual edition without mixing manuscript versions.",

      childrenPublicationTitle: isFR
        ? "Préparation à la publication"
        : "Publication Preparation",

      childrenPublicationBody: isFR
        ? "Préparez le titre, le sous-titre, la couverture, les crédits, les métadonnées et les fichiers d’exportation."
        : "Prepare title, subtitle, cover, credits, metadata, and export requirements.",

      studio: "Story Studio",

      forum: isFR
        ? "Forum / Genre"
        : "Forum / Genre",

      structure: isFR
        ? "Structure"
        : "Structure",

      mode: isFR
        ? "Mode d’écriture"
        : "Writing mode",

      status: isFR
        ? "Statut"
        : "Status",

      updated: isFR
        ? "Dernière mise à jour"
        : "Last updated",

      canon: isFR
        ? "Scènes canon"
        : "Canon scenes",

      proposals: isFR
        ? "Propositions en attente"
        : "Pending proposals",

      openStudio: isFR
        ? "Ouvrir Story Studio"
        : "Open Story Studio",

      dashboard: "Story Dashboard",
      reader: "Reader View",

      editorial: isFR
        ? "Comité éditorial"
        : "Editorial Board",

      loading: isFR
        ? "Chargement de vos projets…"
        : "Loading your projects…",

      emptyTitle: isFR
        ? "Aucun projet Roman / Histoire trouvé"
        : "No Novel / Story project found",

      emptyBody: isFR
        ? "Créez votre premier Roman / Histoire ci-dessus. Il s’ouvrira directement dans votre espace livre."
        : "Create your first Novel / Story above. It will open directly in your book workspace.",

      createStory: isFR
        ? "Créer un nouveau Roman / Histoire"
        : "Create a New Novel / Story",

      readerDiscoveryTitle: isFR
        ? "Découvrez avant de créer"
        : "Explore before you create",

      readerDiscoveryBody: isFR
        ? "Vous pouvez découvrir les Studios, lire les œuvres publiques et comprendre les outils de création de NextScenes. Pour créer votre propre projet, passez au rôle Writer."
        : "You can explore the Studios, read public works, and understand the creative tools available on NextScenes. To create your own project, upgrade to Writer.",

      browseReaderView: isFR
        ? "Parcourir Reader View"
        : "Browse Reader View",

      becomeWriter: isFR
        ? "Devenir Writer pour créer un projet"
        : "Become a Writer to create a project",

      readerMode: isFR
        ? "Mode découverte Reader"
        : "Reader discovery mode",

      retry: isFR
        ? "Réessayer"
        : "Retry",

      loginRequired: isFR
        ? "Connectez-vous pour ouvrir le Hub de projets."
        : "Sign in to open the Project Hub.",

      loadError: isFR
        ? "Impossible de charger vos projets."
        : "Could not load your projects.",

      readOnly: isFR
        ? "Aucune donnée de projet n’est modifiée depuis cette page."
        : "No project data is changed from this page.",

      adminConsole: isFR
        ? "Console d’administration"
        : "Admin Console",

      adminConsoleDescription: isFR
        ? "Gérez les utilisateurs, les storylines, les demandes Writer et les outils administratifs."
        : "Manage users, storylines, Writer requests, and administrative tools.",

      openAdminConsole: isFR
        ? "Ouvrir la console"
        : "Open Admin Console",
    }),
    [
      isFR,
      isReaderDiscovery,
      selectedCategory,
    ]
  );

  const novelCollaborationStories = useMemo(
    () => {
      const ownedIds =
        new Set(
          projects
            .map((project) =>
              projectId(project)
            )
            .filter(Boolean)
        );

      return novelCollaborationCandidates
        .filter((project) => {
          const id =
            projectId(project);

          return (
            id &&
            !ownedIds.has(id)
          );
        });
    },
    [
      novelCollaborationCandidates,
      projects,
    ]
  );

  const categories = useMemo(
    () => [
      {
        id: "story",
        title: ui.novelStory,
        description: ui.novelStoryDescription,
        available: true,
        count:
          (nativeChildrensNovelPilotV1F1
            ? novelStoryVisibleStories.filter((story) => story?.bookCategory !== "children_book").length
            : novelStoryVisibleStories.length),
      },
      {
        id: "children",
        title: ui.children,
        description: ui.childrenDescription,
        available: childrensBookPilot,
        // Report actual native books in the same owner-only shelf below.
        // Legacy Children’s Book pilot is unchanged when native reuse is off.
        count: nativeChildrensNovelPilotV1F1
          ? projects.filter((item) => item?.bookCategory === "children_book").length
          : undefined,
      },
      {
        id: "essay",
        title: ui.essay,
        description: ui.essayDescription,
        available: false,
      },
      {
        id: "textbook",
        title: ui.textbook,
        description: ui.textbookDescription,
        available:
          canCreateProjects ||
          textbookProjects.length > 0,
      },
      {
        id: "research",
        title: ui.research,
        description: ui.researchDescription,
        available: false,
      },
      {
        id: "technical",
        title: ui.technical,
        description: ui.technicalDescription,
        available: false,
      },
      {
        id: "screenplay",
        title: ui.screenplay,
        description: ui.screenplayDescription,
        available:
          canCreateProjects ||
          screenplayProjects.length > 0,
      },
      {
        id: "stage",
        title: ui.stage,
        description: ui.stageDescription,
        available: false,
      },
      {
        id: "music",
        title: ui.music,
        description: ui.musicDescription,
        available: false,
      },
    ].filter((category) =>
      ["story", "children", "screenplay", "textbook"].includes(category.id)
    ),
    [
      childrensBookPilot,
      nativeChildrensNovelPilotV1F1,
      canCreateProjects,
      novelStoryVisibleStories,
      projects,
      textbookProjects.length,
      screenplayProjects.length,
      ui,
    ]
  );

  async function loadProjects() {
    if (!isAuthenticated) {
      setProjects([]);
      setError(ui.loginRequired);
      setLoading(false);
      return;
    }

    if (!canCreateProjects) {
      setProjects([]);
      setError("");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const payload =
        await api.getProjectHubProjects({
          language:
            isFR
              ? "fr"
              : "en",
        });

      setProjects(
        Array.isArray(payload?.projects)
          ? payload.projects
          : []
      );
    } catch (err) {
      setProjects([]);
      setError(
        clean(err?.message) ||
        ui.loadError
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, lang]);

  // Project cards retain their existing language filter. The companion also
  // checks the other language through the same authenticated owner-only route.
  useEffect(() => {
    let active = true;
    setCompanionOtherLanguageLoaded(false);
    setCompanionOtherLanguageProjects([]);

    if (!isAuthenticated || !canCreateProjects) {
      setCompanionOtherLanguageLoaded(true);
      return () => { active = false; };
    }

    api.getProjectHubProjects({ language: isFR ? "en" : "fr" })
      .then((payload) => {
        if (!active) return;
        setCompanionOtherLanguageProjects(
          Array.isArray(payload?.projects) ? payload.projects : []
        );
        setCompanionOtherLanguageLoaded(true);
      })
      .catch(() => {
        if (!active) return;
        setCompanionOtherLanguageProjects([]);
        setCompanionOtherLanguageLoaded(true);
      });

    return () => { active = false; };
  }, [isAuthenticated, canCreateProjects, lang, isFR]);

  // V1F2: restore the exact native book when returning from Writer Studio.
  // Never interpret a legacy Blueprint ID or Novel as a native Children's Book.
  useEffect(() => {
    if (!nativeChildrensNovelPilotV1F1 || selectedCategory !== "children" || !initialProjectId) return;
    const saved = projects.find((item) =>
      item?.bookCategory === "children_book" &&
      projectId(item) === clean(initialProjectId));
    if (saved) setNativeChildrensOpenV1F1(saved);
  }, [nativeChildrensNovelPilotV1F1, selectedCategory, initialProjectId, projects]);

  async function loadNovelStoryVisibility() {
    try {
      setNovelCollaborationLoading(
        true
      );
      setNovelCollaborationError("");

      const rows = [];
      const pageSize = 100;

      // Storylines uses the same access-filtered list route.
      // Walk its pagination so the Project Hub category number
      // represents the complete visible set rather than only page 1.
      for (
        let page = 1;
        page <= 20;
        page += 1
      ) {
        const payload =
          await api.getStoriesList({
            page,
            limit: pageSize,
          });

        const batch =
          Array.isArray(payload)
            ? payload
            : Array.isArray(
                payload?.stories
              )
            ? payload.stories
            : [];

        rows.push(...batch);

        if (
          batch.length <
          pageSize
        ) {
          break;
        }
      }

      const uniqueRows = [];
      const seenIds = new Set();

      rows.forEach((story) => {
        const id =
          projectId(story);

        if (!id || seenIds.has(id)) {
          return;
        }

        seenIds.add(id);
        uniqueRows.push(story);
      });

      setNovelStoryVisibleStories(
        uniqueRows
      );

      // The Project Hub collaboration shelf mirrors only the
      // Collaborative subset of the Storylines-visible set.
      // Writer Studio remains the authority for what can be done
      // after the user opens a book.
      const collaborative =
        uniqueRows.filter((story) => {
          const mode =
            clean(
              story?.writingMode ||
                story?.mode
            ).toLowerCase();

          return mode.includes(
            "collab"
          );
        });

      setNovelCollaborationCandidates(
        collaborative
      );
    } catch (err) {
      setNovelStoryVisibleStories([]);
      setNovelCollaborationCandidates(
        []
      );
      setNovelCollaborationError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible de charger les Storylines disponibles."
              : "Could not load available Storylines."
          )
      );
    } finally {
      setNovelCollaborationLoading(
        false
      );
    }
  }

  useEffect(() => {
    loadNovelStoryVisibility();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    canCreateProjects,
    lang,
  ]);

  useEffect(() => {
    const requestedId = clean(initialProjectId);

    if (
      selectedCategory !== "story" ||
      !requestedId ||
      activeNovelStoryDraftProject ||
      projects.length === 0
    ) {
      return;
    }

    const requestedProject =
      projects.find(
        (project) =>
          projectId(project) === requestedId
      ) || null;

    if (!requestedProject) return;

    setActiveNovelStoryDraftProject(
      requestedProject
    );
  }, [
    initialProjectId,
    selectedCategory,
    projects,
    activeNovelStoryDraftProject,
  ]);

  async function loadScreenplayProjects() {
    if (!isAuthenticated) {
      setScreenplayProjects([]);
      setArchivedScreenplayProjects([]);
      setActiveScreenplayProject(null);
      setScreenplayProjectsError("");
      setScreenplayProjectsLoading(false);
      return;
    }

    try {
      setScreenplayProjectsLoading(true);
      setScreenplayProjectsError("");

      const [
        activePayload,
        archivedPayload,
      ] = await Promise.all([
        api.getScreenplayProjects(),
        canCreateProjects
          ? api.getScreenplayProjects({
              status: "archived",
            })
          : Promise.resolve({
              projects: [],
            }),
      ]);

      const rows =
        Array.isArray(
          activePayload?.projects
        )
          ? activePayload.projects
          : [];

      const archivedRows =
        Array.isArray(
          archivedPayload?.projects
        )
          ? archivedPayload.projects
          : [];

      setScreenplayProjects(rows);
      setArchivedScreenplayProjects(
        archivedRows
      );

      const routeProjectId =
        projectIdFromSearch();

      if (routeProjectId) {
        const restored =
          rows.find(
            (project) =>
              clean(project?.id) ===
              clean(routeProjectId)
          ) || null;

        setActiveScreenplayProject(
          restored
        );

        if (!restored) {
          setScreenplayProjectsError(
            isFR
              ? "Ce projet de scénario est introuvable."
              : "This Screenplay project could not be found."
          );
        }
      }
    } catch (err) {
      setScreenplayProjects([]);
      setArchivedScreenplayProjects([]);
      setActiveScreenplayProject(null);

      setScreenplayProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de charger vos projets de scénario."
            : "Could not load your Screenplay projects."
        )
      );
    } finally {
      setScreenplayProjectsLoading(false);
    }
  }

  useEffect(() => {
    loadScreenplayProjects();

    /*
     * Membership-aware discovery lets an accepted Screenplay member
     * find the private project again from Project Hub. The server
     * remains authoritative about which projects are returned.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    canCreateProjects,
  ]);

  useEffect(() => {
    setScreenplayInvitations([]);
    setScreenplayInvitationsError("");
    setScreenplayInvitationsNotice("");
    setScreenplayInvitationActionId("");
    setShowScreenplayInvitations(false);
  }, [
    activeScreenplayProject?.id,
  ]);

  async function loadScreenplayInvitations(
    projectId =
      clean(
        activeScreenplayProject
          ?.id
      )
  ) {
    const id =
      clean(projectId);

    if (
      !id ||
      screenplayProjectAccess
        ?.canManageProject !==
        true
    ) {
      return;
    }

    try {
      setShowScreenplayInvitations(
        true
      );

      setScreenplayInvitationsLoading(
        true
      );

      setScreenplayInvitationsError("");
      setScreenplayInvitationsNotice("");

      const payload =
        await api
          .getScreenplayProjectInvitations(
            id
          );

      setScreenplayInvitations(
        Array.isArray(
          payload?.invitations
        )
          ? payload.invitations
          : []
      );
    } catch (err) {
      setScreenplayInvitations([]);

      setScreenplayInvitationsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de charger les invitations."
            : "Could not load the invitations."
        )
      );
    } finally {
      setScreenplayInvitationsLoading(
        false
      );
    }
  }

  async function resendScreenplayInvitation(
    invitation
  ) {
    const projectId =
      clean(
        activeScreenplayProject
          ?.id
      );

    const invitationId =
      clean(
        invitation?.id
      );

    if (
      !projectId ||
      !invitationId ||
      screenplayProjectAccess
        ?.canManageProject !==
        true ||
      invitation?.canResend !==
        true
    ) {
      return;
    }

    try {
      setScreenplayInvitationActionId(
        invitationId
      );

      setScreenplayInvitationsError("");
      setScreenplayInvitationsNotice("");

      const payload =
        await api
          .resendScreenplayProjectInvitation(
            projectId,
            invitationId
          );

      const updatedInvitation =
        payload?.invitation ||
        null;

      if (updatedInvitation) {
        setScreenplayInvitations(
          (current) =>
            current.map(
              (row) =>
                clean(row?.id) ===
                invitationId
                  ? updatedInvitation
                  : row
            )
        );
      }

      setScreenplayInvitationsNotice(
        isFR
          ? `Invitation renvoyée à ${clean(updatedInvitation?.email || invitation?.email)}.`
          : `Invitation resent to ${clean(updatedInvitation?.email || invitation?.email)}.`
      );
    } catch (err) {
      const returnedInvitation =
        err?.data?.invitation ||
        null;

      if (returnedInvitation) {
        setScreenplayInvitations(
          (current) =>
            current.map(
              (row) =>
                clean(row?.id) ===
                invitationId
                  ? returnedInvitation
                  : row
            )
        );
      }

      setScreenplayInvitationsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de renvoyer cette invitation."
            : "Could not resend this invitation."
        )
      );
    } finally {
      setScreenplayInvitationActionId("");
    }
  }


  async function loadScreenplayProjectDetail(
    projectId
  ) {
    const requestId =
      screenplayProjectPlanLoadRequest
        .current + 1;

    screenplayProjectPlanLoadRequest
      .current = requestId;

    const id =
      clean(projectId);

    if (!id) {
      setScreenplayProjectAccess(null);
      setScreenplayProjectPlan(null);
      setScreenplayProjectPlanError("");
      setScreenplayProjectPlanLoading(false);
      return;
    }

    try {
      setScreenplayProjectPlanLoading(
        true
      );

      setScreenplayProjectPlanError("");

      const payload =
        await api.getScreenplayProject(
          id
        );

      if (
        requestId !==
        screenplayProjectPlanLoadRequest
          .current
      ) {
        return;
      }

      const returnedProject =
        payload?.project || null;

      const returnedPlan =
        payload?.projectPlan || null;

      const returnedAccess =
        payload?.access || null;

      if (!returnedPlan) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné la vue d’ensemble du projet."
            : "The server did not return the Screenplay Project Overview."
        );
      }

      if (returnedProject) {
        setActiveScreenplayProject(
          (current) =>
            clean(current?.id) === id
              ? {
                  ...current,
                  ...returnedProject,
                }
              : current
        );
      }

      setScreenplayProjectAccess(
        returnedAccess
      );

      if (
        returnedAccess &&
        returnedAccess
          .canOpenScriptWorkspace !==
          true &&
        screenplayWorkspace ===
          "script-workspace"
      ) {
        setScreenplayWorkspace(
          "project-plan"
        );

        replaceProjectWorkspacePath(
          SCREENPLAY_BASE_PATH,
          "project-plan",
          {
            foundationWorkspace:
              "project-plan",
            projectId:
              id,
          }
        );
      }

      setScreenplayProjectPlan(
        returnedPlan
      );
    } catch (err) {
      if (
        requestId !==
        screenplayProjectPlanLoadRequest
          .current
      ) {
        return;
      }

      setScreenplayProjectAccess(null);
      setScreenplayProjectPlan(null);

      setScreenplayProjectPlanError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’ouvrir la vue d’ensemble du projet."
            : "Could not open the Screenplay Project Overview."
        )
      );
    } finally {
      if (
        requestId ===
        screenplayProjectPlanLoadRequest
          .current
      ) {
        setScreenplayProjectPlanLoading(
          false
        );
      }
    }
  }

  useEffect(() => {
    const id =
      clean(
        activeScreenplayProject?.id
      );

    if (
      selectedCategory !==
        "screenplay" ||
      !id
    ) {
      screenplayProjectPlanLoadRequest
        .current += 1;

      setScreenplayProjectAccess(null);
      setScreenplayProjectPlan(null);
      setScreenplayProjectPlanError("");
      setScreenplayProjectPlanLoading(
        false
      );
      return;
    }

    loadScreenplayProjectDetail(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCategory,
    activeScreenplayProject?.id,
  ]);

  async function saveScreenplayProjectOverview(
    overview,
    expectedRevision
  ) {
    const id =
      clean(
        activeScreenplayProject?.id
      );

    if (!id) {
      throw new Error(
        isFR
          ? "Aucun projet de scénario actif."
          : "No active Screenplay project."
      );
    }

    const payload =
      await api.saveScreenplayProjectPlan(
        id,
        {
          overview,
          expectedRevision,
        }
      );

    const nextPlan =
      payload?.projectPlan || null;

    if (!nextPlan) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la vue d’ensemble enregistrée."
          : "The server did not return the saved Project Overview."
      );
    }

    if (
      clean(
        projectIdFromSearch()
      ) === id
    ) {
      setScreenplayProjectPlan(
        (current) => ({
          ...nextPlan,
          storyMap:
            current?.storyMap ??
            nextPlan.storyMap,
        })
      );
    }

    return nextPlan;
  }

  async function saveScreenplayStoryMap(
    storyMap,
    expectedRevision
  ) {
    const id =
      clean(
        activeScreenplayProject?.id
      );

    if (!id) {
      throw new Error(
        isFR
          ? "Aucun projet de scénario actif."
          : "No active Screenplay project."
      );
    }

    const payload =
      await api.saveScreenplayProjectPlan(
        id,
        {
          storyMap,
          expectedRevision,
        }
      );

    const nextPlan =
      payload?.projectPlan || null;

    if (!nextPlan) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la Story Map enregistrée."
          : "The server did not return the saved Story Map."
      );
    }

    if (
      clean(
        projectIdFromSearch()
      ) === id
    ) {
      setScreenplayProjectPlan(
        (current) => ({
          ...nextPlan,
          overview:
            current?.overview ??
            nextPlan.overview,
        })
      );
    }

    return nextPlan;
  }

  async function createScreenplayProject(
    event
  ) {
    event?.preventDefault?.();

    const title =
      clean(screenplayTitle);

    if (!title) {
      setScreenplayProjectsError(
        isFR
          ? "Saisissez d’abord le titre du scénario."
          : "Enter the screenplay title first."
      );

      return;
    }

    const isGroupMode =
      screenplayGroupWritingMode(
        screenplayWritingMode
      );

    let inviteEmails = [];

    if (isGroupMode) {
      const parsed =
        parseScreenplayInviteEmails(
          screenplayInviteEmailsText
        );

      if (
        parsed.invalid.length > 0
      ) {
        setScreenplayInviteEmailsError(
          isFR
            ? `Adresse e-mail invalide : ${parsed.invalid[0]}`
            : `Invalid invitee email: ${parsed.invalid[0]}`
        );

        return;
      }

      if (
        parsed.emails.length === 0
      ) {
        setScreenplayInviteEmailsError(
          isFR
            ? "Ajoutez au moins une adresse e-mail pour l’écriture en groupe."
            : "Add at least one invitee email for Group Writing."
        );

        return;
      }

      if (
        parsed.emails.length > 25
      ) {
        setScreenplayInviteEmailsError(
          isFR
            ? "Vous pouvez inviter jusqu’à 25 personnes lors de la création du projet."
            : "You can invite up to 25 people when creating the project."
        );

        return;
      }

      inviteEmails =
        parsed.emails;
    } else {
      setScreenplayInviteEmailsError(
        ""
      );
    }

    try {
      setScreenplayCreating(true);
      setScreenplayProjectsError("");
      setScreenplayInviteEmailsError(
        ""
      );

      const payload =
        await api.createScreenplayProject({
          title,

          primaryLanguage:
            isFR
              ? "fr"
              : "en",

          writingMode:
            screenplayWritingMode,

          inviteEmails,
        });

      const project =
        payload?.project || null;

      const id =
        clean(project?.id);

      if (!project || !id) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le projet créé."
            : "The server did not return the created project."
        );
      }

      const invitationSummary =
        payload
          ?.collaborationFoundation
          ?.invitationSummary;

      setScreenplayProjects(
        (current) => [
          project,
          ...current.filter(
            (item) =>
              clean(item?.id) !== id
          ),
        ]
      );

      setScreenplayTitle("");
      setScreenplayWritingMode(
        "solo"
      );
      setScreenplayInviteEmailsText(
        ""
      );
      setScreenplayInviteEmailsError(
        ""
      );

      openScreenplayProject(
        project
      );

      if (isGroupMode) {
        const requested =
          Number(
            invitationSummary
              ?.requested || 0
          );

        const sent =
          Number(
            invitationSummary
              ?.sent || 0
          );

        const failed =
          Number(
            invitationSummary
              ?.failed || 0
          );

        setScreenplayInvitationNotice({
          tone:
            failed > 0
              ? "warning"
              : "success",

          text:
            failed > 0
              ? (
                  isFR
                    ? `Projet créé. ${sent} invitation(s) envoyée(s), mais ${failed} sur ${requested} n’ont pas pu être envoyées. Ces invitations devront être renvoyées avant que ces personnes puissent rejoindre le projet.`
                    : `Project created. ${sent} invitation email(s) sent, but ${failed} of ${requested} could not be delivered. Those invitations will need to be resent before those people can join the project.`
                )
              : (
                  isFR
                    ? `Projet créé. ${sent} invitation(s) envoyée(s).`
                    : `Project created. ${sent} invitation email(s) sent.`
                ),
        });
      }
    } catch (err) {
      setScreenplayProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de créer ce projet de scénario."
            : "Could not create this Screenplay project."
        )
      );
    } finally {
      setScreenplayCreating(false);
    }
  }

  function startEditScreenplayProject(
    project
  ) {
    if (
      project?.access
        ?.canManageProject !==
      true
    ) {
      return;
    }

    setScreenplayProjectsError("");
    setScreenplayEditError("");

    setEditingScreenplayProject(
      project
    );

    setScreenplayEditTitle(
      clean(project?.title)
    );

    setScreenplayEditPrimaryLanguage(
      clean(
        project?.primaryLanguage
      ).toLowerCase() ||
        "en"
    );
  }

  function cancelEditScreenplayProject() {
    if (screenplayProjectActionId) {
      return;
    }

    setEditingScreenplayProject(
      null
    );

    setScreenplayEditTitle("");
    setScreenplayEditPrimaryLanguage(
      "en"
    );
    setScreenplayEditError("");
  }

  async function saveEditedScreenplayProject(
    event
  ) {
    event?.preventDefault?.();

    const project =
      editingScreenplayProject;

    const id =
      clean(project?.id);

    const title =
      clean(screenplayEditTitle);

    if (
      !id ||
      project?.access
        ?.canManageProject !==
        true
    ) {
      return;
    }

    if (!title) {
      setScreenplayEditError(
        isFR
          ? "Saisissez le titre du scénario."
          : "Enter the Screenplay title."
      );
      return;
    }

    try {
      setScreenplayProjectActionId(
        id
      );

      setScreenplayEditError("");
      setScreenplayProjectsError("");

      const payload =
        await api
          .updateScreenplayProjectDetails(
            id,
            {
              title,
              primaryLanguage:
                screenplayEditPrimaryLanguage,
            }
          );

      const updatedProject =
        payload?.project || null;

      if (!updatedProject) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le projet mis à jour."
            : "The server did not return the updated Screenplay project."
        );
      }

      setScreenplayProjects(
        (current) =>
          current.map(
            (row) =>
              clean(row?.id) ===
              id
                ? {
                    ...row,
                    ...updatedProject,
                    access:
                      row?.access,
                  }
                : row
          )
      );

      if (
        clean(
          activeScreenplayProject
            ?.id
        ) === id
      ) {
        setActiveScreenplayProject(
          (current) => ({
            ...current,
            ...updatedProject,
          })
        );
      }

      setEditingScreenplayProject(
        null
      );

      setScreenplayEditTitle("");
      setScreenplayEditPrimaryLanguage(
        "en"
      );
      setScreenplayEditError("");
    } catch (err) {
      setScreenplayEditError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de mettre à jour ce projet de scénario."
            : "Could not update this Screenplay project."
        )
      );
    } finally {
      setScreenplayProjectActionId("");
    }
  }


  function requestArchiveScreenplayProject(
    project
  ) {
    if (
      project?.access
        ?.canManageProject !==
      true
    ) {
      return;
    }

    setScreenplayProjectsError("");
    setPendingScreenplayArchiveProject(
      project
    );
  }

  function cancelArchiveScreenplayProject() {
    if (screenplayProjectActionId) {
      return;
    }

    setPendingScreenplayArchiveProject(
      null
    );
  }

  async function confirmArchiveScreenplayProject() {
    const project =
      pendingScreenplayArchiveProject;

    const id =
      clean(project?.id);

    if (
      !id ||
      project?.access
        ?.canManageProject !==
        true
    ) {
      return;
    }

    try {
      setScreenplayProjectActionId(
        id
      );
      setScreenplayProjectsError("");

      await api.archiveScreenplayProject(
        id
      );

      setPendingScreenplayArchiveProject(
        null
      );

      await loadScreenplayProjects();
    } catch (err) {
      setScreenplayProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’archiver ce projet de scénario."
            : "Could not archive this Screenplay project."
        )
      );
    } finally {
      setScreenplayProjectActionId("");
    }
  }

  async function restoreScreenplayProject(
    project
  ) {
    const id =
      clean(project?.id);

    if (
      !id ||
      project?.access
        ?.canManageProject !==
        true
    ) {
      return;
    }

    try {
      setScreenplayProjectActionId(
        id
      );
      setScreenplayProjectsError("");

      await api.restoreScreenplayProject(
        id
      );

      await loadScreenplayProjects();
    } catch (err) {
      setScreenplayProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de restaurer ce projet de scénario."
            : "Could not restore this Screenplay project."
        )
      );
    } finally {
      setScreenplayProjectActionId("");
    }
  }


  function openScreenplayWorkspace(
    workspace
  ) {
    const requestedWorkspace =
      [
        "project-plan",
        "story-map",
        "characters-world",
        "script-workspace",
        "proposal-round",
        "review-and-decide",
      ].includes(workspace)
        ? workspace
        : "project-plan";

    const collaborationEnabled =
      clean(
        activeScreenplayProject
          ?.writingMode
      ).toLowerCase() !==
      "solo";

    let nextWorkspace =
      requestedWorkspace;

    if (
      requestedWorkspace ===
        "script-workspace" &&
      screenplayProjectAccess
        ?.canOpenScriptWorkspace !==
        true
    ) {
      nextWorkspace =
        "project-plan";
    }

    if (
      (
        requestedWorkspace ===
          "proposal-round" ||
        requestedWorkspace ===
          "review-and-decide"
      ) &&
      !collaborationEnabled
    ) {
      nextWorkspace =
        "project-plan";
    }

    if (
      requestedWorkspace ===
        "review-and-decide" &&
      screenplayProjectAccess
        ?.canManageProject !==
        true
    ) {
      nextWorkspace =
        collaborationEnabled
          ? "proposal-round"
          : "project-plan";
    }

    const id =
      clean(
        activeScreenplayProject?.id
      );

    setScreenplayWorkspace(
      nextWorkspace
    );

    replaceProjectWorkspacePath(
      SCREENPLAY_BASE_PATH,
      nextWorkspace,
      {
        foundationWorkspace:
          "project-plan",
        projectId: id,
      }
    );

    if (
      typeof window !==
        "undefined"
    ) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }

  function openScreenplayProject(
    project
  ) {
    const id =
      clean(project?.id);

    if (!id) {
      return;
    }

    setScreenplayInvitationNotice(
      null
    );

    setScreenplayProjectAccess(
      null
    );

    setActiveScreenplayProject(
      project
    );

    const nextWorkspace =
      currentScreenplayWorkspace();

    setScreenplayWorkspace(
      nextWorkspace
    );

    replaceProjectWorkspacePath(
      SCREENPLAY_BASE_PATH,
      nextWorkspace,
      {
        foundationWorkspace:
          "project-plan",
        projectId: id,
      }
    );

    if (
      typeof onContextChange ===
      "function"
    ) {
      onContextChange({
        category:
          "screenplay",

        projectId:
          id,

        projectTitle:
          clean(project?.title),
      });
    }

    if (
      typeof window !==
        "undefined"
    ) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }

  function closeScreenplayProject() {
    screenplayProjectPlanLoadRequest
      .current += 1;

    setActiveScreenplayProject(null);
    setScreenplayProjectAccess(null);
    setScreenplayWorkspace(
      "project-plan"
    );
    setScreenplayProjectPlan(null);
    setScreenplayProjectPlanError("");
    setScreenplayProjectPlanLoading(
      false
    );

    replaceProjectHubPath(
      SCREENPLAY_BASE_PATH,
      {
        clearProjectId: true,
      }
    );

    if (
      typeof onContextChange ===
      "function"
    ) {
      onContextChange({
        category:
          "screenplay",

        projectId: "",
        projectTitle: "",
      });
    }
  }

  async function loadTextbookProjects() {
    if (!isAuthenticated) {
      setTextbookProjects([]);
      setArchivedTextbookProjects([]);
      setActiveTextbookProject(null);
      setTextbookProjectsError("");
      setTextbookProjectsLoading(false);
      return;
    }

    try {
      setTextbookProjectsLoading(true);
      setTextbookProjectsError("");

      const [
        activePayload,
        archivedPayload,
      ] = await Promise.all([
        api.getTextbookProjects(),

        canCreateProjects
          ? api.getTextbookProjects({
              status: "archived",
            })
          : Promise.resolve({
              projects: [],
            }),
      ]);

      const rows =
        Array.isArray(
          activePayload?.projects
        )
          ? activePayload.projects
          : [];

      const archivedRows =
        Array.isArray(
          archivedPayload?.projects
        )
          ? archivedPayload.projects
          : [];

      setTextbookProjects(rows);
      setArchivedTextbookProjects(
        archivedRows
      );

      const routeProjectId =
        projectIdFromSearch();

      if (routeProjectId) {
        const restored =
          rows.find(
            (project) =>
              clean(project?.id) ===
              clean(routeProjectId)
          ) || null;

        setActiveTextbookProject(
          restored
        );

        if (!restored) {
          const archivedMatch =
            archivedRows.find(
              (project) =>
                clean(project?.id) ===
                clean(routeProjectId)
            );

          setTextbookProjectsError(
            archivedMatch
              ? (
                  isFR
                    ? "Ce manuel est archivé. Restaurez-le ci-dessous pour le rouvrir."
                    : "This Textbook is archived. Restore it below to reopen it."
                )
              : (
                  isFR
                    ? "Ce projet de manuel est introuvable."
                    : "This Textbook project could not be found."
                )
          );
        }
      }
    } catch (err) {
      setTextbookProjects([]);
      setArchivedTextbookProjects([]);
      setActiveTextbookProject(null);

      setTextbookProjectsError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible de charger vos projets de manuel."
              : "Could not load your Textbook projects."
          )
      );
    } finally {
      setTextbookProjectsLoading(false);
    }
  }

  useEffect(() => {
    loadTextbookProjects();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    canCreateProjects,
    textbookActorKey,
  ]);

  function requestArchiveTextbookProject(
    project
  ) {
    const id =
      clean(
        project?.id
      );

    if (
      !id ||
      project?.access
        ?.canManageProject !==
        true
    ) {
      return;
    }

    setTextbookProjectsError("");
    setPendingTextbookArchiveProject(
      project
    );
  }


  function cancelArchiveTextbookProject() {
    if (textbookProjectActionId) {
      return;
    }

    setPendingTextbookArchiveProject(
      null
    );
  }


  async function confirmArchiveTextbookProject() {
    const project =
      pendingTextbookArchiveProject;

    const id =
      clean(
        project?.id
      );

    if (
      !id ||
      project?.access
        ?.canManageProject !==
        true
    ) {
      return;
    }

    try {
      setTextbookProjectActionId(
        id
      );
      setTextbookProjectsError("");

      await api
        .archiveTextbookProject(
          id
        );

      setPendingTextbookArchiveProject(
        null
      );

      await loadTextbookProjects();
    } catch (err) {
      setTextbookProjectsError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible d’archiver ce manuel."
              : "Could not archive this Textbook."
          )
      );
    } finally {
      setTextbookProjectActionId("");
    }
  }


  async function restoreTextbookProject(
    project
  ) {
    const id =
      clean(
        project?.id
      );

    if (
      !id ||
      project?.access
        ?.canManageProject !==
        true
    ) {
      return;
    }

    try {
      setTextbookProjectActionId(
        id
      );
      setTextbookProjectsError("");

      await api
        .restoreTextbookProject(
          id
        );

      await loadTextbookProjects();
    } catch (err) {
      setTextbookProjectsError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible de restaurer ce manuel."
              : "Could not restore this Textbook."
          )
      );
    } finally {
      setTextbookProjectActionId("");
    }
  }


  async function loadTextbookProjectDetail(
    projectId
  ) {
    const requestId =
      textbookProjectPlanLoadRequest
        .current + 1;

    textbookProjectPlanLoadRequest
      .current = requestId;

    const id =
      clean(projectId);

    if (!id) {
      setTextbookProjectAccess(null);
      setTextbookProjectAccessActorKey("");
      setTextbookProjectPlan(null);
      setTextbookProjectPlanError("");
      setTextbookProjectPlanLoading(false);
      return;
    }

    try {
      setTextbookProjectPlanLoading(true);
      setTextbookProjectPlanError("");

      const payload =
        await api.getTextbookProject(
          id
        );

      if (
        requestId !==
        textbookProjectPlanLoadRequest
          .current
      ) {
        return;
      }

      const returnedProject =
        payload?.project || null;

      const returnedPlan =
        payload?.projectPlan || null;

      const returnedAccess =
        payload?.access || null;

      if (!returnedPlan) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné la vue d’ensemble du manuel."
            : "The server did not return the Textbook Project Overview."
        );
      }

      if (returnedProject) {
        setActiveTextbookProject(
          (current) =>
            clean(current?.id) === id
              ? {
                  ...current,
                  ...returnedProject,
                }
              : current
        );
      }

      setTextbookProjectAccess(
        returnedAccess
      );
      setTextbookProjectAccessActorKey(
        textbookActorKey
      );

      const directOpenRequested =
        textbookDirectOpenRequest
          .current === id;

      if (directOpenRequested) {
        textbookDirectOpenRequest
          .current = "";
      }

      const directChapters =
        Array.isArray(
          returnedPlan
            ?.bookMap
            ?.items
        )
          ? returnedPlan
              .bookMap
              .items
              .filter(
                (item) =>
                  clean(
                    item?.type
                  ) === "chapter"
              )
          : [];

      if (
        directOpenRequested &&
        returnedAccess
          ?.chapterWorkspaceEnabled ===
          true &&
        directChapters.length > 0
      ) {
        const onlyChapterId =
          directChapters.length === 1
            ? clean(
                directChapters[0]
                  ?.itemId
              )
            : "";

        const directWorkspace =
          onlyChapterId
            ? "chapter-workspace"
            : "write-chapters";

        setTextbookProjectPlan(
          returnedPlan
        );

        setTextbookWorkspace(
          directWorkspace
        );

        setTextbookChapterItemId(
          onlyChapterId
        );

        setTextbookDirectOpenLoading(
          false
        );

        replaceProjectWorkspacePath(
          TEXTBOOK_BASE_PATH,
          directWorkspace,
          {
            foundationWorkspace:
              "project-plan",
            projectId: id,
            search:
              textbookSearchWithChapterItem(
                onlyChapterId
              ),
          }
        );

        return;
      }

      if (directOpenRequested) {
        const directWorkspace =
          returnedAccess
            ?.bookMapEnabled === true
            ? "overview"
            : "project-plan";

        setTextbookProjectPlan(
          returnedPlan
        );

        setTextbookWorkspace(
          directWorkspace
        );

        setTextbookChapterItemId(
          ""
        );

        setTextbookDirectOpenLoading(
          false
        );

        replaceProjectWorkspacePath(
          TEXTBOOK_BASE_PATH,
          directWorkspace,
          {
            foundationWorkspace:
              "project-plan",
            projectId: id,
            search:
              textbookSearchWithChapterItem(
                ""
              ),
          }
        );

        return;
      }

      if (
        (
          textbookWorkspace ===
            "chapter-workspace" &&
          (
            returnedAccess
              ?.chapterWorkspaceEnabled !==
              true ||
            !clean(
              textbookChapterItemId
            )
          )
        ) ||
        (
          [
            "review-your-book",
            "publication-proof",
            "publication-preparation",
          ].includes(
            textbookWorkspace
          ) &&
          returnedAccess
            ?.chapterWorkspaceEnabled !==
            true
        )
      ) {
        const fallbackWorkspace =
          returnedAccess
            ?.bookMapEnabled ===
            true
            ? "book-map"
            : "project-plan";

        setTextbookWorkspace(
          fallbackWorkspace
        );

        setTextbookChapterItemId(
          ""
        );

        setTextbookChapterWorkspaceData(
          null
        );

        setTextbookChapterWorkspaceError(
          ""
        );

        replaceProjectWorkspacePath(
          TEXTBOOK_BASE_PATH,
          fallbackWorkspace,
          {
            foundationWorkspace:
              "project-plan",
            projectId:
              id,
            search:
              textbookSearchWithChapterItem(
                ""
              ),
          }
        );
      } else if (
        returnedAccess
          ?.bookMapEnabled !==
          true &&
        textbookWorkspace ===
          "book-map"
      ) {
        setTextbookWorkspace(
          "project-plan"
        );

        replaceProjectWorkspacePath(
          TEXTBOOK_BASE_PATH,
          "project-plan",
          {
            foundationWorkspace:
              "project-plan",
            projectId:
              id,
            search:
              textbookSearchWithChapterItem(
                ""
              ),
          }
        );
      }

      setTextbookProjectPlan(
        returnedPlan
      );
    } catch (err) {
      if (
        requestId !==
        textbookProjectPlanLoadRequest
          .current
      ) {
        return;
      }

      if (
        textbookDirectOpenRequest
          .current === id
      ) {
        textbookDirectOpenRequest
          .current = "";
        setTextbookDirectOpenLoading(
          false
        );
      }

      setTextbookProjectAccess(null);
      setTextbookProjectAccessActorKey("");
      setTextbookProjectPlan(null);

      setTextbookProjectPlanError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible d’ouvrir la vue d’ensemble du manuel."
              : "Could not open the Textbook Project Overview."
          )
      );
    } finally {
      if (
        requestId ===
        textbookProjectPlanLoadRequest
          .current
      ) {
        setTextbookProjectPlanLoading(
          false
        );
      }
    }
  }


  async function loadTextbookChapterWorkspace(
    projectId,
    chapterItemId
  ) {
    const requestId =
      textbookChapterWorkspaceLoadRequest
        .current + 1;

    textbookChapterWorkspaceLoadRequest
      .current =
      requestId;

    const project =
      clean(projectId);

    const chapter =
      clean(chapterItemId);

    if (
      !project ||
      !chapter
    ) {
      setTextbookChapterWorkspaceData(
        null
      );
      setTextbookChapterWorkspaceActorKey(
        ""
      );

      setTextbookChapterWorkspaceError(
        ""
      );

      setTextbookChapterWorkspaceLoading(
        false
      );

      return;
    }

    try {
      setTextbookChapterWorkspaceLoading(
        true
      );

      setTextbookChapterWorkspaceError(
        ""
      );

      const [
        payload,
        figureResult,
      ] =
        await Promise.all([
          api
            .getTextbookChapterDraft(
              project,
              chapter
            ),

          api
            .getTextbookFigureAssets(
              project,
              chapter
            )
            .then(
              (
                figurePayload
              ) => ({
                payload:
                  figurePayload,
                error: null,
              })
            )
            .catch(
              (error) => ({
                payload: null,
                error,
              })
            ),
        ]);

      if (
        requestId !==
        textbookChapterWorkspaceLoadRequest
          .current
      ) {
        return;
      }

      setTextbookChapterWorkspaceData(
        {
          chapter:
            payload?.chapter ||
            null,

          chapterDraft:
            payload
              ?.chapterDraft ||
            null,

          proposalRound:
            payload
              ?.proposalRound ||
            null,

          myProposals:
            Array.isArray(
              payload
                ?.myProposals
            )
              ? payload
                  .myProposals
              : [],

          proposalReviewProposals:
            Array.isArray(
              payload
                ?.proposalReviewProposals
            )
              ? payload
                  .proposalReviewProposals
              : [],

          acceptedProposalGuidance:
            payload
              ?.acceptedProposalGuidance ||
            null,

          figureAssets:
            Array.isArray(
              figureResult
                ?.payload
                ?.figureAssets
            )
              ? figureResult
                  .payload
                  .figureAssets
              : [],

          figureLoadError:
            clean(
              figureResult
                ?.error
                ?.message
            ),

          access:
            payload?.access ||
            null,
        }
      );
      setTextbookChapterWorkspaceActorKey(
        textbookActorKey
      );
    } catch (error) {
      if (
        requestId !==
        textbookChapterWorkspaceLoadRequest
          .current
      ) {
        return;
      }

      setTextbookChapterWorkspaceData(
        null
      );
      setTextbookChapterWorkspaceActorKey(
        ""
      );

      setTextbookChapterWorkspaceError(
        clean(
          error?.message
        ) ||
          (
            isFR
              ? "Impossible d’ouvrir ce chapitre."
              : "Could not open this chapter."
          )
      );
    } finally {
      if (
        requestId ===
        textbookChapterWorkspaceLoadRequest
          .current
      ) {
        setTextbookChapterWorkspaceLoading(
          false
        );
      }
    }
  }

  useEffect(() => {
    const id =
      clean(
        activeTextbookProject?.id
      );

    if (
      selectedCategory !==
        "textbook" ||
      !id
    ) {
      textbookProjectPlanLoadRequest
        .current += 1;

      setTextbookProjectAccess(null);
      setTextbookProjectAccessActorKey("");
      setTextbookProjectPlan(null);
      setTextbookProjectPlanError("");
      setTextbookProjectPlanLoading(
        false
      );
      return;
    }

    loadTextbookProjectDetail(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCategory,
    activeTextbookProject?.id,
    textbookActorKey,
  ]);


  useEffect(() => {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    if (
      selectedCategory !==
        "textbook" ||
      !projectId ||
      !currentTextbookProjectAccess ||
      currentTextbookProjectAccess
        ?.canManageProject ===
          true ||
      [
        "write-chapters",
        "chapter-workspace",
      ].includes(
        textbookWorkspace
      )
    ) {
      return;
    }

    openTextbookWorkspace(
      "write-chapters"
    );

    // Contributor direct URLs must never expose Owner-only
    // setup, structure, review, proof or publication workspaces.
    // Project access is actor-bound so a Writer account switch
    // cannot inherit the previous Owner's navigation authority.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCategory,
    activeTextbookProject?.id,
    currentTextbookProjectAccess
      ?.canManageProject,
    textbookWorkspace,
  ]);


  useEffect(() => {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    const enabled =
      currentTextbookProjectAccess
        ?.chapterWorkspaceEnabled ===
      true;

    if (
      selectedCategory !==
        "textbook" ||
      textbookWorkspace !==
        "chapter-workspace" ||
      !enabled ||
      !projectId ||
      !chapterItemId
    ) {
      textbookChapterWorkspaceLoadRequest
        .current += 1;

      setTextbookChapterWorkspaceData(
        null
      );
      setTextbookChapterWorkspaceActorKey(
        ""
      );

      setTextbookChapterWorkspaceError(
        ""
      );

      setTextbookChapterWorkspaceLoading(
        false
      );

      return;
    }

    loadTextbookChapterWorkspace(
      projectId,
      chapterItemId
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCategory,
    activeTextbookProject?.id,
    textbookWorkspace,
    textbookChapterItemId,
    currentTextbookProjectAccess
      ?.chapterWorkspaceEnabled,
  ]);

  async function saveTextbookProjectOverview(
    overview,
    formatPlan,
    expectedRevision
  ) {
    const id =
      clean(
        activeTextbookProject?.id
      );

    if (!id) {
      throw new Error(
        isFR
          ? "Aucun projet de manuel actif."
          : "No active Textbook project."
      );
    }

    const payload =
      await api.saveTextbookProjectPlan(
        id,
        {
          overview,
          formatPlan,
          expectedRevision,
        }
      );

    const nextPlan =
      payload?.projectPlan || null;

    if (!nextPlan) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la vue d’ensemble enregistrée."
          : "The server did not return the saved Project Overview."
      );
    }

    if (
      clean(
        projectIdFromSearch()
      ) === id
    ) {
      setTextbookProjectPlan(
        nextPlan
      );
    }

    return nextPlan;
  }

  async function saveTextbookPublicationPlan(
    publicationPlan,
    expectedRevision
  ) {
    const id =
      clean(activeTextbookProject?.id);

    if (!id) {
      throw new Error(
        isFR
          ? "Aucun projet de manuel actif."
          : "No active Textbook project."
      );
    }

    const payload =
      await api.saveTextbookProjectPlan(
        id,
        {
          publicationPlan,
          expectedRevision,
        }
      );

    const nextPlan =
      payload?.projectPlan || null;

    if (!nextPlan) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la préparation de publication enregistrée."
          : "The server did not return the saved Publication Preparation."
      );
    }

    if (clean(projectIdFromSearch()) === id) {
      setTextbookProjectPlan(nextPlan);
    }

    return nextPlan;
  }


  async function lockTextbookPublicationManuscript(
    expectedRevision,
    reviewAssembly,
    chapterBlockSelections
  ) {
    const id =
      clean(
        activeTextbookProject?.id
      );

    if (!id) {
      throw new Error(
        isFR
          ? "Aucun projet de manuel actif."
          : "No active Textbook project."
      );
    }

    const payload =
      await api
        .lockTextbookPublicationManuscript(
          id,
          expectedRevision,
          reviewAssembly,
          chapterBlockSelections
        );

    const nextPlan =
      payload?.projectPlan ||
      null;

    if (!nextPlan) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné le manuscrit de publication verrouillé."
          : "The server did not return the locked publication manuscript."
      );
    }

    if (
      clean(
        projectIdFromSearch()
      ) === id
    ) {
      setTextbookProjectPlan(
        nextPlan
      );
    }

    return nextPlan;
  }


  async function saveTextbookBookMap(
    bookMap,
    expectedRevision
  ) {
    const id =
      clean(
        activeTextbookProject?.id
      );

    if (!id) {
      throw new Error(
        isFR
          ? "Aucun projet de manuel actif."
          : "No active Textbook project."
      );
    }

    const payload =
      await api.saveTextbookProjectPlan(
        id,
        {
          bookMap,
          expectedRevision,
        }
      );

    const nextPlan =
      payload?.projectPlan || null;

    if (!nextPlan) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné le Plan du livre enregistré."
          : "The server did not return the saved Book Map."
      );
    }

    if (
      clean(
        projectIdFromSearch()
      ) === id
    ) {
      setTextbookProjectPlan(
        nextPlan
      );
    }

    return nextPlan;
  }


  async function applyTextbookManuscriptImport(
    importRequest,
    expectedRevision
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    if (!projectId) {
      throw new Error(
        isFR
          ? "Aucun projet de manuel actif."
          : "No active Textbook project."
      );
    }

    const revision =
      Number(
        expectedRevision
      );

    if (
      !Number.isInteger(
        revision
      ) ||
      revision < 1
    ) {
      throw new Error(
        isFR
          ? "Rechargez la Structure du livre avant d’ajouter le manuscrit."
          : "Reload the Book Structure before adding the manuscript."
      );
    }

    const request =
      importRequest &&
      typeof importRequest ===
        "object"
        ? importRequest
        : {};

    const bookMapItems =
      Array.isArray(
        request
          ?.bookMap
          ?.items
      )
        ? request
            .bookMap
            .items
        : [];

    const previousBookMapItems =
      Array.isArray(
        request
          ?.previousBookMap
          ?.items
      )
        ? request
            .previousBookMap
            .items
        : [];

    const chapterDrafts =
      Array.isArray(
        request
          ?.chapterDrafts
      )
        ? request
            .chapterDrafts
        : [];

    if (
      bookMapItems.length === 0 ||
      chapterDrafts.length === 0
    ) {
      throw new Error(
        isFR
          ? "La prévisualisation du manuscrit ne contient aucun chapitre prêt à être ajouté."
          : "The manuscript preview does not contain any ready Chapters to add."
      );
    }

    const chapterIds =
      new Set(
        bookMapItems
          .filter(
            (item) =>
              clean(
                item?.type
              ) ===
                "chapter"
          )
          .map(
            (item) =>
              clean(
                item?.itemId
              )
          )
          .filter(Boolean)
      );

    const invalidDraft =
      chapterDrafts.find(
        (draft) =>
          !clean(
            draft
              ?.chapterItemId
          ) ||
          !chapterIds.has(
            clean(
              draft
                ?.chapterItemId
            )
          ) ||
          !Array.isArray(
            draft?.blocks
          )
      );

    if (invalidDraft) {
      throw new Error(
        isFR
          ? "La prévisualisation du manuscrit contient un brouillon de chapitre invalide."
          : "The manuscript preview contains an invalid Chapter Draft."
      );
    }

    let appliedPlan =
      null;

    const savedDrafts =
      [];

    try {
      const planPayload =
        await api
          .saveTextbookProjectPlan(
            projectId,
            {
              bookMap: {
                items:
                  bookMapItems,
              },

              expectedRevision:
                revision,
            }
          );

      appliedPlan =
        planPayload
          ?.projectPlan ||
        null;

      if (!appliedPlan) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné la Structure du livre enregistrée."
            : "The server did not return the saved Book Structure."
        );
      }

      for (
        const draft
        of chapterDrafts
      ) {
        const chapterItemId =
          clean(
            draft
              ?.chapterItemId
          );

        const draftPayload =
          await api
            .saveTextbookChapterDraft(
              projectId,
              chapterItemId,
              {
                blocks:
                  draft.blocks,

                expectedRevision:
                  0,
              }
            );

        const savedDraft =
          draftPayload
            ?.chapterDraft ||
          null;

        const savedRevision =
          Number(
            savedDraft
              ?.revision ||
            0
          );

        if (
          !savedDraft ||
          !Number.isInteger(
            savedRevision
          ) ||
          savedRevision < 1
        ) {
          throw new Error(
            isFR
              ? "Le serveur n’a pas confirmé l’enregistrement d’un chapitre importé."
              : "The server did not confirm one imported Chapter Draft."
          );
        }

        savedDrafts.push({
          chapterItemId,
          revision:
            savedRevision,
        });
      }

      if (
        clean(
          projectIdFromSearch()
        ) === projectId
      ) {
        setTextbookProjectPlan(
          appliedPlan
        );
      }

      return {
        projectPlan:
          appliedPlan,

        chapterDraftCount:
          savedDrafts.length,
      };
    } catch (error) {
      let rollbackError =
        null;

      if (appliedPlan) {
        for (
          const saved
          of [
            ...savedDrafts,
          ].reverse()
        ) {
          try {
            await api
              .discardTextbookChapterDraft(
                projectId,
                saved.chapterItemId,
                saved.revision
              );
          } catch (
            discardError
          ) {
            rollbackError =
              rollbackError ||
              discardError;
          }
        }

        if (!rollbackError) {
          try {
            const rollbackRevision =
              Number(
                appliedPlan
                  ?.revision ||
                0
              );

            if (
              !Number.isInteger(
                rollbackRevision
              ) ||
              rollbackRevision < 1
            ) {
              throw new Error(
                "The saved Textbook Project Plan revision is unavailable for rollback."
              );
            }

            const restoredPayload =
              await api
                .saveTextbookProjectPlan(
                  projectId,
                  {
                    bookMap: {
                      items:
                        previousBookMapItems,
                    },

                    expectedRevision:
                      rollbackRevision,
                  }
                );

            const restoredPlan =
              restoredPayload
                ?.projectPlan ||
              null;

            if (!restoredPlan) {
              throw new Error(
                "The previous Textbook Book Structure could not be restored."
              );
            }

            if (
              clean(
                projectIdFromSearch()
              ) === projectId
            ) {
              setTextbookProjectPlan(
                restoredPlan
              );
            }
          } catch (
            restoreError
          ) {
            rollbackError =
              restoreError;
          }
        }

        if (
          rollbackError &&
          clean(
            projectIdFromSearch()
          ) === projectId
        ) {
          setTextbookProjectPlan(
            appliedPlan
          );
        }
      }

      if (rollbackError) {
        const originalMessage =
          clean(
            error?.message
          ) ||
          (
            isFR
              ? "L’importation du manuscrit a été interrompue."
              : "The manuscript import was interrupted."
          );

        throw new Error(
          isFR
            ? `${originalMessage} NextScenes n’a pas pu restaurer entièrement l’état précédent. Actualisez le projet avant de continuer.`
            : `${originalMessage} NextScenes could not fully restore the previous state. Refresh the project before continuing.`
        );
      }

      throw error;
    }
  }


  async function saveTextbookChapterDraft(
    blocks,
    expectedRevision,
    sectionLayout = []
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    if (
      !projectId ||
      !chapterItemId
    ) {
      throw new Error(
        isFR
          ? "Aucun chapitre actif."
          : "No active Textbook chapter."
      );
    }

    const payload =
      await api
        .saveTextbookChapterDraft(
          projectId,
          chapterItemId,
          {
            blocks,
            sectionLayout,
            expectedRevision,
          }
        );

    if (!payload?.chapterDraft) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné le chapitre enregistré."
          : "The server did not return the saved Chapter Draft."
      );
    }

    setTextbookChapterWorkspaceData(
      (
        currentData
      ) => ({
        chapter:
          payload?.chapter ||
          currentData
            ?.chapter ||
          null,

        chapterDraft:
          payload.chapterDraft,

        proposalRound:
          currentData
            ?.proposalRound ||
          null,

        myProposals:
          Array.isArray(
            currentData
              ?.myProposals
          )
            ? currentData
                .myProposals
            : [],

        proposalReviewProposals:
          Array.isArray(
            currentData
              ?.proposalReviewProposals
          )
            ? currentData
                .proposalReviewProposals
            : [],

        acceptedProposalGuidance:
          currentData
            ?.acceptedProposalGuidance ||
          null,

        figureAssets:
          Array.isArray(
            currentData
              ?.figureAssets
          )
            ? currentData
                .figureAssets
            : [],

        figureLoadError:
          clean(
            currentData
              ?.figureLoadError
          ),

        access:
          payload?.access ||
          currentData
            ?.access ||
          null,
      })
    );

    return payload.chapterDraft;
  }

  async function openTextbookChapterProposalRound(
    request = {}
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    if (
      !projectId ||
      !chapterItemId
    ) {
      throw new Error(
        isFR
          ? "Aucun chapitre actif."
          : "No active Textbook chapter."
      );
    }

    const payload =
      await api
        .openTextbookChapterProposalRound(
          projectId,
          chapterItemId,
          request
        );

    await loadTextbookChapterWorkspace(
      projectId,
      chapterItemId
    );

    return (
      payload?.proposalRound ||
      null
    );
  }


  async function closeTextbookChapterProposalRound(
    request = {}
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    const proposalRoundId =
      clean(
        request
          ?.proposalRoundId
      );

    const expectedRevision =
      Number(
        request
          ?.expectedRevision
      );

    const closingReason =
      clean(
        request
          ?.closingReason
      );

    if (
      !projectId ||
      !chapterItemId ||
      !proposalRoundId
    ) {
      throw new Error(
        isFR
          ? "Aucun Proposal Round actif."
          : "No active Textbook Proposal Round."
      );
    }

    if (
      !Number.isInteger(
        expectedRevision
      ) ||
      expectedRevision < 1
    ) {
      throw new Error(
        isFR
          ? "Rechargez le Proposal Round avant de le clôturer."
          : "Reload the Proposal Round before closing it."
      );
    }

    if (!closingReason) {
      throw new Error(
        isFR
          ? "Indiquez pourquoi les propositions sont clôturées."
          : "Record why proposals are being closed."
      );
    }

    const payload =
      await api
        .closeTextbookChapterProposalRound(
          projectId,
          chapterItemId,
          proposalRoundId,
          {
            expectedRevision,
            closingReason,
          }
        );

    await loadTextbookChapterWorkspace(
      projectId,
      chapterItemId
    );

    return (
      payload?.proposalRound ||
      null
    );
  }



  async function decideTextbookChapterProposal(
    request = {}
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    const proposalRoundId =
      clean(
        request
          ?.proposalRoundId
      );

    const contributionId =
      clean(
        request
          ?.contributionId
      );

    const action =
      clean(
        request
          ?.action
      ).toLowerCase();

    const expectedRoundRevision =
      Number(
        request
          ?.expectedRoundRevision
      );

    const expectedChapterRevision =
      Number(
        request
          ?.expectedChapterRevision
      );

    if (
      !projectId ||
      !chapterItemId ||
      !proposalRoundId
    ) {
      throw new Error(
        isFR
          ? "Aucun Proposal Round actif."
          : "No active Textbook Proposal Round."
      );
    }

    let payload;

    if (
      action ===
        "reject_all"
    ) {
      payload =
        await api
          .rejectAllTextbookChapterProposals(
            projectId,
            chapterItemId,
            proposalRoundId,
            {
              expectedRoundRevision,
              expectedChapterRevision,
            }
          );
    } else {
      payload =
        await api
          .decideTextbookChapterProposal(
            projectId,
            chapterItemId,
            proposalRoundId,
            contributionId,
            {
              action,
              expectedRoundRevision,
              expectedChapterRevision,
            }
          );
    }

    await loadTextbookChapterWorkspace(
      projectId,
      chapterItemId
    );

    return payload;
  }


  async function submitTextbookChapterProposal(
    request = {}
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    const proposalRoundId =
      clean(
        request
          ?.proposalRoundId
      );

    const expectedChapterRevision =
      Number(
        request
          ?.expectedChapterRevision
      );

    const proposalText =
      clean(
        request
          ?.proposalText
      );

    const proposalManuscriptContent =
      request
        ?.proposalManuscriptContent;

    const submissionKey =
      clean(
        request
          ?.submissionKey
      );

    if (
      !projectId ||
      !chapterItemId ||
      !proposalRoundId
    ) {
      throw new Error(
        isFR
          ? "Aucun Proposal Round actif."
          : "No active Textbook Proposal Round."
      );
    }

    const payload =
      await api
        .submitTextbookChapterProposal(
          projectId,
          chapterItemId,
          proposalRoundId,
          {
            expectedChapterRevision,
            proposalText,
            proposalManuscriptContent,
          },
          submissionKey
        );

    await loadTextbookChapterWorkspace(
      projectId,
      chapterItemId
    );

    return (
      payload?.contribution ||
      null
    );
  }


  function rememberTextbookFigureAsset(
    figureAsset
  ) {
    const figureAssetId =
      clean(
        figureAsset?.id
      );

    if (!figureAssetId) {
      return;
    }

    setTextbookChapterWorkspaceData(
      (
        currentData
      ) => ({
        ...(currentData ||
          {}),

        figureAssets: [
          ...(
            Array.isArray(
              currentData
                ?.figureAssets
            )
              ? currentData
                  .figureAssets
              : []
          ).filter(
            (item) =>
              clean(
                item?.id
              ) !==
              figureAssetId
          ),

          figureAsset,
        ],

        figureLoadError: "",
      })
    );
  }

  async function uploadTextbookFigure(
    file,
    figureAsset
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    if (
      !projectId ||
      !chapterItemId
    ) {
      throw new Error(
        isFR
          ? "Aucun chapitre actif."
          : "No active Textbook chapter."
      );
    }

    const payload =
      await api
        .uploadTextbookFigureAsset(
          projectId,
          chapterItemId,
          file,
          figureAsset
        );

    if (!payload?.figureAsset) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la Figure ajoutée."
          : "The server did not return the added Figure."
      );
    }

    rememberTextbookFigureAsset(
      payload.figureAsset
    );

    return payload.figureAsset;
  }

  async function createTextbookFigureLink(
    figureAsset
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    if (
      !projectId ||
      !chapterItemId
    ) {
      throw new Error(
        isFR
          ? "Aucun chapitre actif."
          : "No active Textbook chapter."
      );
    }

    const payload =
      await api
        .createTextbookFigureAsset(
          projectId,
          chapterItemId,
          figureAsset
        );

    if (!payload?.figureAsset) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la Figure liée."
          : "The server did not return the linked Figure."
      );
    }

    rememberTextbookFigureAsset(
      payload.figureAsset
    );

    return payload.figureAsset;
  }


  async function updateTextbookFigure(
    figureAssetId,
    figureAsset,
    expectedRevision
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterItemId =
      clean(
        textbookChapterItemId
      );

    const figureId =
      clean(
        figureAssetId
      );

    if (
      !projectId ||
      !chapterItemId ||
      !figureId
    ) {
      throw new Error(
        isFR
          ? "Aucune Figure active à modifier."
          : "No active Textbook Figure to edit."
      );
    }

    const payload =
      await api
        .updateTextbookFigureAsset(
          projectId,
          chapterItemId,
          figureId,
          {
            expectedRevision,
            figureAsset,
          }
        );

    if (!payload?.figureAsset) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la Figure mise à jour."
          : "The server did not return the updated Figure."
      );
    }

    rememberTextbookFigureAsset(
      payload.figureAsset
    );

    return payload.figureAsset;
  }


  async function updateTextbookFigureFromMedia(
    chapterItemId,
    figureAssetId,
    figureAsset,
    expectedRevision
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterId =
      clean(
        chapterItemId
      );

    const figureId =
      clean(
        figureAssetId
      );

    if (
      !projectId ||
      !chapterId ||
      !figureId
    ) {
      throw new Error(
        isFR
          ? "Cette Figure ne peut pas être modifiée."
          : "This Textbook Figure cannot be edited."
      );
    }

    const payload =
      await api
        .updateTextbookFigureAsset(
          projectId,
          chapterId,
          figureId,
          {
            expectedRevision,
            figureAsset,
          }
        );

    if (!payload?.figureAsset) {
      throw new Error(
        isFR
          ? "Le serveur n’a pas retourné la Figure mise à jour."
          : "The server did not return the updated Figure."
      );
    }

    return payload.figureAsset;
  }


  async function createTextbookProject(
    event
  ) {
    event?.preventDefault?.();

    const title =
      clean(textbookTitle);

    if (!title) {
      setTextbookProjectsError(
        isFR
          ? "Saisissez d’abord le titre du manuel."
          : "Enter the Textbook title first."
      );
      return;
    }

    const invitationMode =
      textbookInvitationWritingMode(
        textbookWritingMode
      );

    const parsedInvites =
      parseTextbookInviteEmails(
        textbookInviteEmailsText
      );

    if (
      invitationMode &&
      parsedInvites.invalid.length > 0
    ) {
      setTextbookInviteEmailsError(
        isFR
          ? `Adresse e-mail invalide : ${parsedInvites.invalid[0]}`
          : `Invalid invitee email: ${parsedInvites.invalid[0]}`
      );
      return;
    }

    if (
      invitationMode &&
      parsedInvites.emails.length === 0
    ) {
      setTextbookInviteEmailsError(
        isFR
          ? "Ajoutez au moins une personne approuvée par le propriétaire."
          : "Add at least one Group invitee email."
      );
      return;
    }

    try {
      setTextbookCreating(true);
      setTextbookProjectsError("");
      setTextbookInviteEmailsError("");

      const payload =
        await api.createTextbookProject({
          title,

          primaryLanguage:
            textbookPrimaryLanguage,

          writingMode:
            textbookWritingMode,

          inviteEmails:
            invitationMode
              ? parsedInvites.emails
              : [],
        });

      const project =
        payload?.project || null;

      const plan =
        payload?.projectPlan || null;

      const id =
        clean(project?.id);

      if (!project || !id) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le projet créé."
            : "The server did not return the created Textbook project."
        );
      }

      setTextbookProjects(
        (current) => [
          project,
          ...current.filter(
            (item) =>
              clean(item?.id) !== id
          ),
        ]
      );

      setTextbookTitle("");
      setTextbookPrimaryLanguage(
        isFR ? "fr" : "en"
      );
      setTextbookWritingMode("solo");
      setTextbookInviteEmailsText("");
      setTextbookInviteEmailsError("");

      setActiveTextbookProject(
        project
      );

      setTextbookWorkspace(
        "project-plan"
      );

      setTextbookProjectAccess({
        relationship: "owner",
        membershipRole: "owner",
        canEditProjectPlan: true,
        canManageProject: true,
        bookMapEnabled: true,
        chapterWorkspaceEnabled:
          true,
      });
      setTextbookProjectAccessActorKey(
        textbookActorKey
      );

      setTextbookProjectPlan(plan);
      setTextbookProjectPlanError("");

      replaceProjectHubPath(
        TEXTBOOK_BASE_PATH,
        {
          projectId: id,
        }
      );

      if (
        typeof onContextChange ===
        "function"
      ) {
        onContextChange({
          category: "textbook",
          projectId: id,
          projectTitle:
            clean(project?.title),
        });
      }

      if (
        typeof window !==
          "undefined"
      ) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      }
    } catch (err) {
      setTextbookProjectsError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible de créer ce projet de manuel."
              : "Could not create this Textbook project."
          )
      );
    } finally {
      setTextbookCreating(false);
    }
  }

  function openTextbookChapterWorkspace(
    chapterItemId
  ) {
    const projectId =
      clean(
        activeTextbookProject
          ?.id
      );

    const chapterId =
      clean(
        chapterItemId
      );

    if (
      !projectId ||
      !chapterId ||
      currentTextbookProjectAccess
        ?.chapterWorkspaceEnabled !==
        true
    ) {
      return;
    }

    textbookChapterWorkspaceLoadRequest
      .current += 1;

    setTextbookChapterWorkspaceData(
      null
    );
    setTextbookChapterWorkspaceActorKey(
      ""
    );

    setTextbookChapterWorkspaceError(
      ""
    );

    setTextbookChapterWorkspaceLoading(
      false
    );

    setTextbookChapterItemId(
      chapterId
    );

    setTextbookWorkspace(
      "chapter-workspace"
    );

    replaceProjectWorkspacePath(
      TEXTBOOK_BASE_PATH,
      "chapter-workspace",
      {
        foundationWorkspace:
          "project-plan",
        projectId,
        search:
          textbookSearchWithChapterItem(
            chapterId
          ),
      }
    );
  }


  function openTextbookWorkspace(
    workspace
  ) {
    const requestedWorkspace =
      [
        "overview",
        "project-plan",
        "book-map",
        "write-chapters",
        "figures-media",
        "review-your-book",
        "publication-proof",
        "publication-preparation",
      ].includes(workspace)
        ? workspace
        : "overview";

    const bookMapEnabled =
      currentTextbookProjectAccess?.bookMapEnabled === true;
    const chapterWorkspaceEnabled =
      currentTextbookProjectAccess?.chapterWorkspaceEnabled === true;

    let nextWorkspace = requestedWorkspace;

    const contributorView =
      currentTextbookProjectAccess
        ?.canManageProject !==
      true;

    if (
      contributorView &&
      ![
        "write-chapters",
      ].includes(
        requestedWorkspace
      )
    ) {
      nextWorkspace =
        "write-chapters";
    }

    if (
      !contributorView &&
      requestedWorkspace === "book-map" &&
      !bookMapEnabled
    ) {
      nextWorkspace = "project-plan";
    }
    if (
      !contributorView &&
      requestedWorkspace === "write-chapters" &&
      !chapterWorkspaceEnabled
    ) {
      nextWorkspace =
        bookMapEnabled
          ? "book-map"
          : "project-plan";
    }

    if (
      !contributorView &&
      requestedWorkspace === "figures-media" &&
      !chapterWorkspaceEnabled
    ) {
      nextWorkspace =
        bookMapEnabled
          ? "book-map"
          : "project-plan";
    }

    if (
      !contributorView &&
      requestedWorkspace === "review-your-book" &&
      !chapterWorkspaceEnabled
    ) {
      nextWorkspace =
        bookMapEnabled
          ? "book-map"
          : "project-plan";
    }

    if (
      !contributorView &&
      requestedWorkspace === "publication-proof" &&
      !chapterWorkspaceEnabled
    ) {
      nextWorkspace =
        bookMapEnabled
          ? "book-map"
          : "project-plan";
    }

    if (
      !contributorView &&
      requestedWorkspace === "publication-preparation" &&
      !chapterWorkspaceEnabled
    ) {
      nextWorkspace =
        bookMapEnabled
          ? "book-map"
          : "project-plan";
    }

    const id =
      clean(
        activeTextbookProject?.id
      );

    textbookChapterWorkspaceLoadRequest
      .current += 1;

    setTextbookChapterItemId(
      ""
    );

    setTextbookChapterWorkspaceData(
      null
    );
    setTextbookChapterWorkspaceActorKey(
      ""
    );

    setTextbookChapterWorkspaceError(
      ""
    );

    setTextbookChapterWorkspaceLoading(
      false
    );

    setTextbookWorkspace(
      nextWorkspace
    );

    replaceProjectWorkspacePath(
      TEXTBOOK_BASE_PATH,
      nextWorkspace,
      {
        foundationWorkspace:
          "project-plan",
        projectId: id,
        search:
          textbookSearchWithChapterItem(
            ""
          ),
      }
    );

  }

  function openTextbookProject(
    project
  ) {
    const id =
      clean(project?.id);

    if (
      id &&
      typeof window !==
        "undefined"
    ) {
      try {
        window.localStorage
          .setItem(
            "nextscenes.textbook.activeProjectId",
            id
          );
      } catch {
        // Active-card highlighting is presentation only.
      }
    }

    if (!id) return;

    textbookDirectOpenRequest
      .current = id;

    setTextbookDirectOpenLoading(
      true
    );

    setTextbookProjectAccess(null);
    setTextbookProjectAccessActorKey("");
    setTextbookProjectPlan(null);
    setTextbookProjectPlanError("");
    setActiveTextbookProject(
      project
    );

    setTextbookChapterItemId(
      ""
    );

    setTextbookChapterWorkspaceData(
      null
    );
    setTextbookChapterWorkspaceActorKey(
      ""
    );

    setTextbookChapterWorkspaceError(
      ""
    );

    setTextbookWorkspace(
      "overview"
    );

    replaceProjectHubPath(
      TEXTBOOK_BASE_PATH,
      {
        projectId: id,
        search:
          textbookSearchWithChapterItem(
            ""
          ),
      }
    );

    if (
      typeof onContextChange ===
      "function"
    ) {
      onContextChange({
        category: "textbook",
        projectId: id,
        projectTitle:
          clean(project?.title),
      });
    }

    if (
      typeof window !==
        "undefined"
    ) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }


  useEffect(() => {
    if (
      selectedCategory === "textbook" &&
      currentPathname() === TEXTBOOK_BASE_PATH &&
      typeof window !== "undefined"
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [selectedCategory]);


  function closeTextbookProject() {
    textbookDirectOpenRequest
      .current = "";

    setTextbookDirectOpenLoading(
      false
    );

    textbookChapterLeaveGuard
      .current = null;

    textbookProjectPlanLoadRequest
      .current += 1;

    setActiveTextbookProject(null);
    setTextbookProjectAccess(null);
    setTextbookProjectAccessActorKey("");
    setTextbookWorkspace(
      "project-plan"
    );
    setTextbookProjectPlan(null);
    setTextbookProjectPlanError("");
    setTextbookProjectPlanLoading(
      false
    );

    textbookChapterWorkspaceLoadRequest
      .current += 1;

    setTextbookChapterItemId(
      ""
    );

    setTextbookChapterWorkspaceData(
      null
    );
    setTextbookChapterWorkspaceActorKey(
      ""
    );

    setTextbookChapterWorkspaceError(
      ""
    );

    setTextbookChapterWorkspaceLoading(
      false
    );

    replaceProjectHubPath(
      TEXTBOOK_BASE_PATH,
      {
        clearProjectId: true,
        search:
          textbookSearchWithChapterItem(
            ""
          ),
      }
    );

    if (
      typeof onContextChange ===
      "function"
    ) {
      onContextChange({
        category: "textbook",
        projectId: "",
        projectTitle: "",
      });
    }
  }


  async function loadChildrensBookProjects() {
    if (
      !childrensBookReadAccess ||
      !isAuthenticated ||
      !canCreateProjects
    ) {
      setChildrensBookProjects([]);
      setArchivedChildrensBookProjects([]);
      setChildrensBookProjectsError("");
      setChildrensBookProjectsLoading(false);
      return;
    }

    try {
      setChildrensBookProjectsLoading(true);
      setChildrensBookProjectsError("");

      const [
        activePayload,
        archivedPayload,
      ] = await Promise.all([
        api.getChildrensBookProjects(),
        childrensBookPersistencePilot
          ? api.getChildrensBookProjects({
              status: "archived",
            })
          : Promise.resolve({
              projects: [],
            }),
      ]);

      setChildrensBookProjects(
        Array.isArray(
          activePayload?.projects
        )
          ? activePayload.projects
          : []
      );

      setArchivedChildrensBookProjects(
        Array.isArray(
          archivedPayload?.projects
        )
          ? archivedPayload.projects
          : []
      );
    } catch (err) {
      setChildrensBookProjects([]);
      setArchivedChildrensBookProjects([]);
      setChildrensBookProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de charger vos projets de livres pour enfants."
            : "Could not load your Children’s Book projects."
        )
      );
    } finally {
      setChildrensBookProjectsLoading(false);
    }
  }

  useEffect(() => {
    if (
      selectedCategory === "children"
    ) {
      loadChildrensBookProjects();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCategory,
    childrensBookReadAccess,
    isAuthenticated,
    canCreateProjects,
  ]);

  useEffect(() => {
    const routeProjectId =
      currentChildrensBookRouteProjectId();

    const activeProjectId =
      clean(
        activeChildrensBookProject
          ?.project
          ?.id
      );

    if (
      !showChildrensBookCreationFlow ||
      !routeProjectId ||
      activeProjectId ===
        routeProjectId ||
      !childrensBookReadAccess ||
      !isAuthenticated ||
      !canCreateProjects
    ) {
      return;
    }

    let cancelled =
      false;

    async function restoreProjectFromRoute() {
      try {
        setOpeningChildrensBookProjectId(
          routeProjectId
        );

        setChildrensBookProjectsError(
          ""
        );

        const payload =
          await api
            .getChildrensBookProject(
              routeProjectId
            );

        if (cancelled) {
          return;
        }

        setActiveChildrensBookProject(
          payload
        );

        await loadChildrensBookAssets(
          routeProjectId
        );
      } catch (error) {
        if (!cancelled) {
          setActiveChildrensBookProject(
            null
          );

          setChildrensBookProjectsError(
            clean(error?.message) ||
            (
              isFR
                ? "Impossible de restaurer ce projet depuis l’adresse de la page."
                : "Could not restore this project from the page address."
            )
          );
        }
      } finally {
        if (!cancelled) {
          setOpeningChildrensBookProjectId(
            ""
          );
        }
      }
    }

    restoreProjectFromRoute();

    return () => {
      cancelled = true;
    };

    // loadChildrensBookAssets is a stable component function
    // for this local route restoration pilot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showChildrensBookCreationFlow,
    childrensBookReadAccess,
    isAuthenticated,
    canCreateProjects,
  ]);

  async function loadChildrensBookAssets(
    requestedProjectId
  ) {
    const id =
      clean(requestedProjectId);

    if (
      !id ||
      !childrensBookReadAccess ||
      !isAuthenticated ||
      !canCreateProjects
    ) {
      setChildrensBookAssets([]);
      setChildrensBookAssetsError("");
      setChildrensBookAssetsLoading(false);
      return [];
    }

    try {
      setChildrensBookAssetsLoading(true);
      setChildrensBookAssetsError("");

      const payload =
        await api.getChildrensBookAssets(
          id,
          {
            status: "active",
          }
        );

      const assets =
        normalizeChildrensBookAssets(
          payload
        );

      setChildrensBookAssets(
        assets
      );

      return assets;
    } catch (err) {
      setChildrensBookAssets([]);

      setChildrensBookAssetsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de charger les images de ce projet."
            : "Could not load this project’s images."
        )
      );

      return [];
    } finally {
      setChildrensBookAssetsLoading(false);
    }
  }

  const loadChildrensBookAssetFile =
    useCallback(
      async (fileUrl) =>
        await api
          .getChildrensBookAssetFile(
            fileUrl
          ),
      []
    );

  async function uploadChildrensBookCover(
    file,
    purpose = "front_cover"
  ) {
    const coverPurpose =
      clean(purpose).toLowerCase() ||
      "front_cover";

    if (
      coverPurpose !==
        "front_cover" &&
      coverPurpose !==
        "back_cover" &&
      coverPurpose !==
        "author_photo"
    ) {
      throw new Error(
        isFR
          ? "Le type d’image de couverture n’est pas pris en charge."
          : "This cover image purpose is not supported."
      );
    }
    const projectId =
      clean(
        activeChildrensBookProject
          ?.project
          ?.id
      );

    if (!projectId) {
      throw new Error(
        isFR
          ? "Enregistrez d’abord le projet avant de téléverser une couverture."
          : "Save the project before uploading a cover."
      );
    }

    if (!file) {
      throw new Error(
        isFR
          ? "Choisissez d’abord une image de couverture."
          : "Choose a cover image first."
      );
    }

    try {
      setChildrensBookAssetActionId(
        "upload-cover"
      );

      setChildrensBookAssetsError("");

      const uploaded =
        await api.uploadChildrensBookCover(
          projectId,
          file
        );

      const upload =
        uploaded?.upload || null;

      if (
        !upload?.publicUrl ||
        !upload?.storagePath
      ) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné les informations de la couverture."
            : "The server did not return the uploaded cover information."
        );
      }

      const metadata = {
        assetType:
          "cover",

        purpose:
          coverPurpose,

        bookMapUnitId:
          "",

        source:
          upload.source ||
          "upload",

        fileName:
          upload.fileName ||
          file.name ||
          "",

        mimeType:
          upload.mimeType ||
          file.type ||
          "",

        sizeBytes:
          Number(
            upload.sizeBytes ||
            file.size ||
            0
          ),

        storageProvider:
          upload.storageProvider ||
          "local",

        storageBucket:
          upload.storageBucket ||
          "",

        storagePath:
          upload.storagePath,

        publicUrl:
          upload.publicUrl,
      };

      const activeCover =
        childrensBookAssets.find(
          (asset) =>
            asset?.assetType ===
              "cover" &&
            asset?.purpose ===
              coverPurpose &&
            asset?.status ===
              "active"
        ) || null;

      const result =
        activeCover
          ? await replaceChildrensBookAsset(
              activeCover.id,
              activeCover.revision,
              metadata
            )
          : await registerChildrensBookAsset(
              metadata
            );

      return {
        ...result,
        upload,
      };
    } catch (error) {
      setChildrensBookAssetsError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible de téléverser cette couverture."
            : "Could not upload this cover."
        )
      );

      throw error;
    } finally {
      setChildrensBookAssetActionId("");
    }
  }


  async function uploadChildrensBookIllustration(
    bookMapUnitId,
    file
  ) {
    const projectId =
      clean(
        activeChildrensBookProject
          ?.project
          ?.id
      );

    const unitId =
      clean(bookMapUnitId);

    if (!projectId) {
      throw new Error(
        isFR
          ? "Enregistrez d’abord le projet avant de téléverser une illustration."
          : "Save the project before uploading an illustration."
      );
    }

    if (!unitId) {
      throw new Error(
        isFR
          ? "Choisissez une page ou double page du Plan du livre."
          : "Choose a Book Map page or spread."
      );
    }

    if (!file) {
      throw new Error(
        isFR
          ? "Choisissez d’abord une image d’illustration."
          : "Choose an illustration image first."
      );
    }

    try {
      setChildrensBookAssetActionId(
        `upload-illustration-${unitId}`
      );

      setChildrensBookAssetsError("");

      const uploaded =
        await api
          .uploadChildrensBookIllustration(
            projectId,
            unitId,
            file
          );

      const upload =
        uploaded?.upload || null;

      if (
        !upload?.publicUrl ||
        !upload?.storagePath
      ) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné les informations de l’illustration."
            : "The server did not return the uploaded illustration information."
        );
      }

      if (
        clean(
          upload.bookMapUnitId
        ) !== unitId
      ) {
        throw new Error(
          isFR
            ? "L’illustration téléversée n’est pas liée à l’unité attendue du Plan du livre."
            : "The uploaded illustration is not linked to the expected Book Map unit."
        );
      }

      const metadata = {
        assetType:
          "illustration",

        purpose:
          "book_map_illustration",

        bookMapUnitId:
          unitId,

        source:
          upload.source ||
          "upload",

        fileName:
          upload.fileName ||
          file.name ||
          "",

        mimeType:
          upload.mimeType ||
          file.type ||
          "",

        sizeBytes:
          Number(
            upload.sizeBytes ||
            file.size ||
            0
          ),

        storageProvider:
          upload.storageProvider ||
          "local",

        storageBucket:
          upload.storageBucket ||
          "",

        storagePath:
          upload.storagePath,

        publicUrl:
          upload.publicUrl,
      };

      const activeIllustration =
        childrensBookAssets.find(
          (asset) =>
            asset?.assetType ===
              "illustration" &&
            asset?.purpose ===
              "book_map_illustration" &&
            clean(
              asset?.bookMapUnitId
            ) === unitId &&
            asset?.status ===
              "active"
        ) || null;

      const result =
        activeIllustration
          ? await replaceChildrensBookAsset(
              activeIllustration.id,
              activeIllustration.revision,
              metadata
            )
          : await registerChildrensBookAsset(
              metadata
            );

      return {
        ...result,
        upload,
      };
    } catch (error) {
      setChildrensBookAssetsError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible de téléverser cette illustration."
            : "Could not upload this illustration."
        )
      );

      throw error;
    } finally {
      setChildrensBookAssetActionId("");
    }
  }

  async function registerChildrensBookAsset(
    asset
  ) {
    const id =
      clean(
        activeChildrensBookProject
          ?.project
          ?.id
      );

    if (!id) {
      throw new Error(
        isFR
          ? "Enregistrez d’abord le projet avant d’ajouter une image."
          : "Save the project before adding an image."
      );
    }

    try {
      setChildrensBookAssetActionId(
        "register"
      );

      setChildrensBookAssetsError("");

      const result =
        await api
          .registerChildrensBookAsset(
            id,
            asset || {}
          );

      if (result?.asset) {
        setChildrensBookAssets(
          (current) =>
            normalizeChildrensBookAssets([
              ...current,
              result.asset,
            ])
        );
      }

      return result;
    } catch (err) {
      setChildrensBookAssetsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer cette image."
            : "Could not register this image."
        )
      );

      throw err;
    } finally {
      setChildrensBookAssetActionId("");
    }
  }

  async function replaceChildrensBookAsset(
    assetId,
    expectedRevision,
    asset
  ) {
    const id =
      clean(
        activeChildrensBookProject
          ?.project
          ?.id
      );

    const identity =
      clean(assetId);

    if (!id) {
      throw new Error(
        isFR
          ? "Le projet doit d’abord être enregistré."
          : "The project must be saved first."
      );
    }

    if (!identity) {
      throw new Error(
        isFR
          ? "L’identifiant de l’image est requis."
          : "The image identifier is required."
      );
    }

    try {
      setChildrensBookAssetActionId(
        identity
      );

      setChildrensBookAssetsError("");

      const result =
        await api
          .replaceChildrensBookAsset(
            id,
            identity,
            {
              expectedRevision,
              asset:
                asset || {},
            }
          );

      if (result?.asset) {
        setChildrensBookAssets(
          (current) =>
            normalizeChildrensBookAssets([
              ...current.filter(
                (item) =>
                  item.id !==
                  identity
              ),
              result.asset,
            ])
        );
      }

      return result;
    } catch (err) {
      setChildrensBookAssetsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de remplacer cette image."
            : "Could not replace this image."
        )
      );

      throw err;
    } finally {
      setChildrensBookAssetActionId("");
    }
  }

  async function removeChildrensBookAsset(
    assetId,
    expectedRevision
  ) {
    const id =
      clean(
        activeChildrensBookProject
          ?.project
          ?.id
      );

    const identity =
      clean(assetId);

    if (!id) {
      throw new Error(
        isFR
          ? "Le projet doit d’abord être enregistré."
          : "The project must be saved first."
      );
    }

    if (!identity) {
      throw new Error(
        isFR
          ? "L’identifiant de l’image est requis."
          : "The image identifier is required."
      );
    }

    try {
      setChildrensBookAssetActionId(
        identity
      );

      setChildrensBookAssetsError("");

      const result =
        await api
          .removeChildrensBookAsset(
            id,
            identity,
            expectedRevision
          );

      setChildrensBookAssets(
        (current) =>
          current.filter(
            (item) =>
              item.id !== identity
          )
      );

      await loadChildrensBookAssets(
        id
      );

      return result;
    } catch (err) {
      setChildrensBookAssetsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de retirer cette image du projet."
            : "Could not remove this image from the project."
        )
      );

      throw err;
    } finally {
      setChildrensBookAssetActionId("");
    }
  }

  function clearChildrensBookAssetState() {
    setChildrensBookAssets([]);
    setChildrensBookAssetsLoading(false);
    setChildrensBookAssetsError("");
    setChildrensBookAssetActionId("");
  }

  function beginNewChildrensBookProject() {
    setActiveChildrensBookProject(null);
    clearChildrensBookAssetState();
    setShowChildrensBookCreationFlow(true);

    replaceProjectHubPath(
      `${CHILDRENS_BOOK_BASE_PATH}/project-plan`,
      {
        clearProjectId: true,
      }
    );
  }

  function openPublishedChildrensBook(
    project
  ) {
    const id =
      projectId(project);

    if (!id) return;

    window.location.href =
      `/reader/childrens-book/${encodeURIComponent(
        id
      )}`;
  }

  async function openChildrensBookProject(
    project, intent = "book-workspace"
  ) {
    const id =
      clean(project?.id);

    if (!id) return;

    try {
      setOpeningChildrensBookProjectId(id);
      setChildrensBookProjectsError("");

      const payload =
        await api.getChildrensBookProject(
          id
        );

      try {
        const editionView = process.env.REACT_APP_CHILDRENS_BOOK_APPROVED_EDITION_V1_ENABLED === "true"
          ? await api.getChildrensBookPublishedEdition(id) : null;
        if (editionView?.hasApprovedEdition === true && editionView?.reader) {
          payload.blueprint = {
            ...(payload.blueprint || {}),
            publicationReadiness: {
              ...(payload.blueprint?.publicationReadiness || {}),
              status: "published_nextscenes",
              publishedAt: editionView.reader.publication?.publishedAt || "",
            },
          };
        }
      } catch (_error) { /* Existing drafts and legacy books remain accessible. */ }
      setActiveChildrensBookProject(
        payload
      );

      await loadChildrensBookAssets(
        id
      );

      setShowChildrensBookCreationFlow(
        true
      );

      replaceProjectHubPath(
        `${CHILDRENS_BOOK_BASE_PATH}/${intent === "book-workspace" &&
          process.env.REACT_APP_CHILDRENS_BOOK_NOVEL_REUSE_V1E4D_ENABLED === "true"
          ? "manuscript-plan" : "blueprint"}`,
        {
          projectId: id,
        }
      );
    } catch (err) {
      setChildrensBookProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’ouvrir ce projet."
            : "Could not open this project."
        )
      );
    } finally {
      setOpeningChildrensBookProjectId("");
    }
  }

  async function saveChildrensBookProject(
    draft,
    {
      projectId: currentProjectId,
      expectedRevision,
    } = {}
  ) {
    const id =
      clean(currentProjectId);

    if (!id) {
      const created =
        await api.createChildrensBookProject(
          draft
        );

      const createdProjectId =
        clean(
          created?.project?.id ||
          created?.projectId
        );

      if (createdProjectId) {
        // V1E4T: after the first successful save, enter the real Book Workspace.
        // No unsaved book is given a fabricated project ID.
        replaceProjectHubPath(
          childrensBookPersistencePilot &&
          process.env.REACT_APP_CHILDRENS_BOOK_NOVEL_REUSE_V1E4D_ENABLED === "true"
            ? `${CHILDRENS_BOOK_BASE_PATH}/manuscript-plan`
            : (currentPathname() || `${CHILDRENS_BOOK_BASE_PATH}/project-plan`),
          {
            projectId:
              createdProjectId,
          }
        );
      }

      // Set route before the saved-project key remounts CreationFlow.
      setActiveChildrensBookProject(created);
      clearChildrensBookAssetState();

      await loadChildrensBookProjects();

      return created;
    }

    const savedBlueprint = childrensBookBlueprintFromDraft(draft);
    // Published status is server authority, not a writer-draft save field.
    if (process.env.REACT_APP_CHILDRENS_BOOK_APPROVED_EDITION_V1_ENABLED === "true" &&
        savedBlueprint.publicationReadiness?.status === "published_nextscenes") {
      delete savedBlueprint.publicationReadiness;
    }
    const saved = await api.saveChildrensBookBlueprint(id, {
      expectedRevision, blueprint: savedBlueprint,
    });

    const next = {
      project:
        activeChildrensBookProject
          ?.project || {
            id,
            title:
              draft?.identity?.title ||
              "",
          },

      blueprint:
        saved?.blueprint || null,
    };

    setActiveChildrensBookProject(next);

    await loadChildrensBookProjects();

    return next;
  }

  async function updateChildrensBookVisibility(
    projectId,
    visibility
  ) {
    const result =
      await api.updateChildrensBookVisibility(
        projectId,
        visibility
      );

    const savedVisibility =
      clean(
        result?.work?.visibility
      );

    if (savedVisibility) {
      setActiveChildrensBookProject(
        (current) => ({
          ...(current || {}),

          project: {
            ...(current?.project || {}),
            visibility:
              savedVisibility,
          },
        })
      );

      await loadChildrensBookProjects();
    }

    return result;
  }

  function requestArchiveChildrensBookProject(
    project
  ) {
    const id =
      clean(project?.id);

    if (!id) return;

    setChildrensBookProjectsError("");
    setPendingChildrensBookArchiveProject(
      project
    );
  }

  function cancelArchiveChildrensBookProject() {
    if (childrensBookProjectActionId) {
      return;
    }

    setPendingChildrensBookArchiveProject(
      null
    );
  }

  async function confirmArchiveChildrensBookProject() {
    const project =
      pendingChildrensBookArchiveProject;

    const id =
      clean(project?.id);

    if (!id) return;

    try {
      setChildrensBookProjectActionId(id);
      setChildrensBookProjectsError("");

      await api.archiveChildrensBookProject(
        id
      );

      setPendingChildrensBookArchiveProject(
        null
      );

      await loadChildrensBookProjects();
    } catch (err) {
      setChildrensBookProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’archiver ce projet."
            : "Could not archive this project."
        )
      );
    } finally {
      setChildrensBookProjectActionId("");
    }
  }

  async function restoreChildrensBookProject(
    project
  ) {
    const id =
      clean(project?.id);

    if (!id) return;

    try {
      setChildrensBookProjectActionId(id);
      setChildrensBookProjectsError("");

      await api.restoreChildrensBookProject(
        id
      );

      await loadChildrensBookProjects();
    } catch (err) {
      setChildrensBookProjectsError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de restaurer ce projet."
            : "Could not restore this project."
        )
      );
    } finally {
      setChildrensBookProjectActionId("");
    }
  }

  function closeChildrensBookWorkspace() {
    setShowChildrensBookCreationFlow(false);
    setActiveChildrensBookProject(null);
    clearChildrensBookAssetState();

    replaceProjectHubPath(
      CHILDRENS_BOOK_BASE_PATH,
      {
        clearProjectId: true,
      }
    );

    loadChildrensBookProjects();
  }

  function performProjectBreadcrumbNavigation(
    page,
    options = {}
  ) {
    if (page !== "projectHub") {
      onNavigatePage?.(
        page,
        options
      );

      return;
    }

    const requestedCategory =
      clean(
        options?.projectCategory
      );

    setActiveChildrensBookProject(
      null
    );

    clearChildrensBookAssetState();

    setOpeningChildrensBookProjectId(
      ""
    );

    setShowChildrensBookCreationFlow(
      false
    );

    setSelectedCategory(
      requestedCategory
    );

    setActiveTextbookProject(
      null
    );

    setActiveScreenplayProject(
      null
    );

    if (
      requestedCategory ===
      "children"
    ) {
      replaceProjectHubPath(
        CHILDRENS_BOOK_BASE_PATH,
        {
          clearProjectId: true,
        }
      );
    } else if (
      requestedCategory ===
      "textbook"
    ) {
      replaceProjectHubPath(
        TEXTBOOK_BASE_PATH,
        {
          clearProjectId: true,
        }
      );
    } else if (
      requestedCategory ===
      "screenplay"
    ) {
      replaceProjectHubPath(
        SCREENPLAY_BASE_PATH,
        {
          clearProjectId: true,
        }
      );
    } else {
      replaceProjectHubPath(
        "/my-projects"
      );
    }

    if (
      typeof onContextChange ===
      "function"
    ) {
      onContextChange({
        category:
          requestedCategory,

        projectId: "",
        projectTitle: "",
      });
    }

    if (
      typeof window !==
        "undefined"
    ) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }

  function navigateProjectBreadcrumb(
    page,
    options = {}
  ) {
    if (
      selectedCategory ===
        "textbook" &&
      textbookWorkspace ===
        "chapter-workspace" &&
      typeof textbookChapterLeaveGuard
        .current === "function"
    ) {
      textbookChapterLeaveGuard
        .current(() =>
          performProjectBreadcrumbNavigation(
            page,
            options
          )
        );
      return;
    }

    performProjectBreadcrumbNavigation(
      page,
      options
    );
  }

  function selectProjectCategory(category) {
    const nextCategory =
      clean(category);

    setSelectedCategory(nextCategory);
    setActiveNovelStoryDraftProject(null);
    setNativeChildrensOpenV1F1(null);
    setShowChildrensBookCreationFlow(false);
    clearChildrensBookAssetState();
    setActiveTextbookProject(null);
    setTextbookProjectsError("");
    setActiveScreenplayProject(null);
    setScreenplayProjectsError("");

    if (nextCategory === "children") {
      replaceProjectHubPath(
        CHILDRENS_BOOK_BASE_PATH,
        {
          clearProjectId: true,
        }
      );
    } else if (
      nextCategory === "textbook"
    ) {
      replaceProjectHubPath(
        TEXTBOOK_BASE_PATH,
        {
          clearProjectId: true,
        }
      );
    } else if (
      nextCategory === "screenplay"
    ) {
      replaceProjectHubPath(
        SCREENPLAY_BASE_PATH,
        {
          clearProjectId: true,
        }
      );
    } else {
      replaceProjectHubPath(
        "/my-projects"
      );
    }

    if (typeof onContextChange === "function") {
      onContextChange({
        category: nextCategory,
        projectId: "",
        projectTitle: "",
      });
    }
  }

  // Navigation is a read-only lookup. Book Structure owns chapter creation
  // and scene assignments; Continue Writing must never change saved books.
  async function resolveNovelWritingTarget(
    project
  ) {
    const id = projectId(project);
    if (!id) return null;

    const story = await api.getStoryById(id);
    return selectNovelContinueWritingTargetV1(story);
  }

  async function continueNovelStory(
    project
  ) {
    const id = projectId(project);
    if (!id) return;

    try {
      const target =
        await resolveNovelWritingTarget(
          project
        );

      if (!target) {
        openNovelStoryBookDraft(
          project
        );
        return;
      }

      navigate(
        "writerStudio",
        project,
        target
      );
    } catch (err) {
      console.warn(
        "ProjectHub: could not resolve Novel writing target:",
        err?.message || err
      );

      openNovelStoryBookDraft(
        project
      );
    }
  }

  function openNovelStoryBookDraft(project) {
    const id = projectId(project);
    if (!id) return;

    setActiveNovelStoryDraftProject(project);

    if (typeof onContextChange === "function") {
      onContextChange({
        category: "story",
        projectId: id,
        projectTitle: clean(project?.title),
      });
    }

    if (
      typeof window !== "undefined" &&
      project?.isStructurePreview !== true
    ) {
      window.history.replaceState(
        {},
        "",
        `/my-projects?storyId=${encodeURIComponent(id)}`
      );
    }

    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }

  function closeNovelStoryBookDraft() {
    setActiveNovelStoryDraftProject(null);

    if (typeof onContextChange === "function") {
      onContextChange({
        category: "story",
        projectId: "",
        projectTitle: "",
      });
    }

    if (typeof window !== "undefined") {
      window.history.replaceState(
        {},
        "",
        "/my-projects"
      );

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }
  }

  function navigate(page, project, extra = {}) {
    const id = projectId(project);
    if (!id) return;

    if (typeof onContextChange === "function") {
      onContextChange({
        category: nativeChildrensNovelPilotV1F1 && project?.bookCategory === "children_book" ? "children" : "story",
        projectId: id,
        projectTitle: clean(project?.title),
      });
    }

    onNavigatePage?.(
      page,
      {
        storyId: id,
        ...(extra || {}),
      }
    );
  }

  function renderCategoryCard(category) {
    // Continue Writing in Novel/Story must never select a native Children's
    // Book. When the native rollout is off, preserve the previous shortcut.
    const novelContinueProject = nativeChildrensNovelPilotV1F1
      ? projects.find((item) => item?.bookCategory !== "children_book")
      : projects[0];
    // The native Children’s Book card reuses the existing Book Workspace.
    // Never open a Novel or a legacy Blueprint through this shortcut.
    const childrensContinueProject =
      nativeChildrensNovelPilotV1F1 && category.id === "children"
        ? projects.find((item) => item?.bookCategory === "children_book")
        : null;
    const canResumeCategory =
      (category.id === "story" && Boolean(novelContinueProject)) ||
      (category.id === "children" && Boolean(childrensContinueProject));
    const style =
      CATEGORY_STYLE[category.id] ||
      CATEGORY_STYLE.story;

    return (
      <article
        key={category.id}
        className={
          "ns-card ns-project-type-card " +
          (category.available
            ? "is-available"
            : "is-planned")
        }
        style={{
          "--project-accent": style.accent,
          "--project-accent-soft": style.accentSoft,
          "--project-border": style.border,
          padding: 22,
          borderRadius: 22,
          minHeight: 276,
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          background: style.background,
          border: `2px solid ${style.border}`,
          boxShadow: category.available
            ? "0 16px 38px rgba(37,99,235,0.14), 0 3px 9px rgba(15,23,42,0.08)"
            : "0 12px 28px rgba(15,23,42,0.09), 0 2px 7px rgba(15,23,42,0.05)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            background: style.accent,
          }}
        />
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            className="ns-project-type-icon"
            style={{
              width: 58,
              height: 58,
              borderRadius: 17,
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.92)",
              color: style.accent,
              border: `1px solid ${style.border}`,
              boxShadow:
                "0 9px 22px rgba(15,23,42,0.10)",
            }}
          >
            <ProjectTypeIcon type={category.id} />
          </div>

          <span
            style={{
              padding: "6px 9px",
              borderRadius: 999,
              background: category.available
                ? "rgba(22,163,74,0.12)"
                : "rgba(100,116,139,0.12)",
              color: category.available
                ? "#166534"
                : "#475569",
              fontSize: 11,
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            {category.available
              ? ui.active
              : ui.planned}
          </span>
        </div>

        <h3
          className="ns-card-title"
          style={{
            margin: "18px 0 8px",
            fontSize: 22,
            lineHeight: 1.18,
            color: "#172033",
          }}
        >
          {category.title}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          {category.description}
        </p>

        <div
          style={{
            marginTop: "auto",
            paddingTop: 18,
          }}
        >
          {category.available ? (
            canResumeCategory ? (
              <div
                style={{
                  display: "grid",
                  gap: 8,
                }}
              >
                <button
                  className="ns-button ns-button-primary"
                  type="button"
                  onClick={() => {
                    if (category.id === "children") {
                      // First select the correct category. The existing native
                      // opener then carries exact book identity into workspace.
                      selectProjectCategory("children");
                      openNativeChildrensBookV1F2(childrensContinueProject);
                    } else {
                      continueNovelStory(
                        novelContinueProject
                      );
                    }
                  }}
                  style={{
                    width: "100%",
                  }}
                >
                  {isFR
                    ? "Continuer à écrire"
                    : "Continue Writing"}
                </button>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={() =>
                    selectProjectCategory(
                      category.id
                    )
                  }
                  style={{
                    width: "100%",
                  }}
                >
                  {ui.enterCategory}
                  {Number.isFinite(
                    category.count
                  )
                    ? ` · ${category.count}`
                    : ""}
                </button>
              </div>
            ) : (
              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={() =>
                  selectProjectCategory(
                    category.id
                  )
                }
                style={{ width: "100%" }}
              >
                {ui.enterCategory}
                {Number.isFinite(
                  category.count
                )
                  ? ` · ${category.count}`
                  : ""}
              </button>
            )
          ) : (
            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled
              style={{ width: "100%" }}
            >
              {ui.comingLater}
            </button>
          )}
        </div>
      </article>
    );
  }

  function openNativeChildrensBookV1F2(project) {
    const id = projectId(project);
    if (!nativeChildrensNovelPilotV1F1 || project?.bookCategory !== "children_book" || !id) return;
    // Native and legacy views are mutually exclusive; discard only stale UI state.
    // Existing legacy records and archives are never changed here.
    setShowChildrensBookCreationFlow(false);
    setActiveChildrensBookProject(null);
    replaceProjectHubPath(CHILDRENS_BOOK_BASE_PATH, { clearProjectId: true });
    setNativeChildrensOpenV1F1(project);
    onContextChange?.({
      category: "children", projectId: id, projectTitle: clean(project?.title),
    });
  }

  function closeNativeChildrensBookV1F2() {
    setNativeChildrensOpenV1F1(null);
    onContextChange?.({ category: "children", projectId: "", projectTitle: "" });
  }

  async function createNativeChildrensBookV1F1(event) {
    event?.preventDefault?.();
    const title = clean(nativeChildrensTitleV1F1);
    const ageRange = clean(nativeChildrensAgeV1F1);
    const readingArrangement = clean(nativeChildrensArrangementV1F1);
    const readingLevel = clean(nativeChildrensLevelV1F1);
    if (!title || !ageRange || !readingArrangement || !readingLevel) {
      setNativeChildrensErrorV1F1(isFR
        ? "Le titre et les trois choix de lecture sont obligatoires."
        : "Title and all three audience choices are required.");
      return;
    }
    try {
      setNativeChildrensBusyV1F1(true);
      setNativeChildrensErrorV1F1("");
      const created = await api.post("/api/stories", {
        title, forum: "Children's Book", description: "Children's Book working manuscript",
        writingMode: "Solo", mode: "Solo", storyType: "longform",
        language: isFR ? "fr" : "en", visibilityStatus: "private_draft",
        bookCategory: "children_book",
        childrensBookProfile: { ageRange, readingArrangement, readingLevel },
      });
      const id = projectId(created);
      if (!id || created?.bookCategory !== "children_book") {
        throw new Error("Native Children’s Book category was not confirmed by the server.");
      }
      // Reuse the same first-Chapter endpoint already used by Novel creation.
      let opened = created;
      try {
        const response = await api.patch(`/api/stories/${encodeURIComponent(id)}/chapters`, {
          chapters: [{ id: "chapter-1", title: isFR ? "Chapitre 1" : "Chapter 1", sceneIds: [] }],
        });
        opened = response?.story || created;
      } catch (chapterError) {
        setNativeChildrensErrorV1F1(isFR
          ? "Livre créé. Ajoutez le premier chapitre dans Structure du livre."
          : "Book created. Add Chapter 1 in Book Structure.");
      }
      openNativeChildrensBookV1F2({ ...opened, id, bookCategory: "children_book" });
      await loadProjects();
      setNativeChildrensTitleV1F1("");
      setNativeChildrensAgeV1F1("");
      setNativeChildrensArrangementV1F1("");
      setNativeChildrensLevelV1F1("");
    } catch (error) {
      setNativeChildrensErrorV1F1(clean(error?.message) || "Could not create a native Children’s Book.");
    } finally {
      setNativeChildrensBusyV1F1(false);
    }
  }

  function renderChildrensBookFoundation() {
    if (nativeChildrensNovelPilotV1F1 && nativeChildrensOpenV1F1) {
      // Real Novel Book View: never mark this project as a structure-preview proxy.
      return <NovelStoryBookViewDraft
        project={nativeChildrensOpenV1F1} lang={lang}
        returnChapterId={projectId(nativeChildrensOpenV1F1) === clean(initialProjectId) ? initialChapterId : ""}
        onBack={closeNativeChildrensBookV1F2}
        onOpenWriterStudio={(options = {}) =>
          navigate("writerStudio", nativeChildrensOpenV1F1, options)}
      />;
    }
    if (showChildrensBookCreationFlow) {
      const activeProject =
        activeChildrensBookProject?.project;

      const activeBlueprint =
        activeChildrensBookProject?.blueprint;

      return (
        <ChildrensBookCreationFlow
          key={
            activeProject?.id ||
            "new-childrens-book-project"
          }
          lang={lang}
          initialDraft={
            activeChildrensBookProject
          }
          persistenceEnabled={
            childrensBookPersistencePilot
          }
          readOnly={childrensBookReadOnlyPilot && !childrensBookPersistencePilot}
          projectId={
            activeProject?.id || ""
          }
          revision={
            activeBlueprint?.revision || 1
          }
          onSaveProject={
            saveChildrensBookProject
          }
          onApproveChildrensBookEdition={api.approveChildrensBookEdition}
          onUpdateChildrensBookVisibility={
            childrensBookPersistencePilot
              ? updateChildrensBookVisibility
              : undefined
          }
          onLoadChildrensBookAssemblyRange={
            api.getChildrensBookAssembly
          }
          onLoadChildrensBookAssemblyUnit={
            api.getChildrensBookAssemblyUnit
          }
          onLoadChildrensBookPublicationCompatibility={
            childrensBookReadAccess
              ? api.getChildrensBookPublicationCompatibility
              : undefined
          }
          onLoadChildrensBookPublicationProduction={
            childrensBookReadAccess
              ? api.getChildrensBookPublicationProduction
              : undefined
          }
          onLoadChildrensBookCoverPalettePreview={
            childrensBookReadAccess
              ? api.getChildrensBookCoverPalettePreview
              : undefined
          }
          onLoadChildrensBookPublicationInteriorReview={
            childrensBookReadAccess
              ? api.getChildrensBookPublicationInteriorReview
              : undefined
          }
          onLoadChildrensBookPublicationCoverReview={
            childrensBookReadAccess
              ? api.getChildrensBookPublicationCoverReview
              : undefined
          }
          onLoadChildrensBookPublicationFinalInterior={
            childrensBookReadAccess
              ? api.getChildrensBookPublicationFinalInterior
              : undefined
          }
          onLoadChildrensBookPublicationFinalCover={
            childrensBookReadAccess
              ? api.getChildrensBookPublicationFinalCover
              : undefined
          }
          onRegenerateChildrensBookPublicationInterior={
            childrensBookPersistencePilot
              ? api.regenerateChildrensBookPublicationInterior
              : undefined
          }
          onApproveChildrensBookPublicationInterior={
            childrensBookPersistencePilot
              ? api.approveChildrensBookPublicationInterior
              : undefined
          }
          onRegenerateChildrensBookPublicationCover={
            childrensBookPersistencePilot
              ? api.regenerateChildrensBookPublicationCover
              : undefined
          }
          onApproveChildrensBookPublicationCover={
            childrensBookPersistencePilot
              ? api.approveChildrensBookPublicationCover
              : undefined
          }
          onStartChildrensBookPublicationProduction={
            childrensBookPersistencePilot
              ? api.startChildrensBookPublicationProduction
              : undefined
          }
          onPrepareChildrensBookLocalDraftReflow={
            childrensBookPersistencePilot
              ? api.prepareChildrensBookLocalDraftReflow
              : undefined
          }
          onPreviewManuscriptImport={
            api.previewChildrensBookManuscriptImport
          }
          childrensBookAssets={
            childrensBookAssets
          }
          childrensBookAssetsLoading={
            childrensBookAssetsLoading
          }
          childrensBookAssetsError={
            childrensBookAssetsError
          }
          childrensBookAssetActionId={
            childrensBookAssetActionId
          }
          onLoadChildrensBookAssetFile={
            loadChildrensBookAssetFile
          }
          onUploadChildrensBookCover={
            uploadChildrensBookCover
          }
          onUploadChildrensBookIllustration={
            uploadChildrensBookIllustration
          }
          onRegisterChildrensBookAsset={
            registerChildrensBookAsset
          }
          onReplaceChildrensBookAsset={
            replaceChildrensBookAsset
          }
          onRemoveChildrensBookAsset={
            removeChildrensBookAsset
          }
          onExit={
            closeChildrensBookWorkspace
          }
        />
      );
    }

    // V1E5A: one author-facing entry, while legacy read-only mode stays intact.
    const unifiedChildrenWorkspaceV1E5 = childrensBookPersistencePilot &&
      process.env.REACT_APP_CHILDRENS_BOOK_NOVEL_REUSE_V1E4D_ENABLED === "true";
    const planningAreas = [
      {
        title: ui.childrenAgeTitle,
        body: ui.childrenAgeBody,
      },
      {
        title: ui.childrenStructureTitle,
        body: ui.childrenStructureBody,
      },
      {
        title: ui.childrenIllustrationTitle,
        body: ui.childrenIllustrationBody,
      },
      {
        title: ui.childrenFormatTitle,
        body: ui.childrenFormatBody,
      },
      {
        title: ui.childrenLanguageTitle,
        body: ui.childrenLanguageBody,
      },
      {
        title: ui.childrenPublicationTitle,
        body: ui.childrenPublicationBody,
      },
    ];

    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              {nativeChildrensNovelPilotV1F1 ? (isFR ? "ESPACE LIVRE JEUNESSE" : "CHILDREN'S BOOK STUDIO") : ui.childrenStudio}
            </div>

            <h2
              className="ns-section-title"
              style={{ margin: "5px 0 8px" }}
            >
              {nativeChildrensNovelPilotV1F1 ? (isFR ? "Mes livres jeunesse" : "My Children's Books") : ui.childrenFoundationTitle}
            </h2>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {nativeChildrensNovelPilotV1F1
                ? (isFR ? "Reprenez votre écriture ou commencez un nouveau livre." : "Continue writing or start a new book.")
                : ui.childrenFoundationIntro}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              selectProjectCategory("")
            }
          >
            {ui.backToTypes}
          </button>
        </div>

        {nativeChildrensNovelPilotV1F1 ? (
          <div data-native-childrens-novel-v1f1="true" data-native-childrens-entry-v1f2e="true"
            style={{display:"grid",gap:22,marginBottom:24}}>
            <section data-native-childrens-shelf-v1f2e="true">
              {projects.filter(item=>item?.bookCategory === "children_book").length === 0 ? (
                <div className="ns-card" style={{padding:24,borderRadius:18}}>
                  <strong>{isFR ? "Votre bibliothèque commence ici" : "Your first book starts here"}</strong>
                  <p className="ns-card-meta" style={{marginBottom:0}}>
                    {isFR ? "Créez un livre pour commencer à écrire." : "Create a book to begin writing."}
                  </p>
                </div>
              ) : (
                <div data-native-childrens-book-shelf-v1f2c="true"
                  style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,330px),1fr))",gap:18}}>
                  {projects.filter(item=>item?.bookCategory === "children_book").map(item=>(
                    <article key={projectId(item)}
                      className="ns-card ns-childrens-book-project-card"
                      data-native-childrens-book-card-v1f2c="true"
                      data-native-childrens-card-parity-v1f2e="true">
                      <div className="ns-childrens-book-project-head">
                        <div className="ns-childrens-book-project-icon" aria-hidden="true">
                          <ProjectTypeIcon type="children" />
                        </div>
                        <div className="ns-childrens-book-project-badges">
                          <span className="ns-childrens-book-project-badge is-active">
                            {isFR ? "En développement" : "In Development"}
                          </span>
                        </div>
                      </div>
                      <div className="ns-childrens-book-project-eyebrow">{ui.children}</div>
                      <h4 className="ns-card-title ns-childrens-book-project-title">
                        {clean(item?.title) || (isFR ? "Livre sans titre" : "Untitled Book")}
                      </h4>
                      <div className="ns-childrens-book-project-meta">
                        <span className="ns-childrens-book-project-meta-label">{ui.updated}</span>
                        <span>{formatDate(item?.updatedAt,lang)}</span>
                        <span>{Number(item?.summary?.canonScenes || 0)} {isFR ? "scènes Canon" : "Canon Scenes"}</span>
                      </div>
                      <div className="ns-childrens-book-project-actions" style={{gridTemplateColumns:"minmax(0,1fr)"}}>
                        <button type="button"
                          className="ns-button ns-button-primary ns-childrens-book-project-primary"
                          onClick={()=>openNativeChildrensBookV1F2(item)}>
                          {isFR ? "Ouvrir l’espace Livre" : "Open Book Workspace"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
            <section className="ns-card" data-native-childrens-create-v1f2e="true"
              style={{padding:"22px 24px",borderRadius:18,border:"1px solid rgba(5,150,105,0.24)",background:"linear-gradient(130deg,#f0fdf4,#ffffff 65%)"}}>
              <h3 style={{margin:"0 0 6px",fontSize:20}}>
                {isFR ? "Créer un livre jeunesse" : "Create a Children's Book"}
              </h3>
              <p className="ns-card-meta" style={{margin:"0 0 16px"}}>
                {isFR ? "Donnez un titre à votre livre et choisissez son public." : "Give your book a title and choose its audience."}
              </p>
              <form onSubmit={createNativeChildrensBookV1F1}
                style={{display:"grid",gap:14}}>
                <label style={{display:"grid",gap:6,fontWeight:700}}>
                  {isFR ? "Titre du livre" : "Book title"}
                  <input className="ns-input" required maxLength={160}
                    value={nativeChildrensTitleV1F1}
                    onChange={e => setNativeChildrensTitleV1F1(e.target.value)} />
                </label>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,210px),1fr))",gap:12}}>
                  <label style={{display:"grid",gap:6,fontWeight:700}}>
                    {isFR ? "Âge des lecteurs" : "Reader age range"}
                    <input className="ns-input" required maxLength={80}
                      placeholder="7–9" value={nativeChildrensAgeV1F1}
                      onChange={e => setNativeChildrensAgeV1F1(e.target.value)} />
                  </label>
                  <label style={{display:"grid",gap:6,fontWeight:700}}>
                    {isFR ? "Type de lecture" : "Reading arrangement"}
                    <select className="ns-input" required value={nativeChildrensArrangementV1F1}
                      onChange={e => setNativeChildrensArrangementV1F1(e.target.value)}>
                      <option value="">{isFR ? "Choisir" : "Select"}</option>
                      <option value="Read aloud">Read aloud</option>
                      <option value="Independent reading">Independent reading</option>
                      <option value="Shared reading">Shared reading</option>
                    </select>
                  </label>
                  <label style={{display:"grid",gap:6,fontWeight:700}}>
                    {isFR ? "Niveau de lecture" : "Reading level"}
                    <select className="ns-input" required value={nativeChildrensLevelV1F1}
                      onChange={e => setNativeChildrensLevelV1F1(e.target.value)}>
                      <option value="">{isFR ? "Choisir" : "Select"}</option>
                      <option value="Beginning reader">Beginning reader</option>
                      <option value="Early independent reader">Early independent reader</option>
                      <option value="Confident independent reader">Confident independent reader</option>
                    </select>
                  </label>
                </div>
                {nativeChildrensErrorV1F1 ? <p role="alert">{nativeChildrensErrorV1F1}</p> : null}
                <div>
                  <button className="ns-button ns-button-primary" type="submit"
                    disabled={nativeChildrensBusyV1F1}>
                    {nativeChildrensBusyV1F1 ? (isFR ? "Création…" : "Creating…")
                      : (isFR ? "Créer un livre jeunesse" : "Create Children's Book")}
                  </button>
                </div>
              </form>
            </section>
          </div>
        ) : null}
        <div
          className="ns-card"
          style={{
            display: nativeChildrensNovelPilotV1F1 ? "none" : undefined,
            padding: 16,
            marginBottom: 18,
            borderRadius: 18,
            background: "rgba(255,247,237,0.92)",
            border: "1px solid rgba(234,88,12,0.24)",
          }}
        >
          <strong>
            {childrensBookPersistencePilot
              ? (
                  isFR
                    ? "Créez ou ouvrez un livre jeunesse dans un seul espace de travail. Préparation, structure et écriture restent réunies."
                    : "Create or open a Children's Book in one Book Workspace. Book Setup, structure and writing belong together."
                )
              : childrensBookReadOnlyPilot
              ? ui.childrenExistingProjectsReadOnlyNotice
              : ui.childrenReadOnlyNotice}
          </strong>

          {!nativeChildrensNovelPilotV1F1 && canCreateProjects &&
          (
            !childrensBookReadOnlyPilot ||
            childrensBookPersistencePilot
          ) ? (
            <div style={{ marginTop: 14 }}>
              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={
                  beginNewChildrensBookProject
                }
              >
                {isFR
                  ? "Créer un livre jeunesse"
                  : "Create Children's Book"}
              </button>
            </div>
          ) : null}
        </div>

        {childrensBookReadAccess && (
          !nativeChildrensNovelPilotV1F1 ||
          childrensBookProjects.length > 0 ||
          archivedChildrensBookProjects.length > 0 ||
          childrensBookProjectsLoading ||
          Boolean(childrensBookProjectsError)
        ) ? (
          <section
            style={{
              marginBottom: 22,
            }}
          >
            <h3
              className="ns-section-title"
              style={{
                margin: "0 0 12px",
              }}
            >
              {isFR
                ? (nativeChildrensNovelPilotV1F1 ? "Livres existants (ancien système)" : "Mes projets de livres pour enfants")
                : (nativeChildrensNovelPilotV1F1 ? "Existing Books (Legacy)" : "My Children’s Book Projects")}
            </h3>

            {childrensBookProjectsLoading ? (
              <div
                className="ns-card"
                style={{ padding: 18 }}
              >
                {isFR
                  ? "Chargement des projets…"
                  : "Loading projects…"}
              </div>
            ) : childrensBookProjectsError ? (
              <div
                className="ns-card"
                style={{ padding: 18 }}
              >
                <p
                  style={{
                    marginTop: 0,
                    color: "#b91c1c",
                    fontWeight: 800,
                  }}
                >
                  {childrensBookProjectsError}
                </p>

                <button
                  className="ns-button ns-button-outline"
                  type="button"
                  onClick={
                    loadChildrensBookProjects
                  }
                >
                  {ui.retry}
                </button>
              </div>
            ) : childrensBookProjects.length === 0 ? (
              <div
                className="ns-card"
                style={{ padding: 20 }}
              >
                <strong>
                  {isFR
                    ? "Aucun projet enregistré"
                    : (nativeChildrensNovelPilotV1F1 ? "No active legacy book" : "No saved project yet")}
                </strong>

                <p
                  className="ns-card-meta"
                  style={{
                    marginBottom: 0,
                  }}
                >
                  {isFR
                    ? (nativeChildrensNovelPilotV1F1
                      ? "Les anciens projets archivés restent accessibles ci-dessous. Créez les nouveaux livres dans l’espace natif ci-dessus."
                      : "Commencez un nouveau projet puis utilisez « Créer et enregistrer le projet ».")
                    : (nativeChildrensNovelPilotV1F1
                      ? "Archived legacy projects remain available below. Create new books using the native workspace above."
                      : "Begin a new project, then use “Create and Save Project.”")}
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(250px, 1fr))",
                  gap: 16,
                }}
              >
                {childrensBookProjects.map(
                  (project) => {
                    const id =
                      clean(project?.id);

                    const opening =
                      openingChildrensBookProjectId ===
                      id;

                    const revisionNumber =
                      Number(project?.revision);

                    const hasRevision =
                      Number.isFinite(
                        revisionNumber
                      ) &&
                      revisionNumber > 0;
                    const isPublishedOnNextScenes =
                      clean(
                        project
                          ?.publicationReadiness
                          ?.status
                      ).toLowerCase() ===
                      "published_nextscenes";

                    return (
                      <article
                        key={id}
                        className="ns-card ns-childrens-book-project-card"
                      >
                        <div className="ns-childrens-book-project-head">
                          <div
                            className="ns-childrens-book-project-icon"
                            aria-hidden="true"
                          >
                            <ProjectTypeIcon type="children" />
                          </div>

                          <div className="ns-childrens-book-project-badges">
                            <span
                              className={
                                "ns-childrens-book-project-badge" +
                                (
                                  isPublishedOnNextScenes
                                    ? ""
                                    : " is-active"
                                )
                              }
                            >
                              {isPublishedOnNextScenes
                                ? (
                                    isFR
                                      ? "Publié sur NextScenes"
                                      : "Published on NextScenes"
                                  )
                                : (
                                    isFR
                                      ? "En développement"
                                      : "In Development"
                                  )}
                            </span>

                            {hasRevision ? (
                              <span className="ns-childrens-book-project-badge">
                                {isFR
                                  ? `Révision ${revisionNumber}`
                                  : `Revision ${revisionNumber}`}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="ns-childrens-book-project-eyebrow">
                          {ui.children}
                        </div>

                        <h4 className="ns-card-title ns-childrens-book-project-title">
                          {clean(
                            project?.title
                          ) ||
                            (
                              isFR
                                ? "Projet sans titre"
                                : "Untitled project"
                            )}
                        </h4>

                        <div className="ns-childrens-book-project-meta">
                          <span className="ns-childrens-book-project-meta-label">
                            {ui.updated}
                          </span>

                          <span>
                            {formatDate(
                              project?.updatedAt,
                              lang
                            )}
                          </span>
                        </div>

                        <div className="ns-childrens-book-project-actions">
                          {isPublishedOnNextScenes ? (
                            <>
                              <button
                                className="ns-button ns-button-primary ns-childrens-book-project-primary"
                                type="button"
                                disabled={
                                  !projectId(project) ||
                                  childrensBookProjectActionId === id
                                }
                                onClick={() =>
                                  openPublishedChildrensBook(
                                    project
                                  )
                                }
                              >
                                {isFR
                                  ? "Ouvrir le livre publié"
                                  : "Open Published Book"}
                              </button>

                              <button
                                className="ns-button ns-button-outline"
                                type="button"
                                disabled={
                                  !id ||
                                  opening ||
                                  childrensBookProjectActionId === id
                                }
                                onClick={() =>
                                  openChildrensBookProject(
                                    project
                                  )
                                }
                              >
                                {opening
                                  ? (
                                      isFR
                                        ? "Ouverture…"
                                        : "Opening…"
                                    )
                                  : (
                                      isFR
                                        ? "Ouvrir l’espace Livre"
                                        : "Open Book Workspace"
                                    )}
                              </button>
                            </>
                          ) : (
                            <button
                              className="ns-button ns-button-primary ns-childrens-book-project-primary"
                              type="button"
                              disabled={
                                !id ||
                                opening ||
                                childrensBookProjectActionId === id
                              }
                              onClick={() =>
                                openChildrensBookProject(
                                  project
                                )
                              }
                            >
                              {opening
                                ? (
                                    isFR
                                      ? "Ouverture…"
                                      : "Opening…"
                                  )
                                : (
                                    isFR
                                      ? "Ouvrir l’espace Livre"
                                      : "Open Book Workspace"
                                  )}
                            </button>
                          )}


                          {childrensBookPersistencePilot ? (
                            <button
                              className="ns-button ns-button-outline ns-childrens-book-project-archive"
                              type="button"
                              disabled={
                                !id ||
                                childrensBookProjectActionId === id
                              }
                              onClick={() =>
                                requestArchiveChildrensBookProject(
                                  project
                                )
                              }
                            >
                              {childrensBookProjectActionId === id
                                ? (
                                    isFR
                                      ? "Traitement…"
                                      : "Working…"
                                  )
                                : (
                                    isFR
                                      ? "Archiver"
                                      : "Archive"
                                  )}
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  }
                )}
              </div>
            )}

            {pendingChildrensBookArchiveProject ? (
              <form
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="childrens-book-archive-title"
                aria-describedby="childrens-book-archive-description"
                onSubmit={(event) => {
                  event.preventDefault();

                  confirmArchiveChildrensBookProject();
                }}
                className="ns-card"
                style={{
                  padding: 20,
                  marginTop: 18,
                  borderRadius: 18,
                  border:
                    "2px solid rgba(185,28,28,0.34)",
                  background: "#fff7f7",
                }}
              >
                <h4
                  id="childrens-book-archive-title"
                  className="ns-card-title"
                  style={{
                    margin: 0,
                    color: "#991b1b",
                  }}
                >
                  {isFR
                    ? "Archiver ce projet ?"
                    : "Archive This Project?"}
                </h4>

                <p
                  id="childrens-book-archive-description"
                  className="ns-card-meta"
                  style={{
                    margin: "10px 0 0",
                    lineHeight: 1.6,
                  }}
                >
                  {isFR
                    ? `« ${
                        clean(
                          pendingChildrensBookArchiveProject
                            ?.title
                        ) ||
                        "Projet sans titre"
                      } » sera retiré de votre liste active. Son Blueprint et ses ressources seront conservés, et vous pourrez restaurer le projet plus tard.`
                    : `“${
                        clean(
                          pendingChildrensBookArchiveProject
                            ?.title
                        ) ||
                        "Untitled project"
                      }” will be removed from your active list. Its Blueprint and project assets will be retained, and you can restore the project later.`}
                </p>

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    marginTop: 16,
                  }}
                >
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={
                      childrensBookProjectActionId ===
                      clean(
                        pendingChildrensBookArchiveProject
                          ?.id
                      )
                    }
                    onClick={
                      cancelArchiveChildrensBookProject
                    }
                  >
                    {isFR ? "Annuler" : "Cancel"}
                  </button>

                  <button
                    className="ns-button ns-button-primary"
                    type="submit"
                    disabled={
                      !clean(
                        pendingChildrensBookArchiveProject
                          ?.id
                      ) ||
                      childrensBookProjectActionId ===
                        clean(
                          pendingChildrensBookArchiveProject
                            ?.id
                        )
                    }
                    aria-busy={
                      childrensBookProjectActionId ===
                      clean(
                        pendingChildrensBookArchiveProject
                          ?.id
                      )
                    }
                    style={{
                      background: "#b91c1c",
                      borderColor: "#991b1b",
                      color: "#ffffff",
                    }}
                  >
                    {childrensBookProjectActionId ===
                    clean(
                      pendingChildrensBookArchiveProject
                        ?.id
                    )
                      ? (
                          isFR
                            ? "Archivage…"
                            : "Archiving…"
                        )
                      : (
                          isFR
                            ? "Archiver le projet"
                            : "Archive Project"
                        )}
                  </button>
                </div>
              </form>
            ) : null}

            <div
              style={{
                marginTop: 18,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  setShowArchivedChildrensBookProjects(
                    (current) => !current
                  )
                }
              >
                {showArchivedChildrensBookProjects
                  ? (
                      isFR
                        ? "Masquer les projets archivés"
                        : "Hide Archived Projects"
                    )
                  : (
                      isFR
                        ? `Projets archivés (${archivedChildrensBookProjects.length})`
                        : `Archived Projects (${archivedChildrensBookProjects.length})`
                    )}
              </button>
            </div>

            {showArchivedChildrensBookProjects ? (
              <div
                style={{
                  marginTop: 16,
                  display: "grid",
                  gap: 12,
                }}
              >
                {archivedChildrensBookProjects.length === 0 ? (
                  <div
                    className="ns-card"
                    style={{ padding: 18 }}
                  >
                    {isFR
                      ? "Aucun projet archivé."
                      : "No archived project."}
                  </div>
                ) : (
                  archivedChildrensBookProjects.map(
                    (project) => {
                      const id =
                        clean(project?.id);

                      const working =
                        childrensBookProjectActionId ===
                        id;

                      return (
                        <article
                          key={id}
                          className="ns-card ns-childrens-book-project-card is-archived"
                        >
                          <h4 className="ns-card-title ns-childrens-book-project-title">
                            {clean(project?.title) ||
                              (
                                isFR
                                  ? "Projet sans titre"
                                  : "Untitled project"
                              )}
                          </h4>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "6px 0 12px",
                            }}
                          >
                            {isFR
                              ? "Archivé le"
                              : "Archived"}:{" "}
                            {formatDate(
                              project?.archivedAt,
                              lang
                            )}
                          </p>

                          {childrensBookPersistencePilot ? (
                            <button
                              className="ns-button ns-button-primary"
                              type="button"
                              disabled={!id || working}
                              onClick={() =>
                                restoreChildrensBookProject(
                                  project
                                )
                              }
                            >
                              {working
                                ? (
                                    isFR
                                      ? "Restauration…"
                                      : "Restoring…"
                                  )
                                : (
                                    isFR
                                      ? "Restaurer"
                                      : "Restore"
                                  )}
                            </button>
                          ) : null}
                        </article>
                      );
                    }
                  )
                )}
              </div>
            ) : null}
          </section>
        ) : null}

        {!nativeChildrensNovelPilotV1F1 && !unifiedChildrenWorkspaceV1E5 ? <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 16,
          }}
        >
          {planningAreas.map((area, index) => (
            <article
              key={area.title}
              className="ns-card ns-childrens-book-planning-card"
            >
              <div
                className="ns-childrens-book-planning-index"
                aria-hidden="true"
              >
                {String(index + 1).padStart(2, "0")}
              </div>

              <h3
                className="ns-card-title"
                style={{
                  margin: "0 0 9px",
                  fontSize: 19,
                }}
              >
                {area.title}
              </h3>

              <p
                className="ns-card-meta"
                style={{
                  margin: 0,
                  lineHeight: 1.55,
                }}
              >
                {area.body}
              </p>
            </article>
          ))}
        </div> : null}
      </>
    );
  }

  function renderScreenplayFoundation() {
    const active =
      activeScreenplayProject;

    const canEditProjectPlan =
      screenplayProjectAccess
        ?.canEditProjectPlan ===
        true;

    const canOpenScriptWorkspace =
      screenplayProjectAccess
        ?.canOpenScriptWorkspace ===
        true;

    const canManageScreenplayProject =
      screenplayProjectAccess
        ?.canManageProject ===
        true;

    const screenplayCollaborationEnabled =
      clean(
        active?.writingMode
      ).toLowerCase() !==
      "solo";

    const isScreenplayCollaborator =
      screenplayProjectAccess
        ?.relationship ===
        "collaborator";

    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <div style={{ maxWidth: 760 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#dc2626",
              }}
            >
              {isFR
                ? "STUDIO SCÉNARIO"
                : "SCREENPLAY STUDIO"}
            </div>

            <h2
              className="ns-section-title"
              style={{
                margin: "5px 0 8px",
              }}
            >
              {active
                ? clean(active?.title)
                : (
                    isFR
                      ? "Vos projets de scénario"
                      : "Your Screenplay Projects"
                  )}
            </h2>

            <p
              className="ns-card-meta"
              style={{
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {active
                ? (
                    isFR
                      ? "L’identité du projet est enregistrée dans la fondation commune de NextScenes."
                      : "The project identity is established in the shared NextScenes Creative Project foundation."
                  )
                : (
                    isFR
                      ? "Créez ou reprenez le projet qui accueillera votre scénario et les futurs outils du Screenplay Studio."
                      : "Create or reopen the project that will hold your screenplay and its future Screenplay Studio tools."
                  )}
            </p>
          </div>

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              active
                ? closeScreenplayProject()
                : selectProjectCategory("")
            }
          >
            {active
              ? (
                  isFR
                    ? "Retour aux scénarios"
                    : "Back to Screenplays"
                )
              : ui.backToTypes}
          </button>
        </div>

        {!canCreateProjects &&
        screenplayProjects.length === 0 &&
        !active ? (
          <div
            className="ns-card"
            style={{
              padding: 20,
            }}
          >
            <strong>
              {isFR
                ? "Un compte Writer ou Moderator est requis pour créer un projet de scénario."
                : "Writer or Moderator access is required to create a Screenplay project."}
            </strong>
          </div>
        ) : active ? (
          <>
            {screenplayInvitationNotice?.text ? (
              <div
                className="ns-card"
                role={
                  screenplayInvitationNotice
                    ?.tone === "warning"
                    ? "alert"
                    : "status"
                }
                style={{
                  padding: 14,
                  marginBottom: 14,
                  border:
                    screenplayInvitationNotice
                      ?.tone === "warning"
                      ? "1.5px solid rgba(217,119,6,0.38)"
                      : "1.5px solid rgba(22,163,74,0.30)",
                  background:
                    screenplayInvitationNotice
                      ?.tone === "warning"
                      ? "#fff7ed"
                      : "#f0fdf4",
                }}
              >
                <strong>
                  {
                    screenplayInvitationNotice
                      .text
                  }
                </strong>
              </div>
            ) : null}

            {isScreenplayCollaborator ? (
              <div
                className="ns-card"
                role="status"
                style={{
                  padding: 14,
                  marginBottom: 14,
                  border:
                    "1.5px solid rgba(37,99,235,0.24)",
                  background:
                    "#eff6ff",
                }}
              >
                <strong>
                  {isFR
                    ? "Accès collaborateur au projet"
                    : "Collaborator project access"}
                </strong>

                <div
                  className="ns-card-meta"
                  style={{
                    marginTop: 5,
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Vous pouvez consulter la vue d’ensemble, la Story Map et Personnages et univers. Les contributions éligibles passent par un Proposal Round ouvert ; les modifications directes et le Script Workspace restent réservés au propriétaire."
                    : "You can review the Project Overview, Story Map and Characters & World. Eligible contributions are submitted through an open Proposal Round; direct edits and Script Workspace remain owner-only."}
                </div>
              </div>
            ) : null}

            <ScreenplayWorkspaceNavigator
              currentWorkspace={
                screenplayWorkspace
              }
              onNavigate={
                openScreenplayWorkspace
              }
              scriptWorkspaceEnabled={
                canOpenScriptWorkspace
              }
              collaborationWorkspaceEnabled={
                screenplayCollaborationEnabled
              }
              reviewWorkspaceEnabled={
                canManageScreenplayProject
              }
              isFR={isFR}
            />

            {screenplayWorkspace ===
              "project-plan" &&
            screenplayProjectAccess
              ?.canManageProject ===
              true &&
            screenplayGroupWritingMode(
              active?.writingMode
            ) ? (
              <section
                className="ns-card"
                aria-label={
                  isFR
                    ? "Gestion des invitations"
                    : "Invitation management"
                }
                style={{
                  padding: 16,
                  marginBottom: 16,
                  border:
                    "1px solid rgba(37,99,235,0.20)",
                  background:
                    "rgba(248,250,252,0.98)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems:
                      "flex-start",
                    justifyContent:
                      "space-between",
                    gap: 12,
                    flexWrap:
                      "wrap",
                  }}
                >
                  <div
                    style={{
                      maxWidth: 700,
                    }}
                  >
                    <strong>
                      Invitations
                    </strong>

                    <div
                      className="ns-card-meta"
                      style={{
                        marginTop: 4,
                        lineHeight: 1.5,
                      }}
                    >
                      {isFR
                        ? "Voyez qui a répondu et renvoyez une invitation encore en attente. L’adhésion n’accorde pas encore l’écriture du scénario."
                        : "See who has responded and resend an invitation that is still pending. Membership does not yet grant Screenplay writing access."}
                    </div>
                  </div>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    disabled={
                      screenplayInvitationsLoading
                    }
                    onClick={() => {
                      if (
                        showScreenplayInvitations
                      ) {
                        setShowScreenplayInvitations(
                          false
                        );
                        return;
                      }

                      loadScreenplayInvitations(
                        clean(active?.id)
                      );
                    }}
                  >
                    {screenplayInvitationsLoading
                      ? (
                          isFR
                            ? "Chargement…"
                            : "Loading…"
                        )
                      : showScreenplayInvitations
                      ? (
                          isFR
                            ? "Masquer"
                            : "Hide Invitations"
                        )
                      : (
                          isFR
                            ? "Voir les invitations"
                            : "View Invitations"
                        )}
                  </button>
                </div>

                {showScreenplayInvitations ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 10,
                      marginTop: 14,
                    }}
                  >
                    {screenplayInvitationsError ? (
                      <div
                        role="alert"
                        style={{
                          color:
                            "#b91c1c",
                          fontWeight:
                            700,
                          fontSize:
                            13,
                        }}
                      >
                        {
                          screenplayInvitationsError
                        }
                      </div>
                    ) : null}

                    {screenplayInvitationsNotice ? (
                      <div
                        role="status"
                        style={{
                          color:
                            "#166534",
                          fontWeight:
                            700,
                          fontSize:
                            13,
                        }}
                      >
                        {
                          screenplayInvitationsNotice
                        }
                      </div>
                    ) : null}

                    {!screenplayInvitationsLoading &&
                    !screenplayInvitationsError &&
                    screenplayInvitations.length ===
                      0 ? (
                      <div
                        className="ns-card-meta"
                      >
                        {isFR
                          ? "Aucune invitation enregistrée pour ce projet."
                          : "No invitations are recorded for this project."}
                      </div>
                    ) : null}

                    {screenplayInvitations.map(
                      (invitation) => {
                        const invitationId =
                          clean(
                            invitation?.id
                          );

                        const status =
                          clean(
                            invitation
                              ?.effectiveStatus
                          ) ||
                          "pending";

                        const working =
                          screenplayInvitationActionId ===
                          invitationId;

                        const statusBackground =
                          status ===
                          "accepted"
                            ? "#f0fdf4"
                            : status ===
                              "delivery_failed"
                            ? "#fef2f2"
                            : status ===
                              "expired"
                            ? "#fff7ed"
                            : "#eff6ff";

                        const statusColor =
                          status ===
                          "accepted"
                            ? "#166534"
                            : status ===
                              "delivery_failed"
                            ? "#b91c1c"
                            : status ===
                              "expired"
                            ? "#9a3412"
                            : "#1d4ed8";

                        return (
                          <div
                            key={
                              invitationId
                            }
                            style={{
                              display:
                                "flex",
                              alignItems:
                                "center",
                              justifyContent:
                                "space-between",
                              gap: 12,
                              flexWrap:
                                "wrap",
                              padding:
                                "11px 12px",
                              borderRadius:
                                12,
                              border:
                                "1px solid rgba(148,163,184,0.24)",
                              background:
                                "#ffffff",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight:
                                    800,
                                }}
                              >
                                {clean(
                                  invitation
                                    ?.email
                                )}
                              </div>

                              <div
                                className="ns-card-meta"
                                style={{
                                  marginTop:
                                    3,
                                  fontSize:
                                    12.5,
                                }}
                              >
                                {status ===
                                  "accepted" &&
                                invitation
                                  ?.acceptedAt
                                  ? (
                                      isFR
                                        ? `Acceptée le ${formatDate(invitation.acceptedAt, lang)}`
                                        : `Accepted ${formatDate(invitation.acceptedAt, lang)}`
                                    )
                                  : status ===
                                    "declined" &&
                                    invitation
                                      ?.declinedAt
                                  ? (
                                      isFR
                                        ? `Refusée le ${formatDate(invitation.declinedAt, lang)}`
                                        : `Declined ${formatDate(invitation.declinedAt, lang)}`
                                    )
                                  : invitation
                                      ?.expiresAt
                                  ? (
                                      isFR
                                        ? `Expire le ${formatDate(invitation.expiresAt, lang)}`
                                        : `Expires ${formatDate(invitation.expiresAt, lang)}`
                                    )
                                  : ""}
                              </div>
                            </div>

                            <div
                              style={{
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                gap: 8,
                                flexWrap:
                                  "wrap",
                              }}
                            >
                              <span
                                style={{
                                  display:
                                    "inline-flex",
                                  alignItems:
                                    "center",
                                  minHeight:
                                    30,
                                  padding:
                                    "4px 9px",
                                  borderRadius:
                                    999,
                                  background:
                                    statusBackground,
                                  color:
                                    statusColor,
                                  fontSize:
                                    12,
                                  fontWeight:
                                    800,
                                }}
                              >
                                {screenplayInvitationStatusLabel(
                                  status,
                                  isFR
                                )}
                              </span>

                              {invitation
                                ?.canResend ===
                              true ? (
                                <button
                                  className="ns-button ns-button-outline"
                                  type="button"
                                  disabled={
                                    working
                                  }
                                  onClick={() =>
                                    resendScreenplayInvitation(
                                      invitation
                                    )
                                  }
                                >
                                  {working
                                    ? (
                                        isFR
                                          ? "Renvoi…"
                                          : "Resending…"
                                      )
                                    : (
                                        isFR
                                          ? "Renvoyer"
                                          : "Resend Invitation"
                                      )}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                ) : null}
              </section>
            ) : null}

            <section
              id="screenplay-project-plan-workspace"
              aria-label={
                isFR
                  ? "Vue d’ensemble du projet"
                  : "Project Overview workspace"
              }
              hidden={
                screenplayWorkspace !==
                "project-plan"
              }
            >
            <ScreenplayProjectOverview
                        project={active}
                        projectPlan={
                          screenplayProjectPlan
                        }
                        loading={
                          screenplayProjectPlanLoading
                        }
                        loadError={
                          screenplayProjectPlanError
                        }
                        onReload={() =>
                          loadScreenplayProjectDetail(
                            clean(active?.id)
                          )
                        }
                        onSave={
                          saveScreenplayProjectOverview
                        }
                        canEdit={
                          canEditProjectPlan
                        }
                        isFR={isFR}
                        writingModeLabel={
                          writingModeLabel(
                            active?.writingMode,
                            lang
                          )
                        }
                        updatedLabel={
                          formatDate(
                            screenplayProjectPlan
                              ?.updatedAt ||
                              active?.updatedAt,
                            lang
                          )
                        }
                      />
            </section>

            <section
              id="screenplay-story-map-workspace"
              aria-label="Story Map workspace"
              hidden={
                screenplayWorkspace !==
                "story-map"
              }
            >
            <ScreenplayStoryMap
                        projectPlan={
                          screenplayProjectPlan
                        }
                        onSave={
                          saveScreenplayStoryMap
                        }
                        canEdit={
                          canEditProjectPlan
                        }
                        isFR={isFR}
                      />
            </section>

            <section
              id="screenplay-characters-world-workspace"
              aria-label={
                isFR
                  ? "Personnages et univers"
                  : "Characters & World"
              }
              hidden={
                screenplayWorkspace !==
                "characters-world"
              }
            >
              <ScreenplayCharactersWorld
                projectId={
                  clean(active?.id)
                }
                canManage={
                  canManageScreenplayProject
                }
                active={
                  screenplayWorkspace ===
                  "characters-world"
                }
                isFR={isFR}
              />
            </section>

            <section
              id="screenplay-proposal-round-workspace"
              aria-label="Proposal Round"
              hidden={
                screenplayWorkspace !==
                "proposal-round"
              }
            >
              <ScreenplayProposalRoundWorkspace
                projectId={
                  clean(active?.id)
                }
                writingMode={
                  active?.writingMode
                }
                access={
                  screenplayProjectAccess
                }
                active={
                  screenplayWorkspace ===
                  "proposal-round"
                }
                isFR={isFR}
                onOpenReview={() =>
                  openScreenplayWorkspace(
                    "review-and-decide"
                  )
                }
              />
            </section>

            <section
              id="screenplay-review-and-decide-workspace"
              aria-label="Review and Decide"
              hidden={
                screenplayWorkspace !==
                "review-and-decide"
              }
            >
              <ScreenplayReviewDecideWorkspace
                projectId={
                  clean(active?.id)
                }
                access={
                  screenplayProjectAccess
                }
                active={
                  screenplayWorkspace ===
                  "review-and-decide"
                }
                isFR={isFR}
                onOpenProposal={() =>
                  openScreenplayWorkspace(
                    "proposal-round"
                  )
                }
              />
            </section>

            <section
              id="screenplay-script-workspace"
              aria-label="Script Workspace"
              hidden={
                screenplayWorkspace !==
                "script-workspace"
              }
            >
              <ScreenplayScriptWorkspace
                projectId={
                  clean(active?.id)
                }
                active={
                  screenplayWorkspace ===
                    "script-workspace" &&
                  canOpenScriptWorkspace
                }
                isFR={isFR}
              />
            </section>
          </>
        ) : (
          <>
            <form
              className="ns-card"
              hidden={
                !canCreateProjects
              }
              onSubmit={
                createScreenplayProject
              }
              style={{
                padding: 20,
                borderRadius: 20,
                marginBottom: 22,
                border:
                  "1.5px solid rgba(220,38,38,0.24)",
              }}
            >
              <h3
                className="ns-card-title"
                style={{
                  margin: "0 0 7px",
                }}
              >
                {isFR
                  ? "Créer un projet de scénario"
                  : "Create a Screenplay Project"}
              </h3>

              <p
                className="ns-card-meta"
                style={{
                  margin:
                    "0 0 14px",
                }}
              >
                {isFR
                  ? "Commencez par l’identité commune du projet. Les détails du scénario viendront dans le Studio."
                  : "Begin with the common project identity. Screenplay-specific planning belongs in the Studio."}
              </p>

              <label
                style={{
                  display: "grid",
                  gap: 7,
                  maxWidth: 620,
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                  }}
                >
                  {isFR
                    ? "Titre du scénario"
                    : "Screenplay title"}
                </span>

                <input
                  className="ns-input"
                  value={
                    screenplayTitle
                  }
                  maxLength={240}
                  disabled={
                    screenplayCreating
                  }
                  onChange={(event) =>
                    setScreenplayTitle(
                      event.target.value
                    )
                  }
                  placeholder={
                    isFR
                      ? "Titre de votre scénario"
                      : "Your screenplay title"
                  }
                />
              </label>

              <label
                style={{
                  display: "grid",
                  gap: 7,
                  maxWidth: 620,
                  marginTop: 14,
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                  }}
                >
                  {isFR
                    ? "Mode d’écriture"
                    : "Writing mode"}
                </span>

                <select
                  className="ns-input"
                  value={
                    screenplayWritingMode
                  }
                  disabled={
                    screenplayCreating
                  }
                  onChange={(event) => {
                    const nextMode =
                      event.target.value;

                    setScreenplayWritingMode(
                      nextMode
                    );

                    setScreenplayInviteEmailsError(
                      ""
                    );

                    if (
                      !screenplayGroupWritingMode(
                        nextMode
                      )
                    ) {
                      setScreenplayInviteEmailsText(
                        ""
                      );
                    }
                  }}
                >
                  <option value="solo">
                    Solo
                  </option>

                  <option value="collaborative">
                    {isFR
                      ? "Collaboratif"
                      : "Collaborative"}
                  </option>

                  <option value="group_open">
                    {isFR
                      ? "Groupe (ouvert)"
                      : "Group (Open)"}
                  </option>

                  <option value="group_closed">
                    {isFR
                      ? "Groupe (fermé)"
                      : "Group (Closed)"}
                  </option>
                </select>

                <span
                  className="ns-card-meta"
                  style={{
                    lineHeight: 1.45,
                  }}
                >
                  {screenplayWritingMode ===
                  "solo"
                    ? (
                        isFR
                          ? "Vous développez le scénario sous votre contrôle personnel."
                          : "You develop the screenplay under your personal control."
                      )
                    : screenplayWritingMode ===
                      "collaborative"
                    ? (
                        isFR
                          ? "Une équipe définie collabore sous une direction responsable."
                          : "A defined team collaborates under accountable leadership."
                      )
                    : screenplayWritingMode ===
                      "group_open"
                    ? (
                        isFR
                          ? "Une communauté éligible plus large pourra participer selon les règles du projet."
                          : "A wider eligible community may participate under the project’s rules."
                      )
                    : (
                        isFR
                          ? "La participation est limitée aux membres invités."
                          : "Participation is limited to invited members."
                      )}
                </span>
              </label>

              {screenplayGroupWritingMode(
                screenplayWritingMode
              ) ? (
                <label
                  style={{
                    display: "grid",
                    gap: 7,
                    maxWidth: 620,
                    marginTop: 14,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 800,
                    }}
                  >
                    {isFR
                      ? "E-mails des invités"
                      : "Invitee emails"}
                  </span>

                  <textarea
                    className="ns-input"
                    value={
                      screenplayInviteEmailsText
                    }
                    rows={3}
                    disabled={
                      screenplayCreating
                    }
                    onChange={(event) => {
                      setScreenplayInviteEmailsText(
                        event.target.value
                      );

                      setScreenplayInviteEmailsError(
                        ""
                      );
                    }}
                    placeholder={
                      isFR
                        ? "ex. ami@exemple.com, coautrice@exemple.com"
                        : "e.g. friend@example.com, cowriter@example.com"
                    }
                  />

                  <span
                    className="ns-card-meta"
                    style={{
                      lineHeight: 1.45,
                    }}
                  >
                    {isFR
                      ? "Ajoutez une ou plusieurs adresses, séparées par des virgules. Les invitations sont envoyées après la création du projet."
                      : "Add one or more addresses, separated by commas. Invitations are sent after the project is created."}
                  </span>

                  {screenplayInviteEmailsError ? (
                    <span
                      role="alert"
                      style={{
                        color: "#b91c1c",
                        fontWeight: 800,
                        fontSize: 13,
                      }}
                    >
                      {
                        screenplayInviteEmailsError
                      }
                    </span>
                  ) : null}
                </label>
              ) : null}


              <div
                style={{
                  marginTop: 14,
                }}
              >
                <button
                  className="ns-button ns-button-primary"
                  type="submit"
                  disabled={
                    screenplayCreating ||
                    !clean(
                      screenplayTitle
                    )
                  }
                >
                  {screenplayCreating
                    ? (
                        isFR
                          ? "Création…"
                          : "Creating…"
                      )
                    : (
                        isFR
                          ? "Créer le projet"
                          : "Create Project"
                      )}
                </button>
              </div>
            </form>

            <section>
              <h3
                className="ns-section-title"
                style={{
                  margin: "0 0 12px",
                }}
              >
                {isFR
                  ? "Mes scénarios"
                  : "My Screenplays"}
              </h3>

              {screenplayProjectsLoading ? (
                <div
                  className="ns-card"
                  style={{
                    padding: 18,
                  }}
                >
                  {isFR
                    ? "Chargement des projets…"
                    : "Loading projects…"}
                </div>
              ) : screenplayProjectsError ? (
                <div
                  className="ns-card"
                  style={{
                    padding: 18,
                  }}
                >
                  <p
                    style={{
                      marginTop: 0,
                      color: "#b91c1c",
                      fontWeight: 800,
                    }}
                  >
                    {screenplayProjectsError}
                  </p>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={
                      loadScreenplayProjects
                    }
                  >
                    {ui.retry}
                  </button>
                </div>
              ) : screenplayProjects.length === 0 ? (
                <div
                  className="ns-card"
                  style={{
                    padding: 20,
                  }}
                >
                  <strong>
                    {isFR
                      ? "Aucun projet de scénario pour le moment."
                      : "No Screenplay project yet."}
                  </strong>
                </div>
              ) : (
                <div
                  className="ns-project-hub-screenplay-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(3, minmax(0, 1fr))",
                    gap: 14,
                    alignItems:
                      "stretch",
                    justifyContent:
                      "stretch",
                  }}
                >
                  {screenplayProjects.map(
                    (project) => {
                      const id =
                        clean(
                          project?.id
                        );

                      const isEditing =
                        clean(
                          editingScreenplayProject
                            ?.id
                        ) === id;

                      return (
                        <article
                          key={id}
                          className="ns-card"
                          style={{
                            padding: 16,
                            borderRadius: 16,
                            border:
                              "1px solid rgba(220,38,38,0.16)",
                            borderTop:
                              "3px solid rgba(220,38,38,0.72)",
                            boxShadow:
                              "0 7px 18px rgba(15,23,42,0.06)",
                            minHeight:
                              178,
                            height:
                              "100%",
                            display:
                              "flex",
                            flexDirection:
                              "column",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 900,
                              letterSpacing:
                                "0.1em",
                              textTransform:
                                "uppercase",
                              color:
                                "#dc2626",
                            }}
                          >
                            {isFR
                              ? "SCÉNARIO"
                              : "SCREENPLAY"}
                          </div>

                          <h4
                            className="ns-card-title"
                            style={{
                              margin:
                                "7px 0 7px",
                              fontSize: 15.5,
                              fontWeight:
                                800,
                              lineHeight:
                                1.24,
                            }}
                          >
                            {clean(
                              project?.title
                            ) ||
                              (
                                isFR
                                  ? "Scénario sans titre"
                                  : "Untitled Screenplay"
                              )}
                          </h4>

                          <p
                            className="ns-card-meta"
                            style={{
                              margin:
                                "0 0 12px",
                              fontSize:
                                13.5,
                            }}
                          >
                            {isFR
                              ? "Mis à jour"
                              : "Updated"}:{" "}
                            {formatDate(
                              project?.updatedAt,
                              lang
                            )}
                          </p>

                          {isEditing ? (
                            <form
                              onSubmit={
                                saveEditedScreenplayProject
                              }
                              style={{
                                display:
                                  "grid",
                                gap: 10,
                                marginTop:
                                  2,
                              }}
                            >
                              <label
                                style={{
                                  display:
                                    "grid",
                                  gap: 5,
                                  fontSize:
                                    12.5,
                                  fontWeight:
                                    800,
                                }}
                              >
                                {isFR
                                  ? "Titre du scénario"
                                  : "Screenplay title"}

                                <input
                                  className="ns-input"
                                  value={
                                    screenplayEditTitle
                                  }
                                  disabled={
                                    screenplayProjectActionId ===
                                    id
                                  }
                                  onChange={(
                                    event
                                  ) => {
                                    setScreenplayEditTitle(
                                      event
                                        .target
                                        .value
                                    );
                                    setScreenplayEditError(
                                      ""
                                    );
                                  }}
                                />
                              </label>

                              <label
                                style={{
                                  display:
                                    "grid",
                                  gap: 5,
                                  fontSize:
                                    12.5,
                                  fontWeight:
                                    800,
                                }}
                              >
                                {isFR
                                  ? "Langue principale"
                                  : "Primary language"}

                                <select
                                  className="ns-input"
                                  value={
                                    screenplayEditPrimaryLanguage
                                  }
                                  disabled={
                                    screenplayProjectActionId ===
                                    id
                                  }
                                  onChange={(
                                    event
                                  ) => {
                                    setScreenplayEditPrimaryLanguage(
                                      event
                                        .target
                                        .value
                                    );
                                    setScreenplayEditError(
                                      ""
                                    );
                                  }}
                                >
                                  <option
                                    value="en"
                                  >
                                    {isFR
                                      ? "Anglais"
                                      : "English"}
                                  </option>

                                  <option
                                    value="fr"
                                  >
                                    {isFR
                                      ? "Français"
                                      : "French"}
                                  </option>

                                  <option
                                    value="bilingual"
                                  >
                                    {isFR
                                      ? "Bilingue"
                                      : "Bilingual"}
                                  </option>
                                </select>
                              </label>

                              {screenplayEditError ? (
                                <div
                                  role="alert"
                                  style={{
                                    color:
                                      "#b91c1c",
                                    fontSize:
                                      12.5,
                                    fontWeight:
                                      700,
                                  }}
                                >
                                  {
                                    screenplayEditError
                                  }
                                </div>
                              ) : null}

                              <div
                                style={{
                                  display:
                                    "flex",
                                  gap: 8,
                                  flexWrap:
                                    "wrap",
                                }}
                              >
                                <button
                                  className="ns-button ns-button-primary"
                                  type="submit"
                                  disabled={
                                    screenplayProjectActionId ===
                                    id ||
                                    !clean(
                                      screenplayEditTitle
                                    )
                                  }
                                >
                                  {screenplayProjectActionId ===
                                  id
                                    ? (
                                        isFR
                                          ? "Enregistrement…"
                                          : "Saving…"
                                      )
                                    : (
                                        isFR
                                          ? "Enregistrer"
                                          : "Save Changes"
                                      )}
                                </button>

                                <button
                                  className="ns-button ns-button-outline"
                                  type="button"
                                  disabled={
                                    screenplayProjectActionId ===
                                    id
                                  }
                                  onClick={
                                    cancelEditScreenplayProject
                                  }
                                >
                                  {isFR
                                    ? "Annuler"
                                    : "Cancel"}
                                </button>
                              </div>
                            </form>
                          ) : (
                            <div
                              style={{
                                display:
                                  "flex",
                                gap: 8,
                                flexWrap:
                                  "wrap",
                                marginTop:
                                  "auto",
                                paddingTop:
                                  2,
                              }}
                            >
                            <button
                              className="ns-button ns-button-primary"
                              type="button"
                              disabled={!id}
                              onClick={() =>
                                openScreenplayProject(
                                  project
                                )
                              }
                            >
                              {isFR
                                ? "Ouvrir le projet"
                                : "Open Project"}
                            </button>

                            {project?.access
                              ?.canManageProject ===
                            true ? (
                              <button
                                className="ns-button ns-button-outline"
                                type="button"
                                disabled={
                                  !id ||
                                  Boolean(
                                    screenplayProjectActionId
                                  )
                                }
                                onClick={() =>
                                  startEditScreenplayProject(
                                    project
                                  )
                                }
                              >
                                {isFR
                                  ? "Modifier"
                                  : "Edit"}
                              </button>
                            ) : null}

                            {project?.access
                              ?.canManageProject ===
                            true ? (
                              <button
                                className="ns-button ns-button-outline"
                                type="button"
                                disabled={
                                  !id ||
                                  screenplayProjectActionId ===
                                    id
                                }
                                onClick={() =>
                                  requestArchiveScreenplayProject(
                                    project
                                  )
                                }
                              >
                                {isFR
                                  ? "Archiver"
                                  : "Archive"}
                              </button>
                            ) : null}
                          </div>
                          )}
                        </article>
                      );
                    }
                  )}
                </div>
              )}

              {pendingScreenplayArchiveProject ? (
                <form
                  role="alertdialog"
                  aria-modal="true"
                  aria-labelledby="screenplay-archive-title"
                  aria-describedby="screenplay-archive-description"
                  onSubmit={(event) => {
                    event.preventDefault();
                    confirmArchiveScreenplayProject();
                  }}
                  className="ns-card"
                  style={{
                    marginTop: 16,
                    padding: 18,
                  }}
                >
                  <h4
                    id="screenplay-archive-title"
                    className="ns-card-title"
                    style={{
                      margin:
                        "0 0 8px",
                    }}
                  >
                    {isFR
                      ? "Archiver ce projet ?"
                      : "Archive This Project?"}
                  </h4>

                  <p
                    id="screenplay-archive-description"
                    className="ns-card-meta"
                    style={{
                      margin:
                        "0 0 14px",
                    }}
                  >
                    {isFR
                      ? `« ${clean(
                          pendingScreenplayArchiveProject
                            ?.title
                        ) || "Ce scénario"} » sera retiré de votre liste active. Son contenu sera conservé et vous pourrez le restaurer plus tard.`
                      : `“${clean(
                          pendingScreenplayArchiveProject
                            ?.title
                        ) || "This screenplay"}” will be removed from your active list. Its content will be kept, and you can restore it later.`}
                  </p>

                  <div
                    style={{
                      display:
                        "flex",
                      gap: 8,
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={
                        Boolean(
                          screenplayProjectActionId
                        )
                      }
                      onClick={
                        cancelArchiveScreenplayProject
                      }
                    >
                      {isFR
                        ? "Annuler"
                        : "Cancel"}
                    </button>

                    <button
                      className="ns-button ns-button-primary"
                      type="submit"
                      disabled={
                        !clean(
                          pendingScreenplayArchiveProject
                            ?.id
                        ) ||
                        Boolean(
                          screenplayProjectActionId
                        )
                      }
                    >
                      {screenplayProjectActionId
                        ? (
                            isFR
                              ? "Archivage…"
                              : "Archiving…"
                          )
                        : (
                            isFR
                              ? "Archiver le projet"
                              : "Archive Project"
                          )}
                    </button>
                  </div>
                </form>
              ) : null}

              {archivedScreenplayProjects.length >
              0 ? (
                <section
                  style={{
                    marginTop: 18,
                  }}
                >
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() =>
                      setShowArchivedScreenplayProjects(
                        (current) =>
                          !current
                      )
                    }
                  >
                    {showArchivedScreenplayProjects
                      ? (
                          isFR
                            ? "Masquer les projets archivés"
                            : "Hide Archived Projects"
                        )
                      : (
                          isFR
                            ? `Projets archivés (${archivedScreenplayProjects.length})`
                            : `Archived Projects (${archivedScreenplayProjects.length})`
                        )}
                  </button>

                  {showArchivedScreenplayProjects ? (
                    <div
                      style={{
                        display:
                          "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(250px, 305px))",
                        gap: 14,
                        marginTop: 12,
                        alignItems:
                          "stretch",
                        justifyContent:
                          "start",
                      }}
                    >
                      {archivedScreenplayProjects.map(
                        (project) => {
                          const id =
                            clean(
                              project?.id
                            );

                          const working =
                            screenplayProjectActionId ===
                            id;

                          return (
                            <article
                              key={id}
                              className="ns-card"
                              style={{
                                padding: 16,
                                borderRadius:
                                  16,
                                border:
                                  "1px solid rgba(100,116,139,0.18)",
                                borderTop:
                                  "3px solid rgba(100,116,139,0.48)",
                                boxShadow:
                                  "0 7px 18px rgba(15,23,42,0.05)",
                                minHeight:
                                  178,
                                height:
                                  "100%",
                                display:
                                  "flex",
                                flexDirection:
                                  "column",
                              }}
                            >
                              <div
                                style={{
                                  fontSize:
                                    10,
                                  fontWeight:
                                    900,
                                  letterSpacing:
                                    "0.1em",
                                  textTransform:
                                    "uppercase",
                                }}
                              >
                                {isFR
                                  ? "SCÉNARIO ARCHIVÉ"
                                  : "ARCHIVED SCREENPLAY"}
                              </div>

                              <h4
                                className="ns-card-title"
                                style={{
                                  margin:
                                    "7px 0 7px",
                                  fontSize:
                                    15.5,
                                  fontWeight:
                                    800,
                                  lineHeight:
                                    1.24,
                                }}
                              >
                                {clean(
                                  project?.title
                                ) ||
                                  (
                                    isFR
                                      ? "Scénario sans titre"
                                      : "Untitled Screenplay"
                                  )}
                              </h4>

                              <p
                                className="ns-card-meta"
                                style={{
                                  margin:
                                    "0 0 12px",
                                  fontSize:
                                    13.5,
                                }}
                              >
                                {isFR
                                  ? "Archivé"
                                  : "Archived"}:{" "}
                                {formatDate(
                                  project
                                    ?.archivedAt,
                                  lang
                                )}
                              </p>

                              <button
                                className="ns-button ns-button-primary"
                                type="button"
                                style={{
                                  alignSelf:
                                    "flex-start",
                                  marginTop:
                                    "auto",
                                }}
                                disabled={
                                  !id ||
                                  working
                                }
                                onClick={() =>
                                  restoreScreenplayProject(
                                    project
                                  )
                                }
                              >
                                {working
                                  ? (
                                      isFR
                                        ? "Restauration…"
                                        : "Restoring…"
                                    )
                                  : (
                                      isFR
                                        ? "Restaurer"
                                        : "Restore"
                                    )}
                              </button>
                            </article>
                          );
                        }
                      )}
                    </div>
                  ) : null}
                </section>
              ) : null}
            </section>
          </>
        )}
      </>
    );
  }

  function navigateTextbookStudioWorkspace(
    workspace
  ) {
    const navigate = () =>
      openTextbookWorkspace(
        workspace
      );

    if (
      textbookWorkspace ===
        "chapter-workspace" &&
      typeof textbookChapterLeaveGuard
        .current === "function"
    ) {
      textbookChapterLeaveGuard
        .current(navigate);
      return;
    }

    navigate();
  }


  function renderTextbookFoundation() {
    const active =
      activeTextbookProject;

    const canManageTextbookProject =
      currentTextbookProjectAccess
        ?.canManageProject ===
      true;

    const effectiveTextbookWorkspace =
      active &&
      !canManageTextbookProject &&
      ![
        "write-chapters",
        "chapter-workspace",
      ].includes(
        textbookWorkspace
      )
        ? "write-chapters"
        : textbookWorkspace;

    const canEditProjectPlan =
      currentTextbookProjectAccess
        ?.canEditProjectPlan ===
        true;

    const bookMapEnabled =
      currentTextbookProjectAccess
        ?.bookMapEnabled ===
        true;

    const chapterWorkspaceEnabled =
      currentTextbookProjectAccess
        ?.chapterWorkspaceEnabled ===
        true;

    const canEditChapterDraft =
      currentTextbookChapterWorkspaceData
        ?.access
        ?.canEditChapterDraft ===
        true;

    const canManageProposalRound =
      currentTextbookChapterWorkspaceData
        ?.access
        ?.canManageProposalRound ===
        true;

    const canSubmitProposal =
      currentTextbookChapterWorkspaceData
        ?.access
        ?.canSubmitProposal ===
        true;

    const proposalRoundBlocksCanon =
      currentTextbookChapterWorkspaceData
        ?.access
        ?.proposalRoundBlocksCanon ===
        true;

    if (
      active &&
      textbookDirectOpenLoading
    ) {
      return (
        <section
          className="ns-card ns-textbook-clarity-v1"
          data-textbook-direct-open-loading="true"
          role="status"
          style={{
            padding: "28px 30px",
            borderRadius: 20,
            border:
              "1px solid rgba(5,150,105,0.20)",
            background:
              "linear-gradient(135deg, rgba(240,253,248,0.98), rgba(255,253,247,0.98))",
          }}
        >
          <div
            style={{
              color: "#047857",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            {isFR
              ? "STUDIO MANUEL"
              : "TEXTBOOK STUDIO"}
          </div>

          <h3
            className="ns-card-title ns-textbook-academic-title"
            style={{
              margin: "7px 0 7px",
            }}
          >
            {clean(active?.title) ||
              (
                isFR
                  ? "Ouverture du manuel…"
                  : "Opening Textbook…"
              )}
          </h3>

          <div className="ns-card-meta">
            {isFR
              ? "Préparation de votre espace de travail…"
              : "Preparing your writing workspace…"}
          </div>
        </section>
      );
    }

    return (
      <div
        className="ns-textbook-clarity-v1"
        data-textbook-ui-hierarchy="true"
      >
        {active &&
        effectiveTextbookWorkspace !==
          "chapter-workspace" ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 10,
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                closeTextbookProject
              }
            >
              {isFR
                ? "Retour aux manuels"
                : "Back to Textbooks"}
            </button>
          </div>
        ) : null}

        {active ? (
          <>
            <TextbookWorkspaceNavigator
              currentWorkspace={
                [
                  "overview",
                  "project-plan",
                ].includes(effectiveTextbookWorkspace)
                  ? "overview"
                  : [
                      "chapter-workspace",
                      "figures-media",
                    ].includes(effectiveTextbookWorkspace)
                    ? "write-chapters"
                    : effectiveTextbookWorkspace
              }
              onNavigate={
                navigateTextbookStudioWorkspace
              }
              bookMapEnabled={bookMapEnabled}
              chapterWorkspaceEnabled={chapterWorkspaceEnabled}
              projectPlan={textbookProjectPlan}
              canManageProject={
                canManageTextbookProject
              }
              isFR={isFR}
            />

            {canManageTextbookProject &&
            clean(
              active?.writingMode
            ).toLowerCase() ===
              "collaborative" ? (
              <TextbookCollaborativeRequests
                api={api}
                projectId={
                  active?.id
                }
                isFR={isFR}
              />
            ) : null}

            {effectiveTextbookWorkspace ===
              "chapter-workspace" &&
            chapterWorkspaceEnabled &&
            clean(
              textbookChapterItemId
            ) ? (
              <TextbookChapterWorkspace
                key={
                  textbookChapterItemId
                }
                project={active}
                formatPlan={
                  textbookProjectPlan
                    ?.formatPlan ||
                  {}
                }
                chapter={
                  currentTextbookChapterWorkspaceData
                    ?.chapter ||
                  null
                }
                chapterDraft={
                  currentTextbookChapterWorkspaceData
                    ?.chapterDraft ||
                  null
                }
                proposalRound={
                  currentTextbookChapterWorkspaceData
                    ?.proposalRound ||
                  null
                }
                proposalRoundBlocksCanon={
                  proposalRoundBlocksCanon
                }
                canManageProposalRound={
                  canManageProposalRound
                }
                canSubmitProposal={
                  canSubmitProposal
                }
                myProposals={
                  currentTextbookChapterWorkspaceData
                    ?.myProposals ||
                  []
                }
                proposalReviewProposals={
                  currentTextbookChapterWorkspaceData
                    ?.proposalReviewProposals ||
                  []
                }
                figureAssets={
                  currentTextbookChapterWorkspaceData
                    ?.figureAssets ||
                  []
                }
                figureLoadError={
                  currentTextbookChapterWorkspaceData
                    ?.figureLoadError ||
                  ""
                }
                onUploadFigure={
                  uploadTextbookFigure
                }
                onCreateFigureLink={
                  createTextbookFigureLink
                }
                onUpdateFigure={
                  updateTextbookFigure
                }
                onLoadFigureFile={
                  api.getTextbookFigureFile
                }
                loading={
                  textbookChapterWorkspaceLoading
                }
                loadError={
                  textbookChapterWorkspaceError
                }
                onReload={() =>
                  loadTextbookChapterWorkspace(
                    active?.id,
                    textbookChapterItemId
                  )
                }
                onSave={
                  saveTextbookChapterDraft
                }
                onLoadVersionHistory={
                  api.getTextbookBookSnapshots
                }
                onPreviewVersion={
                  api.getTextbookBookSnapshotPreview
                }
                onOpenProposalRound={
                  canManageProposalRound
                    ? openTextbookChapterProposalRound
                    : undefined
                }
                onCloseProposalRound={
                  canManageProposalRound
                    ? closeTextbookChapterProposalRound
                    : undefined
                }
                onSubmitProposal={
                  canSubmitProposal
                    ? submitTextbookChapterProposal
                    : undefined
                }
                onDecideProposal={
                  canManageProposalRound
                    ? decideTextbookChapterProposal
                    : undefined
                }
                onBackToBookMap={() =>
                  openTextbookWorkspace(
                    "write-chapters"
                  )
                }
                onRegisterLeaveGuard={
                  (guard) => {
                    textbookChapterLeaveGuard
                      .current =
                      typeof guard ===
                        "function"
                        ? guard
                        : null;
                  }
                }
                canEdit={
                  canEditChapterDraft
                }
                isFR={isFR}
              />
            ) : effectiveTextbookWorkspace ===
                "figures-media" &&
              chapterWorkspaceEnabled ? (
              <TextbookFiguresMedia
                project={active}
                projectPlan={textbookProjectPlan}
                onLoadChapterDraft={
                  api.getTextbookChapterDraft
                }
                onLoadFigureAssets={
                  api.getTextbookFigureAssets
                }
                onLoadFigureFile={
                  api.getTextbookFigureFile
                }
                onUpdateFigure={
                  updateTextbookFigureFromMedia
                }
                onOpenChapter={
                  openTextbookChapterWorkspace
                }
                onBackToWriting={() =>
                  openTextbookWorkspace(
                    "write-chapters"
                  )
                }
                canEdit={
                  canEditChapterDraft
                }
                isFR={isFR}
              />
            ) : effectiveTextbookWorkspace ===
                "publication-proof" &&
              chapterWorkspaceEnabled ? (
              <TextbookPublicationProof
                project={active}
                projectPlan={
                  textbookProjectPlan
                }
                onLoadChapterDraft={
                  api.getTextbookChapterDraft
                }
                onLoadFigureAssets={
                  api.getTextbookFigureAssets
                }
                onLoadFigureFile={
                  api.getTextbookFigureFile
                }
                onLoadPublicationAssets={
                  api.getTextbookPublicationAssets
                }
                onLoadPublicationAssetFile={
                  api.getTextbookPublicationAssetFile
                }
                onOpenChapter={
                  openTextbookChapterWorkspace
                }
                onOpenBookStructure={() =>
                  openTextbookWorkspace(
                    "book-map"
                  )
                }
                onOpenPublicationPreparation={() =>
                  openTextbookWorkspace(
                    "publication-preparation"
                  )
                }
                onReturn={() =>
                  openTextbookWorkspace(
                    "review-your-book"
                  )
                }
                isFR={isFR}
              />
            ) : effectiveTextbookWorkspace ===
                "publication-preparation" &&
              chapterWorkspaceEnabled ? (
              <TextbookPublicationPreparation
                project={active}
                projectPlan={textbookProjectPlan}
                onSave={saveTextbookPublicationPlan}
                onOpenReview={() =>
                  openTextbookWorkspace(
                    "review-your-book"
                  )
                }
                canEdit={canEditProjectPlan}
                isFR={isFR}
              />
            ) : effectiveTextbookWorkspace ===
                "review-your-book" &&
              chapterWorkspaceEnabled ? (
              <TextbookBookReview
                project={active}
                projectPlan={textbookProjectPlan}
                onLoadChapterDraft={
                  api.getTextbookChapterDraft
                }
                onLockPublicationManuscript={
                  lockTextbookPublicationManuscript
                }
                canLockPublicationManuscript={
                  canEditProjectPlan
                }
                onLoadFigureAssets={
                  api.getTextbookFigureAssets
                }
                onLoadFigureFile={
                  api.getTextbookFigureFile
                }
                onOpenChapter={
                  openTextbookChapterWorkspace
                }
                onBackToWriting={() =>
                  openTextbookWorkspace(
                    "write-chapters"
                  )
                }
                isFR={isFR}
              />
            ) : effectiveTextbookWorkspace ===
                "write-chapters" &&
              chapterWorkspaceEnabled ? (
              <TextbookWriteChapters
                project={active}
                projectPlan={textbookProjectPlan}
                onOpenChapter={openTextbookChapterWorkspace}
                onOpenBookStructure={() => openTextbookWorkspace("book-map")}
                onLoadChapterDraft={
                  api.getTextbookChapterDraft
                }
                canEdit={canEditProjectPlan}
                isFR={isFR}
              />
            ) : effectiveTextbookWorkspace ===
                "book-map" &&
              bookMapEnabled ? (
              <TextbookBookMap
                project={active}
                projectPlan={
                  textbookProjectPlan
                }
                loading={
                  textbookProjectPlanLoading
                }
                loadError={
                  textbookProjectPlanError
                }
                onReload={() =>
                  loadTextbookProjectDetail(
                    active?.id
                  )
                }
                onSave={
                  saveTextbookBookMap
                }
                        onPreviewManuscriptImport={
                          api.previewManuscriptImport
                        }
                onOpenChapter={
                  chapterWorkspaceEnabled
                    ? openTextbookChapterWorkspace
                    : undefined
                }
                canEdit={
                  canEditProjectPlan
                }
                isFR={isFR}
                      onApplyManuscriptImport={
          applyTextbookManuscriptImport
        }
/>
            ) : effectiveTextbookWorkspace === "overview" ? (
              <TextbookBlueprintOverview
                project={active}
                projectPlan={textbookProjectPlan}
                onOpenProjectPlan={() => openTextbookWorkspace("project-plan")}
                onOpenBookStructure={() => openTextbookWorkspace("book-map")}
                onOpenWrite={() => openTextbookWorkspace("write-chapters")}
                bookMapEnabled={bookMapEnabled}
                chapterWorkspaceEnabled={chapterWorkspaceEnabled}
                isFR={isFR}
              />
            ) : (
              <TextbookProjectOverview
                project={active}
                projectPlan={
                  textbookProjectPlan
                }
                loading={
                  textbookProjectPlanLoading
                }
                loadError={
                  textbookProjectPlanError
                }
                onReload={() =>
                  loadTextbookProjectDetail(
                    active?.id
                  )
                }
                onSave={
                  saveTextbookProjectOverview
                }
                onOpenBookStructure={() =>
                  openTextbookWorkspace(
                    "book-map"
                  )
                }
                canEdit={
                  canEditProjectPlan
                }
                isFR={isFR}
                writingModeLabel={
                  textbookWritingModeLabel(
                    active?.writingMode,
                    lang
                  )
                }
                updatedLabel={
                  formatDate(
                    textbookProjectPlan
                      ?.updatedAt ||
                      active?.updatedAt,
                    lang
                  )
                }
              />
            )}
          </>
        ) : (
          <>
            <div
              data-textbook-category-actions="true"
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent:
                  "space-between",
                gap: 16,
                flexWrap: "wrap",
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  maxWidth: 760,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing:
                      "0.12em",
                    textTransform:
                      "uppercase",
                    color: "#047857",
                  }}
                >
                  {isFR
                    ? "STUDIO MANUEL SCOLAIRE"
                    : "TEXTBOOK STUDIO"}
                </div>

                <h2
                  className="ns-section-title"
                  style={{
                    margin: "5px 0 8px",
                  }}
                >
                  {isFR
                    ? "Vos projets de manuel"
                    : "Your Textbook Projects"}
                </h2>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {isFR
                    ? "Créez ou reprenez le manuel que vous souhaitez développer jusqu’à une édition publiable."
                    : "Create or reopen the textbook you want to develop into a publication-ready book."}
                </p>
              </div>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  selectProjectCategory("")
                }
              >
                {ui.backToTypes}
              </button>
            </div>

            {canCreateProjects ? (
              <form
                className="ns-card"
                onSubmit={
                  createTextbookProject
                }
                style={{
                  padding: 20,
                  borderRadius: 20,
                  marginBottom: 20,
                  border:
                    "1.5px solid rgba(5,150,105,0.22)",
                }}
              >
                <div
                  style={{
                    maxWidth: 760,
                  }}
                >
                  <h3
                    className="ns-card-title"
                    style={{
                      margin: "0 0 6px",
                    }}
                  >
                    {isFR
                      ? "Commencer un nouveau manuel"
                      : "Start a New Textbook"}
                  </h3>

                  <p
                    className="ns-card-meta"
                    style={{
                      margin: "0 0 16px",
                      lineHeight: 1.55,
                    }}
                  >
                    {isFR
                      ? "Trois choix suffisent pour commencer. Les détails pédagogiques viennent ensuite dans la vue d’ensemble du projet."
                      : "Three choices are enough to begin. The educational details come next in Project Overview."}
                  </p>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 14,
                  }}
                >
                  <label
                    style={{
                      display: "grid",
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 800,
                      }}
                    >
                      {isFR
                        ? "Titre du manuel"
                        : "Textbook title"}
                    </span>

                    <input
                      className="ns-input"
                      value={
                        textbookTitle
                      }
                      disabled={
                        textbookCreating
                      }
                      onChange={(
                        event
                      ) => {
                        setTextbookTitle(
                          event.target.value
                        );
                        setTextbookProjectsError(
                          ""
                        );
                      }}
                      placeholder={
                        isFR
                          ? "Titre du livre"
                          : "Book title"
                      }
                      autoFocus
                    />
                  </label>

                  <label
                    style={{
                      display: "grid",
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 800,
                      }}
                    >
                      {isFR
                        ? "Langue principale"
                        : "Primary language"}
                    </span>

                    <select
                      className="ns-input"
                      value={
                        textbookPrimaryLanguage
                      }
                      disabled={
                        textbookCreating
                      }
                      onChange={(
                        event
                      ) =>
                        setTextbookPrimaryLanguage(
                          event.target.value
                        )
                      }
                    >
                      <option value="en">
                        {isFR
                          ? "Anglais"
                          : "English"}
                      </option>

                      <option value="fr">
                        {isFR
                          ? "Français"
                          : "French"}
                      </option>

                      <option
                        value="bilingual"
                      >
                        {isFR
                          ? "Bilingue"
                          : "Bilingual"}
                      </option>
                    </select>
                  </label>

                  <label
                    style={{
                      display: "grid",
                      gap: 7,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 800,
                      }}
                    >
                      {isFR
                        ? "Mode d’écriture"
                        : "Writing mode"}
                    </span>

                    <select
                      className="ns-input"
                      value={
                        textbookWritingMode
                      }
                      disabled={
                        textbookCreating
                      }
                      onChange={(
                        event
                      ) => {
                        const nextMode =
                          event.target.value;

                        setTextbookWritingMode(
                          nextMode
                        );

                        setTextbookInviteEmailsError(
                          ""
                        );

                        if (
                          !textbookInvitationWritingMode(
                            nextMode
                          )
                        ) {
                          setTextbookInviteEmailsText(
                            ""
                          );
                        }
                      }}
                    >
                      <option value="solo">
                        Solo
                      </option>

                      <option
                        value="collaborative"
                      >
                        {isFR
                          ? "Collaboratif"
                          : "Collaborative"}
                      </option>

                      <option value="group_closed">
                        {isFR
                          ? "Écriture en groupe"
                          : "Group Writing"}
                      </option>
                    </select>
                  </label>
                </div>

                <div
                  className="ns-card-meta"
                  style={{
                    marginTop: 10,
                    lineHeight: 1.5,
                  }}
                >
                  {textbookWritingMode ===
                  "collaborative"
                    ? (
                        isFR
                          ? "Les Writers enregistrés peuvent demander à collaborer. Le propriétaire approuve ou refuse chaque demande ; le Canon reste sous son contrôle."
                          : "Registered Writers can request to collaborate. The Owner evaluates and approves or rejects every request; Canon remains Owner-controlled."
                      )
                    : textbookInvitationWritingMode(
                        textbookWritingMode
                      )
                    ? (
                        isFR
                          ? "L’écriture en groupe est strictement sur invitation du propriétaire. Les membres qui acceptent peuvent lire le Canon autorisé, mais ne peuvent proposer du contenu que lorsque le propriétaire ouvre un Proposal Round."
                          : "Group Writing is strictly by Owner invitation. Accepted members may read the available Canon, but may submit proposals only when the Owner opens a Proposal Round."
                      )
                    : (
                        isFR
                          ? "Vous pouvez commencer seul et développer le livre à votre rythme."
                          : "You can begin alone and develop the book at your own pace."
                      )}
                </div>

                {textbookInvitationWritingMode(
                  textbookWritingMode
                ) ? (
                  <label
                    style={{
                      display: "grid",
                      gap: 7,
                      maxWidth: 620,
                      marginTop: 14,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 800,
                      }}
                    >
                      {isFR
                        ? "E-mails des invités"
                        : "Invitee emails"}
                    </span>

                    <textarea
                      className="ns-input"
                      value={
                        textbookInviteEmailsText
                      }
                      rows={3}
                      disabled={
                        textbookCreating
                      }
                      onChange={(event) => {
                        setTextbookInviteEmailsText(
                          event.target.value
                        );

                        setTextbookInviteEmailsError(
                          ""
                        );
                      }}
                      placeholder={
                        isFR
                          ? "ex. auteur@exemple.com, collegue@exemple.com"
                          : "e.g. author@example.com, colleague@example.com"
                      }
                    />

                    <span
                      className="ns-card-meta"
                      style={{
                        lineHeight: 1.45,
                      }}
                    >
                      {isFR
                        ? "Ajoutez une ou plusieurs adresses e-mail. Les invités du groupe peuvent accepter ou refuser l’invitation."
                        : "Add one or more invitee email addresses. Group invitees receive an invitation and may accept or decline it."}
                    </span>

                    {textbookInviteEmailsError ? (
                      <span
                        role="alert"
                        style={{
                          color: "#b91c1c",
                          fontWeight: 800,
                          fontSize: 13,
                        }}
                      >
                        {
                          textbookInviteEmailsError
                        }
                      </span>
                    ) : null}
                  </label>
                ) : null}

                {textbookProjectsError ? (
                  <div
                    role="alert"
                    style={{
                      color: "#b91c1c",
                      fontWeight: 700,
                      marginTop: 12,
                    }}
                  >
                    {
                      textbookProjectsError
                    }
                  </div>
                ) : null}

                <div
                  style={{
                    marginTop: 16,
                  }}
                >
                  <button
                    className="ns-button ns-button-primary"
                    type="submit"
                    disabled={
                      textbookCreating ||
                      !clean(
                        textbookTitle
                      )
                    }
                    aria-busy={
                      textbookCreating
                    }
                  >
                    {textbookCreating
                      ? (
                          isFR
                            ? "Création…"
                            : "Creating…"
                        )
                      : (
                          isFR
                            ? "Créer le projet"
                            : "Create Textbook Project"
                        )}
                  </button>
                </div>
              </form>
            ) : null}

            <TextbookCollaborativeDiscovery
              api={api}
              isFR={isFR}
            />

            <section>
              <h3
                className="ns-section-title"
                style={{
                  margin: "0 0 12px",
                }}
              >
                {isFR
                  ? "Mes manuels"
                  : "My Textbooks"}
              </h3>

              {textbookProjectsLoading ? (
                <div
                  className="ns-card"
                  style={{
                    padding: 18,
                  }}
                >
                  {isFR
                    ? "Chargement des projets…"
                    : "Loading projects…"}
                </div>
              ) : textbookProjectsError &&
                textbookProjects.length ===
                  0 ? (
                <div
                  className="ns-card"
                  style={{
                    padding: 18,
                  }}
                >
                  <p
                    style={{
                      marginTop: 0,
                      color: "#b91c1c",
                      fontWeight: 800,
                    }}
                  >
                    {
                      textbookProjectsError
                    }
                  </p>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={
                      loadTextbookProjects
                    }
                  >
                    {ui.retry}
                  </button>
                </div>
              ) : textbookProjects.length ===
                0 ? (
                <div
                  className="ns-card"
                  style={{
                    padding: 20,
                  }}
                >
                  <strong>
                    {canCreateProjects
                      ? (
                          isFR
                            ? "Aucun manuel enregistré pour le moment."
                            : "No saved Textbook yet."
                        )
                      : (
                          isFR
                            ? "Aucun projet de manuel accessible."
                            : "No accessible Textbook project."
                        )}
                  </strong>

                  <p
                    className="ns-card-meta"
                    style={{
                      marginBottom: 0,
                    }}
                  >
                    {canCreateProjects
                      ? (
                          isFR
                            ? "Créez votre premier projet ci-dessus. Vous passerez ensuite directement à la vue d’ensemble."
                            : "Create your first project above. You will then move directly into Project Overview."
                        )
                      : (
                          isFR
                            ? "Un accès Writer est requis pour créer un nouveau manuel."
                            : "Writer access is required to create a new Textbook."
                        )}
                  </p>
                </div>
              ) : (
                <div
                  className="ns-project-hub-textbook-grid"
                >
                  {textbookProjects.map(
                    (project) => {
                      const id =
                        clean(
                          project?.id
                        );

                      const ownerName =
                        projectOwnerDisplayName(
                          project
                        );

                      const avatarUrl =
                        clean(
                          project
                            ?.owner
                            ?.avatarUrl
                        );

                      const proposalSupported =
                        project
                          ?.proposalStatus
                          ?.supported ===
                        true;

                      const proposalOpen =
                        project
                          ?.proposalStatus
                          ?.open ===
                        true;

                      const relationship =
                        clean(
                          project
                            ?.access
                            ?.relationship
                        ).toLowerCase();

                      let activeCard =
                        false;

                      if (
                        id &&
                        typeof window !==
                          "undefined"
                      ) {
                        try {
                          activeCard =
                            clean(
                              window
                                .localStorage
                                .getItem(
                                  "nextscenes.textbook.activeProjectId"
                                )
                            ) === id;
                        } catch {
                          activeCard =
                            false;
                        }
                      }

                      const archiveTargetCard =
                        Boolean(id) &&
                        clean(
                          pendingTextbookArchiveProject
                            ?.id
                        ) === id;

                      return (
                        <article
                          key={id}
                          className={
                            "ns-card ns-project-hub-textbook-card " +
                            (activeCard
                              ? "is-active "
                              : "") +
                            (archiveTargetCard
                              ? "is-archive-target"
                              : "")
                          }
                          data-textbook-project-card="true"
                          data-textbook-project-active={
                            activeCard
                              ? "true"
                              : "false"
                          }
                          data-textbook-archive-target={
                            archiveTargetCard
                              ? "true"
                              : "false"
                          }
                          style={
                            archiveTargetCard
                              ? {
                                  background:
                                    "#fffaf0",
                                  boxShadow:
                                    "0 0 0 3px rgba(217, 154, 36, 0.38), 0 12px 28px rgba(15, 23, 42, 0.10)",
                                }
                              : undefined
                          }
                        >
                          <div
                            className="ns-project-hub-textbook-topline"
                            aria-hidden="true"
                          />

                          <div className="ns-project-hub-textbook-main">
                            <div
                              className="ns-project-hub-textbook-identity"
                              aria-label={
                                isFR
                                  ? "Projet de manuel"
                                  : "Textbook project"
                              }
                            >
                              <div
                                className="ns-project-hub-textbook-identity-icon"
                                aria-hidden="true"
                              >
                                <ProjectTypeIcon type="textbook" />
                              </div>

                              <div className="ns-project-hub-textbook-identity-kicker">
                                {isFR ? "MANUEL" : "TEXTBOOK"}
                              </div>

                              <div className="ns-project-hub-textbook-identity-foot">
                                {isFR
                                  ? "Projet académique"
                                  : "Academic project"}
                              </div>
                            </div>

                            <div className="ns-project-hub-textbook-copy">
                              <div className="ns-project-hub-textbook-heading-row">
                                <h4 className="ns-project-hub-textbook-title">
                                  {clean(project?.title) ||
                                    (isFR ? "Manuel sans titre" : "Untitled Textbook")}
                                </h4>

                                {activeCard ? (
                                  <span
                                    className="ns-project-hub-textbook-active-badge"
                                    aria-label={
                                      isFR
                                        ? "Projet actif"
                                        : "Active project"
                                    }
                                  >
                                    {isFR
                                      ? "Actif"
                                      : "Active"}
                                  </span>
                                ) : null}

                                {proposalSupported ? (
                                  <span
                                    className={
                                      "ns-project-hub-textbook-status " +
                                      (proposalOpen ? "is-open" : "is-closed")
                                    }
                                    data-textbook-project-proposal-status={
                                      proposalOpen ? "open" : "closed"
                                    }
                                  >
                                    {proposalOpen
                                      ? (isFR ? "Propositions ouvertes" : "Proposals Open")
                                      : (isFR ? "Propositions fermées" : "Proposals Closed")}
                                  </span>
                                ) : null}
                              </div>

                              <div
                                className="ns-project-hub-textbook-byline"
                                data-textbook-project-owner="true"
                              >
                                {avatarUrl ? (
                                  <img
                                    className="ns-project-hub-textbook-avatar"
                                    src={avatarUrl}
                                    alt=""
                                  />
                                ) : (
                                  <div
                                    className="ns-project-hub-textbook-avatar ns-project-hub-textbook-avatar-text"
                                    aria-hidden="true"
                                  >
                                    {projectOwnerInitials(project)}
                                  </div>
                                )}

                                <div className="ns-project-hub-textbook-byline-copy">
                                  <span className="ns-project-hub-textbook-byline-label">
                                    {isFR ? "Par" : "By"}
                                  </span>
                                  <strong
                                    className="ns-project-hub-textbook-author"
                                    title={ownerName}
                                  >
                                    {ownerName}
                                  </strong>
                                </div>
                              </div>

                              <div className="ns-project-hub-textbook-meta-line">
                                <span>
                                  {textbookWritingModeLabel(project?.writingMode, lang)}
                                </span>
                                <span aria-hidden="true">•</span>
                                <span>
                                  {projectLanguageLabel(project?.primaryLanguage, lang)}
                                </span>
                              </div>

                              <div className="ns-project-hub-textbook-chip-row">
                                <span
                                  className={
                                    "ns-project-hub-textbook-chip " +
                                    (relationship === "owner"
                                      ? "is-owner"
                                      : "is-collaborator")
                                  }
                                >
                                  {relationship === "owner"
                                    ? (isFR ? "Propriétaire" : "Owner")
                                    : (isFR ? "Collaborateur" : "Collaborator")}
                                </span>

                                <span className="ns-project-hub-textbook-chip is-development">
                                  {isFR ? "En développement" : "In Development"}
                                </span>
                              </div>

                              <div className="ns-project-hub-textbook-dates">
                                <div className="ns-card-meta">
                                  {isFR ? "Mis à jour" : "Last updated"}:{" "}
                                  <strong>
                                    {formatDate(project?.updatedAt, lang)}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="ns-project-hub-textbook-actions">
                            <button
                              className="ns-button ns-button-primary"
                              type="button"
                              disabled={
                                !id ||
                                textbookProjectActionId ===
                                  id
                              }
                              onClick={() => openTextbookProject(project)}
                            >
                              {isFR ? "Ouvrir le manuel" : "Open Textbook"}
                            </button>

                            {project?.access
                              ?.canManageProject ===
                            true ? (
                              <button
                                className="ns-button ns-button-outline"
                                type="button"
                                data-textbook-archive-project="true"
                                aria-pressed={
                                  archiveTargetCard
                                }
                                disabled={
                                  !id ||
                                  textbookProjectActionId ===
                                    id
                                }
                                style={
                                  archiveTargetCard
                                    ? {
                                        borderColor:
                                          "#d99a24",
                                        background:
                                          "#fff1cf",
                                        color:
                                          "#704200",
                                      }
                                    : undefined
                                }
                                onClick={() =>
                                  requestArchiveTextbookProject(
                                    project
                                  )
                                }
                              >
                                {isFR
                                  ? "Archiver"
                                  : "Archive"}
                              </button>
                            ) : null}
                          </div>
                        </article>
                      );
                    }
                  )}
                </div>
              )}

              {pendingTextbookArchiveProject ? (
                <form
                  role="alertdialog"
                  aria-modal="true"
                  aria-labelledby="textbook-archive-title"
                  aria-describedby="textbook-archive-description"
                  data-textbook-archive-confirmation="true"
                  onSubmit={(event) => {
                    event.preventDefault();
                    confirmArchiveTextbookProject();
                  }}
                  className="ns-card"
                  style={{
                    marginTop: 16,
                    padding: 18,
                    background: "#fff6df",
                    border:
                      "1px solid #d99a24",
                    boxShadow:
                      "0 10px 30px rgba(122, 74, 0, 0.16)",
                  }}
                >
                  <h4
                    id="textbook-archive-title"
                    className="ns-card-title"
                    style={{
                      margin: "0 0 7px",
                      color: "#7a4a00",
                    }}
                  >
                    {isFR
                      ? `Archiver « ${
                          clean(
                            pendingTextbookArchiveProject
                              ?.title
                          ) || "ce manuel"
                        } » ?`
                      : `Archive “${
                          clean(
                            pendingTextbookArchiveProject
                              ?.title
                          ) || "this textbook"
                        }”?`}
                  </h4>

                  <p
                    id="textbook-archive-description"
                    className="ns-card-meta"
                    style={{
                      margin: "0 0 14px",
                      color: "#6b4b1f",
                    }}
                  >
                    {isFR
                      ? "Il quittera votre liste active. Rien ne sera supprimé."
                      : "It will leave your active list. Nothing will be deleted."}
                  </p>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      className="ns-button ns-button-outline"
                      type="button"
                      disabled={
                        Boolean(
                          textbookProjectActionId
                        )
                      }
                      onClick={
                        cancelArchiveTextbookProject
                      }
                    >
                      {isFR
                        ? "Annuler"
                        : "Cancel"}
                    </button>

                    <button
                      className="ns-button ns-button-primary"
                      type="submit"
                      disabled={
                        !clean(
                          pendingTextbookArchiveProject
                            ?.id
                        ) ||
                        Boolean(
                          textbookProjectActionId
                        )
                      }
                    >
                      {textbookProjectActionId
                        ? (
                            isFR
                              ? "Archivage…"
                              : "Archiving…"
                          )
                        : (
                            isFR
                              ? "Archiver"
                              : "Archive"
                          )}
                    </button>
                  </div>
                </form>
              ) : null}

              {archivedTextbookProjects.length >
              0 ? (
                <section
                  data-textbook-archived-projects="true"
                  style={{
                    marginTop: 18,
                  }}
                >
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() =>
                      setShowArchivedTextbookProjects(
                        (current) =>
                          !current
                      )
                    }
                  >
                    {showArchivedTextbookProjects
                      ? (
                          isFR
                            ? "Masquer les archives"
                            : "Hide Archived"
                        )
                      : (
                          isFR
                            ? `Archivés (${archivedTextbookProjects.length})`
                            : `Archived (${archivedTextbookProjects.length})`
                        )}
                  </button>

                  {showArchivedTextbookProjects ? (
                    <div
                      style={{
                        display: "grid",
                        gap: 10,
                        marginTop: 12,
                        maxWidth: 760,
                      }}
                    >
                      {archivedTextbookProjects.map(
                        (project) => {
                          const id =
                            clean(
                              project?.id
                            );

                          const working =
                            textbookProjectActionId ===
                            id;

                          return (
                            <div
                              key={id}
                              className="ns-card"
                              data-textbook-archived-project="true"
                              style={{
                                padding:
                                  "12px 14px",
                                borderRadius:
                                  14,
                                display:
                                  "flex",
                                alignItems:
                                  "center",
                                justifyContent:
                                  "space-between",
                                gap: 14,
                                flexWrap:
                                  "wrap",
                              }}
                            >
                              <div
                                style={{
                                  minWidth: 0,
                                }}
                              >
                                <strong>
                                  {clean(
                                    project
                                      ?.title
                                  ) ||
                                    (
                                      isFR
                                        ? "Manuel sans titre"
                                        : "Untitled Textbook"
                                    )}
                                </strong>

                                <div
                                  className="ns-card-meta"
                                  style={{
                                    marginTop:
                                      3,
                                  }}
                                >
                                  {isFR
                                    ? "Archivé"
                                    : "Archived"}{" "}
                                  {formatDate(
                                    project
                                      ?.archivedAt,
                                    lang
                                  )}
                                </div>
                              </div>

                              <button
                                className="ns-button ns-button-primary"
                                type="button"
                                data-textbook-restore-project="true"
                                disabled={
                                  !id ||
                                  working
                                }
                                onClick={() =>
                                  restoreTextbookProject(
                                    project
                                  )
                                }
                              >
                                {working
                                  ? (
                                      isFR
                                        ? "Restauration…"
                                        : "Restoring…"
                                    )
                                  : (
                                      isFR
                                        ? "Restaurer"
                                        : "Restore"
                                    )}
                              </button>
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : null}
                </section>
              ) : null}
            </section>
          </>
        )}
      </div>
    );
  }


  async function createNovelStoryProject(event) {
    event?.preventDefault?.();

    const title = clean(novelStoryTitle);
    const forum = clean(novelStoryForum);
    const description = clean(novelStoryDescription);

    if (!title) {
      setNovelStoryCreateError(
        isFR
          ? "Saisissez d’abord le titre du Roman / Histoire."
          : "Enter the Novel / Story title first."
      );
      return;
    }

    if (!forum) {
      setNovelStoryCreateError(
        isFR
          ? "Choisissez un genre."
          : "Choose a Genre."
      );
      return;
    }

    if (!description) {
      setNovelStoryCreateError(
        isFR
          ? "Ajoutez une brève description."
          : "Add a brief Description."
      );
      return;
    }

    const isGroup =
      clean(novelStoryWritingMode)
        .toLowerCase()
        .includes("group");

    let inviteEmails = [];

    if (isGroup) {
      const parsed =
        parseScreenplayInviteEmails(
          novelStoryInviteEmailsText
        );

      if (parsed.invalid.length > 0) {
        setNovelStoryCreateError(
          isFR
            ? `Adresse e-mail invalide : ${parsed.invalid[0]}`
            : `Invalid invitee email: ${parsed.invalid[0]}`
        );
        return;
      }

      if (parsed.emails.length === 0) {
        setNovelStoryCreateError(
          isFR
            ? "Ajoutez au moins une adresse e-mail pour l’écriture en groupe."
            : "Add at least one invitee email for Group Writing."
        );
        return;
      }

      inviteEmails = parsed.emails;
    }

    try {
      setNovelStoryCreating(true);
      setNovelStoryCreateError("");

      let authorPhotoUrl =
        clean(novelStoryAuthorPhotoUrl);

      let coverImageUrl =
        clean(novelStoryCoverImageUrl);

      if (novelStoryAuthorPhotoFile) {
        const uploaded =
          await api.uploadAuthorPhoto(
            novelStoryAuthorPhotoFile
          );

        authorPhotoUrl =
          clean(
            uploaded?.url ||
            uploaded?.publicUrl
          );
      }

      if (novelStoryCoverFile) {
        const uploaded =
          await api.uploadStoryCover(
            novelStoryCoverFile
          );

        coverImageUrl =
          clean(
            uploaded?.url ||
            uploaded?.publicUrl
          );
      }

      const created =
        await api.post(
          "/api/stories",
          {
            title,
            forum,
            description,
            synopsis:
              clean(novelStorySynopsis),
            aboutAuthor:
              clean(novelStoryAboutAuthor),
            authorPhotoUrl,
            coverImageUrl,
            storyType:
              novelStoryType,
            mode:
              novelStoryWritingMode,
            writingMode:
              novelStoryWritingMode,
            visibilityStatus:
              novelStoryVisibility,
            language:
              novelStoryLanguage,
            inviteEmails,
          }
        );

      const id =
        projectId(created);

      if (!created || !id) {
        throw new Error(
          isFR
            ? "Le serveur n’a pas retourné le projet créé."
            : "The server did not return the created Novel / Story project."
        );
      }

      setNovelStoryTitle("");
      setNovelStoryForum("");
      setNovelStoryWritingMode("Solo");
      setNovelStoryType("longform");
      setNovelStoryVisibility(
        "public_preview"
      );
      setNovelStoryLanguage(
        isFR ? "fr" : "en"
      );
      setNovelStoryDescription("");
      setNovelStorySynopsis("");
      setNovelStoryAboutAuthor("");
      setNovelStoryAuthorPhotoUrl("");
      setNovelStoryCoverImageUrl("");
      setNovelStoryAuthorPhotoFile(null);
      setNovelStoryCoverFile(null);
      setNovelStoryInviteEmailsText("");
      setShowNovelStoryCreate(false);

      let createdWithChapter =
        created;

      try {
        const firstChapter = {
          id: "chapter-1",
          title:
            isFR
              ? "Chapitre 1"
              : "Chapter 1",
          sceneIds: [],
        };

        const chapterResponse =
          await api.patch(
            `/api/stories/${encodeURIComponent(
              id
            )}/chapters`,
            {
              chapters: [
                firstChapter,
              ],
            }
          );

        createdWithChapter =
          chapterResponse?.story ||
          created;
      } catch (startErr) {
        console.warn(
          "ProjectHub: Novel created but Chapter 1 could not be seeded immediately:",
          startErr?.message ||
            startErr
        );
      }

      setProjects((current) => [
        createdWithChapter,
        ...current.filter(
          (item) =>
            projectId(item) !== id
        ),
      ]);

      // A newly created book remains in its Book Workspace so the
      // author can work across Front Matter, Manuscript and Back Matter.
      // Continue Writing is the returning-writer shortcut, not the
      // creation landing route.
      openNovelStoryBookDraft(
        createdWithChapter
      );
    } catch (err) {
      setNovelStoryCreateError(
        clean(err?.message) ||
          (
            isFR
              ? "Impossible de créer ce projet."
              : "Could not create this Novel / Story project."
          )
      );
    } finally {
      setNovelStoryCreating(false);
    }
  }


  function renderStoryProjects() {
    if (activeNovelStoryDraftProject) {
      return (
        <NovelStoryBookViewDraft
          project={activeNovelStoryDraftProject}
          lang={lang}
          onBack={closeNovelStoryBookDraft}
          onOpenWriterStudio={(options = {}) =>
            navigate(
              "writerStudio",
              activeNovelStoryDraftProject,
              {
                ...(options || {}),
                demoPreview:
                  activeNovelStoryDraftProject
                    ?.isStructurePreview === true,
              }
            )
          }
        />
      );
    }

    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 18,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              selectProjectCategory("")
            }
          >
            ← {ui.backToTypes}
          </button>

          <div className="ns-card-meta">
            {ui.title} / {ui.novelStory}
          </div>
        </div>

        <section
          className="ns-card ns-card-soft"
          style={{
            padding: 18,
            borderRadius: 18,
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0, flex: "1 1 420px" }}>
            <h2
              className="ns-section-title"
              style={{ margin: 0 }}
            >
              {ui.projectsHeading}
            </h2>

            <p
              className="ns-card-meta"
              style={{
                margin: "7px 0 0",
              }}
            >
              {ui.projectsIntro}
            </p>
          </div>

          {!isReaderDiscovery && canCreateProjects ? (
            <button
              className={
                showNovelStoryCreate
                  ? "ns-button ns-button-outline"
                  : "ns-button ns-button-primary"
              }
              type="button"
              onClick={() => {
                setNovelStoryCreateError("");
                setShowNovelStoryCreate(
                  (current) => !current
                );
              }}
              style={{
                flex: "0 0 auto",
                minWidth: 230,
                padding: "11px 18px",
                fontWeight: 800,
              }}
            >
              {showNovelStoryCreate
                ? (
                    isFR
                      ? "Fermer"
                      : "Close"
                  )
                : ui.createStory}
            </button>
          ) : null}

          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              onNavigatePage?.(
                "storylines",
                {
                  resetStorylines: true,
                  source:
                    "project_hub_novel_story",
                }
              )
            }
            style={{
              flex: "0 0 auto",
              minWidth: 190,
              padding: "11px 18px",
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Parcourir Storylines"
              : "Browse Storylines"}
          </button>

          {showNovelStoryCreate &&
          !isReaderDiscovery &&
          canCreateProjects ? (
            <form
              onSubmit={createNovelStoryProject}
              data-novel-story-create-project="true"
              style={{
                flex: "1 0 100%",
                marginTop: 4,
                paddingTop: 18,
                borderTop:
                  "1px solid rgba(148,163,184,0.28)",
                display: "grid",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 14,
                }}
              >
                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR ? "Titre" : "Title"}
                  </span>
                  <input
                    className="ns-input"
                    value={novelStoryTitle}
                    onChange={(event) =>
                      setNovelStoryTitle(
                        event.target.value
                      )
                    }
                    placeholder={
                      isFR
                        ? "Titre du livre"
                        : "Book title"
                    }
                  />
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Type d’histoire"
                      : "Story Type"}
                  </span>
                  <select
                    className="ns-input"
                    value={novelStoryType}
                    onChange={(event) =>
                      setNovelStoryType(
                        event.target.value
                      )
                    }
                  >
                    <option value="longform">
                      {isFR
                        ? "Roman / récit long"
                        : "Long-form Story / Novel"}
                    </option>
                    <option value="short">
                      {isFR
                        ? "Nouvelle"
                        : "Short Story"}
                    </option>
                  </select>
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Genre"
                      : "Genre"}
                  </span>
                  <select
                    className="ns-input"
                    value={novelStoryForum}
                    onChange={(event) =>
                      setNovelStoryForum(
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      {isFR
                        ? "Choisir un genre…"
                        : "Choose a Genre…"}
                    </option>
                    {[
                      "Action Forum",
                      "Adventure Forum",
                      "Children Forum",
                      "Comedy Forum",
                      "Crime Forum",
                      "Fantasy Forum",
                      "Historical Forum",
                      "Inspirational Stories Forum",
                      "Mystery Forum",
                      "Poetry Forum",
                      "Romance Forum",
                      "Suspense / Thriller Forum",
                      "Science Fiction Forum",
                      "Faith & Inspiration Forum",
                      "Humour Forum",
                      "Non-Fiction Stories Forum",
                      "Young Adult Forum",
                      "Young Illustrators Gallery",
                      "Comics & Illustrated Stories Forum",
                      "NextScenes Originals",
                      "Unclassified Forum",
                    ].map((forum) => (
                      <option
                        key={forum}
                        value={forum}
                      >
                        {forum.replace(
                          / Forum$/,
                          ""
                        )}
                      </option>
                    ))}
                  </select>
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Mode d’écriture"
                      : "Writing Mode"}
                  </span>
                  <select
                    className="ns-input"
                    value={
                      novelStoryWritingMode
                    }
                    onChange={(event) =>
                      setNovelStoryWritingMode(
                        event.target.value
                      )
                    }
                  >
                    <option value="Solo">
                      Solo
                    </option>
                    <option value="Collaborative">
                      {isFR
                        ? "Collaboratif"
                        : "Collaborative"}
                    </option>
                    <option value="Group (Open)">
                      {isFR
                        ? "Groupe (ouvert)"
                        : "Group (Open)"}
                    </option>
                    <option value="Group (Closed)">
                      {isFR
                        ? "Groupe (fermé)"
                        : "Group (Closed)"}
                    </option>
                  </select>
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Visibilité"
                      : "Visibility"}
                  </span>
                  <select
                    className="ns-input"
                    value={
                      novelStoryVisibility
                    }
                    onChange={(event) =>
                      setNovelStoryVisibility(
                        event.target.value
                      )
                    }
                  >
                    <option value="private_draft">
                      {isFR
                        ? "Brouillon privé"
                        : "Private Draft"}
                    </option>
                    <option value="nextscenes_readers">
                      {isFR
                        ? "Lecteurs NextScenes"
                        : "NextScenes Readers Only"}
                    </option>
                    <option value="public_preview">
                      {isFR
                        ? "Aperçu public"
                        : "Public Preview"}
                    </option>
                    <option value="published">
                      {isFR
                        ? "Publié / terminé"
                        : "Published / Completed"}
                    </option>
                  </select>
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Langue principale"
                      : "Primary Language"}
                  </span>
                  <select
                    className="ns-input"
                    value={
                      novelStoryLanguage
                    }
                    onChange={(event) =>
                      setNovelStoryLanguage(
                        event.target.value
                      )
                    }
                  >
                    <option value="en">
                      English
                    </option>
                    <option value="fr">
                      Français
                    </option>
                  </select>
                </label>
              </div>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 800,
                }}
              >
                <span>
                  {isFR
                    ? "Description"
                    : "Description"}
                </span>
                <textarea
                  className="ns-input"
                  rows={3}
                  value={
                    novelStoryDescription
                  }
                  onChange={(event) =>
                    setNovelStoryDescription(
                      event.target.value
                    )
                  }
                  placeholder={
                    isFR
                      ? "Brève description du projet…"
                      : "Brief description of the project…"
                  }
                  style={{
                    resize: "vertical",
                  }}
                />
              </label>

              {clean(
                novelStoryWritingMode
              )
                .toLowerCase()
                .includes("group") ? (
                <label
                  style={{
                    display: "grid",
                    gap: 6,
                    fontWeight: 800,
                  }}
                >
                  <span>
                    {isFR
                      ? "Invités du groupe"
                      : "Group Invitees"}
                  </span>
                  <textarea
                    className="ns-input"
                    rows={2}
                    value={
                      novelStoryInviteEmailsText
                    }
                    onChange={(event) =>
                      setNovelStoryInviteEmailsText(
                        event.target.value
                      )
                    }
                    placeholder={
                      isFR
                        ? "adresses e-mail séparées par des virgules"
                        : "email addresses separated by commas"
                    }
                    style={{
                      resize: "vertical",
                    }}
                  />
                </label>
              ) : null}

              <details
                style={{
                  border:
                    "1px solid rgba(148,163,184,0.28)",
                  borderRadius: 14,
                  padding: "12px 14px",
                  background:
                    "rgba(255,255,255,0.76)",
                }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 850,
                  }}
                >
                  {isFR
                    ? "Détails du livre (facultatif)"
                    : "Book Details (optional)"}
                </summary>

                <div
                  style={{
                    marginTop: 14,
                    display: "grid",
                    gap: 14,
                  }}
                >
                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      fontWeight: 800,
                    }}
                  >
                    <span>
                      {isFR
                        ? "Synopsis"
                        : "Synopsis"}
                    </span>
                    <textarea
                      className="ns-input"
                      rows={4}
                      value={
                        novelStorySynopsis
                      }
                      onChange={(event) =>
                        setNovelStorySynopsis(
                          event.target.value
                        )
                      }
                      style={{
                        resize: "vertical",
                      }}
                    />
                  </label>

                  <label
                    style={{
                      display: "grid",
                      gap: 6,
                      fontWeight: 800,
                    }}
                  >
                    <span>
                      {isFR
                        ? "À propos de l’auteur"
                        : "About the Author"}
                    </span>
                    <textarea
                      className="ns-input"
                      rows={4}
                      value={
                        novelStoryAboutAuthor
                      }
                      onChange={(event) =>
                        setNovelStoryAboutAuthor(
                          event.target.value
                        )
                      }
                      style={{
                        resize: "vertical",
                      }}
                    />
                  </label>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 14,
                    }}
                  >
                    <label
                      style={{
                        display: "grid",
                        gap: 6,
                        fontWeight: 800,
                      }}
                    >
                      <span>
                        {isFR
                          ? "Photo de l’auteur"
                          : "Author Photo"}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) =>
                          setNovelStoryAuthorPhotoFile(
                            event.target.files?.[0] ||
                            null
                          )
                        }
                      />
                      <input
                        className="ns-input"
                        value={
                          novelStoryAuthorPhotoUrl
                        }
                        onChange={(event) =>
                          setNovelStoryAuthorPhotoUrl(
                            event.target.value
                          )
                        }
                        placeholder={
                          isFR
                            ? "ou URL de la photo"
                            : "or photo URL"
                        }
                      />
                    </label>

                    <label
                      style={{
                        display: "grid",
                        gap: 6,
                        fontWeight: 800,
                      }}
                    >
                      <span>
                        {isFR
                          ? "Couverture"
                          : "Book Cover"}
                      </span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) =>
                          setNovelStoryCoverFile(
                            event.target.files?.[0] ||
                            null
                          )
                        }
                      />
                      <input
                        className="ns-input"
                        value={
                          novelStoryCoverImageUrl
                        }
                        onChange={(event) =>
                          setNovelStoryCoverImageUrl(
                            event.target.value
                          )
                        }
                        placeholder={
                          isFR
                            ? "ou URL de la couverture"
                            : "or cover image URL"
                        }
                      />
                    </label>
                  </div>
                </div>
              </details>

              {novelStoryCreateError ? (
                <div
                  role="alert"
                  style={{
                    color: "#b91c1c",
                    fontWeight: 800,
                  }}
                >
                  {novelStoryCreateError}
                </div>
              ) : null}

              <div>
                <button
                  className="ns-button ns-button-primary"
                  type="submit"
                  disabled={
                    novelStoryCreating
                  }
                >
                  {novelStoryCreating
                    ? (
                        isFR
                          ? "Création…"
                          : "Creating…"
                      )
                    : (
                        isFR
                          ? "Créer Roman / Histoire"
                          : "Create Novel / Story"
                      )}
                </button>
              </div>
            </form>
          ) : null}
        </section>

        {canCreateProjects &&
        (
          novelCollaborationLoading ||
          novelCollaborationError ||
          novelCollaborationStories.length >
            0
        ) ? (
          <section
            data-novel-collaboration-discovery="true"
            className="ns-card"
            style={{
              padding: 20,
              borderRadius: 20,
              marginBottom: 20,
              border:
                "2px solid rgba(5,150,105,0.28)",
              background:
                "linear-gradient(145deg, rgba(236,253,245,0.96), rgba(248,255,252,0.98))",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent:
                  "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing:
                      "0.1em",
                    textTransform:
                      "uppercase",
                    color: "#047857",
                  }}
                >
                  {isFR
                    ? "PARTICIPATION COLLABORATIVE"
                    : "COLLABORATIVE PARTICIPATION"}
                </div>

                <h3
                  className="ns-section-title"
                  style={{
                    margin:
                      "5px 0 5px",
                  }}
                >
                  {isFR
                    ? "Storylines collaboratives"
                    : "Collaborative Storylines"}
                </h3>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: 0,
                    maxWidth: 760,
                    lineHeight: 1.5,
                  }}
                >
                  {isFR
                    ? "Ces projets proviennent du même ensemble visible dans Storylines. Writer Studio applique ensuite les règles du Mode Canon, du Mode Proposition et du rôle de l’utilisateur."
                    : "These projects come from the same set visible in Storylines. Writer Studio then applies Canon Mode, Proposal Mode, and user-role permissions."}
                </p>
              </div>

              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={
                  novelCollaborationLoading
                }
                onClick={
                  loadNovelStoryVisibility
                }
              >
                {isFR
                  ? "Actualiser"
                  : "Refresh"}
              </button>
            </div>

            {novelCollaborationError ? (
              <div
                role="alert"
                style={{
                  color: "#b91c1c",
                  fontWeight: 700,
                  marginBottom: 10,
                }}
              >
                {
                  novelCollaborationError
                }
              </div>
            ) : null}

            {novelCollaborationLoading ? (
              <div
                className="ns-card"
                style={{ padding: 16 }}
              >
                {isFR
                  ? "Chargement des projets collaboratifs…"
                  : "Loading Collaborative projects…"}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(250px, 305px))",
                  gap: 14,
                }}
              >
                {novelCollaborationStories.map(
                  (story) => {
                    const id =
                      projectId(story);

                    const owner =
                      clean(
                        story?.authorName
                      ) ||
                      clean(
                        story
                          ?.authorProfile
                          ?.name
                      ) ||
                      clean(
                        story
                          ?.authorUsername
                      ) ||
                      clean(
                        story?.author
                      ) ||
                      (
                        isFR
                          ? "Auteur"
                          : "Author"
                      );

                    return (
                      <article
                        key={id}
                        className="ns-card"
                        data-novel-collaboration-card="true"
                        style={{
                          padding: 16,
                          borderRadius: 16,
                          border:
                            "1px solid rgba(5,150,105,0.20)",
                          borderLeft:
                            "4px solid rgba(5,150,105,0.55)",
                          display: "flex",
                          flexDirection:
                            "column",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 900,
                            letterSpacing:
                              "0.1em",
                            textTransform:
                              "uppercase",
                            color:
                              "#047857",
                          }}
                        >
                          {story
                            ?.proposalsOpen ===
                          true
                            ? (
                                isFR
                                  ? "TOUR DE PROPOSITIONS OUVERT"
                                  : "PROPOSAL ROUND OPEN"
                              )
                            : (
                                isFR
                                  ? "MODE CANON · PROPOSITIONS VERROUILLÉES"
                                  : "CANON MODE · PROPOSALS LOCKED"
                              )}
                        </div>

                        <h4
                          className="ns-card-title"
                          style={{
                            margin: 0,
                            fontSize: 17,
                          }}
                        >
                          {clean(
                            story?.title
                          ) ||
                            (
                              isFR
                                ? "Histoire sans titre"
                                : "Untitled Story"
                            )}
                        </h4>

                        <div className="ns-card-meta">
                          {isFR
                            ? "Par"
                            : "By"}{" "}
                          {owner}
                        </div>

                        <p
                          className="ns-card-meta"
                          style={{
                            margin: 0,
                            lineHeight: 1.5,
                          }}
                        >
                          {story
                            ?.proposalsOpen ===
                          true
                            ? (
                                isFR
                                  ? "Ce projet accepte actuellement des propositions. Writer Studio appliquera les règles de participation."
                                  : "This project is currently accepting proposals. Writer Studio will apply the participation rules."
                              )
                            : (
                                isFR
                                  ? "Ce projet est visible pour ce compte dans Storylines. Le Mode Canon est actif et les actions de proposition restent verrouillées."
                                  : "This project is visible to this account in Storylines. Canon Mode is active and proposal actions remain locked."
                              )}
                        </p>

                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          onClick={() =>
                            navigate(
                              "writerStudio",
                              story
                            )
                          }
                          style={{
                            alignSelf:
                              "flex-start",
                            marginTop: 4,
                          }}
                        >
                          {isFR
                            ? "Ouvrir Writer Studio"
                            : "Open Writer Studio"}
                        </button>
                      </article>
                    );
                  }
                )}
              </div>
            )}
          </section>
        ) : null}

        {isReaderDiscovery ? (
          <div
            className="ns-card"
            style={{
              padding: 24,
              borderRadius: 20,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(37,99,235,0.10)",
                color: "#1d4ed8",
                fontSize: 12,
                fontWeight: 900,
              }}
            >
              {ui.readerMode}
            </div>

            <h3
              className="ns-card-title"
              style={{
                marginTop: 14,
                marginBottom: 8,
              }}
            >
              {ui.readerDiscoveryTitle}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                maxWidth: 760,
                lineHeight: 1.6,
              }}
            >
              {ui.readerDiscoveryBody}
            </p>

            <div
              style={{
                marginTop: 18,
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() =>
                  onNavigatePage?.("readerView")
                }
              >
                {ui.browseReaderView}
              </button>

              <button
                className="ns-button ns-button-primary"
                type="button"
                onClick={() =>
                  onNavigatePage?.("profile")
                }
              >
                {ui.becomeWriter}
              </button>
            </div>
          </div>
        ) : loading ? (
          <div className="ns-card" style={{ padding: 22 }}>
            {ui.loading}
          </div>
        ) : error ? (
          <div className="ns-card" style={{ padding: 22 }}>
            <p
              style={{
                marginTop: 0,
                color: "#b91c1c",
                fontWeight: 800,
              }}
            >
              {error}
            </p>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={loadProjects}
            >
              {ui.retry}
            </button>
          </div>
        ) : (nativeChildrensNovelPilotV1F1
          ? projects.filter((project) => project?.bookCategory !== "children_book")
          : projects).length === 0 ? (
          <div className="ns-card" style={{ padding: 24 }}>
            <h3
              className="ns-card-title"
              style={{ marginTop: 0 }}
            >
              {ui.emptyTitle}
            </h3>

            <p className="ns-card-meta">
              {ui.emptyBody}
            </p>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
            </div>
          </div>
        ) : (
          <div
            className="ns-project-hub-story-grid"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(3, minmax(0, 1fr))",
              justifyContent: "stretch",
              alignItems: "stretch",
              gap: 24,
            }}
          >
            {(nativeChildrensNovelPilotV1F1
              ? projects.filter((project) => project?.bookCategory !== "children_book")
              : projects).map((project) => {
              const id = projectId(project);
              const classification =
                project?.classification || {};
              const lifecycle =
                project?.lifecycle || {};
              const publication =
                project?.publication || {};
              const summary =
                project?.summary || {};

              const publicationStatus =
                clean(
                  lifecycle.publicationStatus
                ).toLowerCase();

              const isPublished =
                [
                  "published",
                  "publishedamazon",
                  "published_amazon",
                  "publishedexternal",
                  "published_external",
                  "externallypublished",
                ].includes(
                  publicationStatus
                );

              const authorName =
                isPublished
                  ? clean(
                      publication?.authorName
                    )
                  : (
                      clean(project?.owner?.name) ||
                      clean(project?.owner?.username)
                    );

              const storylineTitle =
                clean(project?.title);

              const publicationTitle =
                clean(publication?.title);

              const displayTitle =
                (
                  isPublished
                    ? publicationTitle
                    : storylineTitle
                ) ||
                storylineTitle ||
                (
                  isFR
                    ? "Storyline sans titre"
                    : "Untitled Storyline"
                );

              return (
                <article
                  key={id}
                  className={
                    "ns-card ns-project-card-clickable ns-project-hub-story-card" +
                    (
                      isPublished
                        ? " ns-project-card-published"
                        : ""
                    )
                  }
                  role="button"
                  tabIndex={id ? 0 : -1}
                  aria-label={
                    displayTitle
                      ? (
                          isPublished
                            ? `Open ${displayTitle} in Reader View`
                            : `Continue writing ${displayTitle}`
                        )
                      : (
                          isPublished
                            ? "Open published project in Reader View"
                            : "Continue writing project"
                        )
                  }
                  onClick={(event) => {
                    if (!id) return;

                    const interactive =
                      event?.target?.closest?.(
                        "button, a, input, select, textarea, summary, details"
                      );

                    if (interactive) return;

                    if (isPublished) {
                      navigate(
                        "readerView",
                        project
                      );
                    } else {
                      continueNovelStory(
                        project
                      );
                    }
                  }}
                  onKeyDown={(event) => {
                    if (!id) return;
                    // Nested controls, including the More options summary,
                    // own their keyboard actions; only the card itself navigates.
                    if (event.target !== event.currentTarget) return;

                    if (
                      event.key === "Enter" ||
                      event.key === " "
                    ) {
                      event.preventDefault();

                      if (isPublished) {
                        navigate(
                          "readerView",
                          project
                        );
                      } else {
                        continueNovelStory(
                          project
                        );
                      }
                    }
                  }}
                  style={{
                    padding: 20,
                    paddingTop: 24,
                    borderRadius: 22,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: "none",
                    minHeight: 410,
                    height: "100%",
                    cursor: id
                      ? "pointer"
                      : "default",
                    position: "relative",
                    overflow: "hidden",
                    background:
                      "linear-gradient(180deg, rgba(239,246,255,0.96) 0%, #ffffff 42%)",
                    border:
                      "2px solid rgba(37,99,235,0.18)",
                    boxShadow:
                      "0 14px 34px rgba(15,23,42,0.08)",
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: "#2563eb",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                    }}
                  >
                    {project?.coverImageUrl ? (
                      <img
                        src={project.coverImageUrl}
                        alt=""
                        style={{
                          width: 72,
                          height: 96,
                          objectFit: "cover",
                          borderRadius: 10,
                          flex: "0 0 auto",
                        }}
                      />
                    ) : (
                      <div
                        aria-hidden="true"
                        style={{
                          width: 72,
                          height: 96,
                          borderRadius: 10,
                          background:
                            "linear-gradient(145deg, #dbeafe, #e2e8f0)",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 30,
                          flex: "0 0 auto",
                        }}
                      >
                        ✦
                      </div>
                    )}

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 900,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "#2563eb",
                          }}
                        >
                          {ui.novelStory}
                        </span>

                        {isPublished ? (
                          <span
                            className="ns-project-published-badge"
                          >
                            {isFR
                              ? "Publié"
                              : "Published"}
                          </span>
                        ) : null}
                      </div>

                      <h3
                        className="ns-card-title"
                        style={{
                          margin: "5px 0 6px",
                          fontSize: 19,
                          lineHeight: 1.2,
                        }}
                      >
                        {displayTitle}
                      </h3>

                      {authorName ? (
                        <div
                          className="ns-project-card-author"
                          style={{
                            marginTop: 3,
                            marginBottom: 4,
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#475569",
                          }}
                        >
                          {isFR ? "Par" : "By"}{" "}
                          {authorName}
                        </div>
                      ) : null}

                      {!isPublished ? (
                        <div className="ns-card-meta">
                          {ui.studio}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {isPublished ? (
                    <>
                      {clean(publication?.subtitle) ? (
                        <p
                          className="ns-card-meta"
                          style={{
                            marginTop: 12,
                            marginBottom: 0,
                            fontStyle: "italic",
                            lineHeight: 1.45,
                          }}
                        >
                          {clean(
                            publication.subtitle
                          )}
                        </p>
                      ) : null}

                      <dl
                        className="ns-project-published-details"
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(118px, auto) 1fr",
                          gap: "9px 12px",
                          margin: "18px 0 0",
                          fontSize: 13,
                        }}
                      >
                        <dt
                          style={{
                            fontWeight: 800,
                          }}
                        >
                          {isFR
                            ? "Statut"
                            : "Status"}
                        </dt>
                        <dd style={{ margin: 0 }}>
                          {publicationLabel(
                            lifecycle.publicationStatus,
                            lang
                          )}
                        </dd>

                        {clean(
                          publication?.publishedAt
                        ) ? (
                          <>
                            <dt
                              style={{
                                fontWeight: 800,
                              }}
                            >
                              {isFR
                                ? "Publié le"
                                : "Published"}
                            </dt>
                            <dd style={{ margin: 0 }}>
                              {formatDate(
                                publication.publishedAt,
                                lang
                              )}
                            </dd>
                          </>
                        ) : null}
                      </dl>
                    </>
                  ) : null}

                  {!isPublished ? (
                  <dl
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(108px, auto) 1fr",
                      gap: "8px 12px",
                      margin: "10px 0 0",
                      fontSize: 13,
                    }}
                  >
                    <dt style={{ fontWeight: 800 }}>
                      {ui.forum}
                    </dt>
                    <dd style={{ margin: 0 }}>
                      {clean(classification.forum) ||
                        (isFR
                          ? "Non classé"
                          : "Unclassified")}
                    </dd>

                    <dt style={{ fontWeight: 800 }}>
                      {ui.structure}
                    </dt>
                    <dd style={{ margin: 0 }}>
                      {storyTypeLabel(
                        classification.storyType,
                        lang
                      )}
                    </dd>

                    <dt style={{ fontWeight: 800 }}>
                      {ui.mode}
                    </dt>
                    <dd style={{ margin: 0 }}>
                      {writingModeLabel(
                        classification.writingMode,
                        lang
                      )}
                    </dd>

                    <dt style={{ fontWeight: 800 }}>
                      {ui.status}
                    </dt>
                    <dd style={{ margin: 0 }}>
                      {publicationLabel(
                        lifecycle.publicationStatus,
                        lang
                      )}
                    </dd>

                    <dt style={{ fontWeight: 800 }}>
                      {ui.updated}
                    </dt>
                    <dd style={{ margin: 0 }}>
                      {formatDate(
                        project?.updatedAt,
                        lang
                      )}
                    </dd>
                  </dl>
                  ) : null}

                  {!isPublished ? (
                    <div
                      className="ns-card-meta"
                      style={{
                        display: "flex",
                        gap: 12,
                        flexWrap: "wrap",
                        marginTop: 14,
                      }}
                    >
                      <span>
                        {ui.canon}:{" "}
                        <strong>
                          {Number(
                            summary.canonScenes || 0
                          )}
                        </strong>
                      </span>

                      <span>
                        {ui.proposals}:{" "}
                        <strong>
                          {Number(
                            summary.pendingProposals || 0
                          )}
                        </strong>
                      </span>
                    </div>
                  ) : null}

                  <div
                    style={{
                      marginTop: "auto",
                      paddingTop: 18,
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    {!isPublished ? (
                      <>
                        <button
                          className="ns-button ns-button-primary"
                          type="button"
                          disabled={!id}
                          onClick={() =>
                            continueNovelStory(
                              project
                            )
                          }
                          style={{
                            width: "100%",
                          }}
                        >
                          {Number(
                            summary.canonScenes ||
                              0
                          ) > 0
                            ? (
                                isFR
                                  ? "Continuer à écrire"
                                  : "Continue Writing"
                              )
                            : (
                                isFR
                                  ? "Commencer à écrire"
                                  : "Start Writing"
                              )}
                        </button>

                        <button
                          className="ns-button ns-button-outline"
                          type="button"
                          data-novel-story-book-view-draft-button="true"
                          disabled={!id}
                          onClick={() =>
                            openNovelStoryBookDraft(
                              project
                            )
                          }
                          style={{
                            width: "100%",
                          }}
                        >
                          {isFR
                            ? "Espace Livre"
                            : "Book Workspace"}
                        </button>

                        <details
                          style={{
                            marginTop: 2,
                          }}
                        >
                          <summary
                            style={{
                              cursor: "pointer",
                              textAlign: "center",
                              fontSize: 12,
                              fontWeight: 800,
                              color: "#475569",
                            }}
                          >
                            {isFR
                              ? "Plus d’options"
                              : "More options"}
                          </summary>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(3, minmax(0, 1fr))",
                              gap: 7,
                              marginTop: 8,
                            }}
                          >
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={!id}
                              onClick={() =>
                                navigate(
                                  "storyDashboard",
                                  project
                                )
                              }
                              style={{
                                padding: "7px 6px",
                                fontSize: 12,
                              }}
                            >
                              {ui.dashboard}
                            </button>

                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={!id}
                              onClick={() =>
                                navigate(
                                  "readerView",
                                  project
                                )
                              }
                              style={{
                                padding: "7px 6px",
                                fontSize: 12,
                              }}
                            >
                              {ui.reader}
                            </button>

                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              disabled={!id}
                              onClick={() =>
                                navigate(
                                  "editorialBoard",
                                  project
                                )
                              }
                              style={{
                                padding: "7px 6px",
                                fontSize: 12,
                              }}
                            >
                              {ui.editorial}
                            </button>
                          </div>
                        </details>
                      </>
                    ) : (
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        disabled={!id}
                        onClick={() =>
                          navigate(
                            "readerView",
                            project
                          )
                        }
                        style={{
                          padding: "8px 10px",
                          fontSize: 12,
                        }}
                      >
                        {ui.reader}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </>
    );
  }

  return (
    <main
      className="ns-page"
      style={{
        maxWidth: 1180,
        margin: "0 auto",
        padding: "22px 16px 48px",
      }}
    >
      <ProjectBreadcrumbTrail
        onNavigatePage={
          navigateProjectBreadcrumb
        }
        lang={lang}
        projectCategory={
          selectedCategory || ""
        }
        projectTitle={
          activeNovelStoryDraftProject
            ? clean(
                activeNovelStoryDraftProject
                  ?.title
              )
            : showChildrensBookCreationFlow
            ? clean(
                activeChildrensBookProject
                  ?.project
                  ?.title
              )
            : selectedCategory ===
                "textbook" &&
              activeTextbookProject
            ? clean(
                activeTextbookProject
                  ?.title
              )
            : selectedCategory ===
                "screenplay" &&
              activeScreenplayProject
            ? clean(
                activeScreenplayProject
                  ?.title
              )
            : ""
        }
        pageKey={
          showChildrensBookCreationFlow
            ? "bookBlueprint"
            : selectedCategory === "children"
            ? "childrensBook"
            : selectedCategory ===
                "textbook"
            ? (
                activeTextbookProject
                  ? (
                      textbookWorkspace === "chapter-workspace"
                        ? "textbookChapterWorkspace"
                        : textbookWorkspace === "write-chapters"
                        ? "textbookWriteChapters"
                        : textbookWorkspace === "book-map"
                        ? "textbookBookMap"
                        : textbookWorkspace === "overview"
                        ? "textbookBlueprintOverview"
                        : "textbookProjectOverview"
                    )
                  : "textbook"
              )
            : selectedCategory ===
                "screenplay"
            ? (
                activeScreenplayProject
                  ? (
                      screenplayWorkspace ===
                        "story-map"
                        ? "screenplayStoryMap"
                        : screenplayWorkspace ===
                            "characters-world"
                        ? "screenplayCharactersWorld"
                        : screenplayWorkspace ===
                            "script-workspace"
                        ? "screenplayScriptWorkspace"
                        : screenplayWorkspace ===
                            "proposal-round"
                        ? "screenplayProposalRound"
                        : screenplayWorkspace ===
                            "review-and-decide"
                        ? "screenplayReviewAndDecide"
                        : "screenplayProjectOverview"
                    )
                  : "screenplay"
              )
            : selectedCategory
            ? "myProjects"
            : "projectHub"
        }
      />

      <header
        className="ns-card ns-project-hub-hero"
        data-textbook-compact-hero={
          selectedCategory === "textbook"
            ? "true"
            : undefined
        }
        style={{
          display:
            selectedCategory === "textbook" ||
            activeNovelStoryDraftProject
              ? "none"
              : undefined,
          padding:
            selectedCategory === "textbook"
              ? "14px 24px"
              : "clamp(22px, 3vw, 32px) clamp(22px, 4vw, 40px)",
          minHeight:
            selectedCategory === "textbook"
              ? 150
              : undefined,
          borderRadius:
            selectedCategory === "textbook"
              ? 18
              : 22,
          color: "#ffffff",
          overflow: "hidden",
        }}
      >
        <div
          className="ns-project-hub-hero-pattern"
          aria-hidden="true"
        />

        <div className="ns-project-hub-hero-copy">
            <div
              style={{
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.16em",
                opacity: 0.88,
              }}
            >
              {ui.eyebrow}
            </div>

        <h1
          style={{
            margin: "10px 0 8px",
            fontFamily:
              'Georgia, "Times New Roman", serif',
            fontSize:
              selectedCategory === "textbook"
                ? "clamp(1.8rem, 3vw, 2.35rem)"
                : "clamp(2rem, 4.2vw, 3.15rem)",
            lineHeight: 1.02,
            color: "#ffffff",
          }}
        >
          {selectedCategory
            ? ui.projectsTitle
            : ui.title}
        </h1>

        <p
          style={{
            maxWidth: 760,
            margin: 0,
            fontSize:
              selectedCategory === "textbook"
                ? "0.98rem"
                : "clamp(1rem, 1.5vw, 1.16rem)",
            lineHeight: 1.55,
            color: "rgba(255,255,255,0.94)",
          }}
        >
          {selectedCategory
            ? ui.projectsSubtitle
            : ui.subtitle}
        </p>

      </div>
    </header>

      <section style={{ marginTop: 24 }}>
        {/* Keep the Writing Companion's space stable while project lists load. */}
        {!selectedCategory && isAuthenticated && !error ? (
          loading || !companionOtherLanguageLoaded ? (
            <div className="ns-project-hub-companion-placeholder" aria-hidden="true" />
          ) : (
            <WritingCompanionPilotV1
              lang={lang}
              canWrite={canCreateProjects}
              projects={[...projects, ...companionOtherLanguageProjects]}
              onContinue={continueNovelStory}
              onStart={() => selectProjectCategory("story")}
              onBrowse={() => onNavigatePage?.("readerView")}
            />
          )
        ) : null}
        {!selectedCategory ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "#64748b",
                }}
              >
                {ui.chooseType}
              </div>

              <h2
                className="ns-section-title"
                style={{
                  margin: "5px 0 6px",
                }}
              >
                {ui.chooseType}
              </h2>

              <p
                className="ns-card-meta"
                style={{ margin: 0 }}
              >
                {ui.chooseTypeHelp}
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(245px, 1fr))",
                gridAutoRows: "1fr",
                alignItems: "stretch",
                gap: 16,
              }}
            >
              {categories.map(renderCategoryCard)}

              {isAdmin ? (
                <article
                  className="ns-card ns-project-type-card is-available"
                  style={{
                    padding: 22,
                    borderRadius: 22,
                    minHeight: 276,
                    height: "100%",
                    width: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    background:
                      "linear-gradient(145deg, rgba(238,242,255,0.99), rgba(250,250,255,0.99))",
                    border:
                      "2px solid rgba(79,70,229,0.42)",
                    boxShadow:
                      "0 16px 38px rgba(79,70,229,0.13), 0 3px 9px rgba(15,23,42,0.08)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: "#4f46e5",
                    }}
                  />

                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 17,
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(255,255,255,0.94)",
                      color: "#4338ca",
                      border:
                        "1px solid rgba(79,70,229,0.30)",
                      fontSize: 28,
                    }}
                    aria-hidden="true"
                  >
                    ⚙
                  </div>

                  <h3
                    className="ns-card-title"
                    style={{
                      margin: "18px 0 8px",
                      fontSize: 22,
                    }}
                  >
                    {ui.adminConsole}
                  </h3>

                  <p
                    className="ns-card-meta"
                    style={{
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {ui.adminConsoleDescription}
                  </p>

                  <div
                    style={{
                      marginTop: "auto",
                      paddingTop: 18,
                    }}
                  >
                    <button
                      className="ns-button ns-button-primary"
                      type="button"
                      onClick={() =>
                        onNavigatePage?.("admin")
                      }
                      style={{ width: "100%" }}
                    >
                      {ui.openAdminConsole}
                    </button>
                  </div>
                </article>
              ) : null}
            </div>
          </>
        ) : selectedCategory === "children" ? (
          renderChildrensBookFoundation()
        ) : selectedCategory === "textbook" ? (
          renderTextbookFoundation()
        ) : selectedCategory === "screenplay" ? (
          renderScreenplayFoundation()
        ) : (
          renderStoryProjects()
        )}
      </section>

    </main>
  );
}
